"use client";

import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { 
  BookOpen, 
  Shield, 
  AlertTriangle, 
  Users, 
  FileText, 
  XCircle,
  ChevronRight,
  ArrowLeft,
  Search,
  Network,
  Upload,
  Download,
  Filter,
  X,
  Plus,
  AlertCircle,
  Trash2,
  Loader2,
  MessageSquare,
  ArrowRight,
  FileCheck,
  Eye,
  CheckCircle,
  ExternalLink,
  Clock,
  Monitor,
  Workflow,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import * as XLSX from "xlsx";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { getAssertionAbbreviation } from "../../utils/assertion-helpers";
import DocumentsPage from "../documents/DocumentsPage";
import { logger } from "../../utils/logger";
import { fileManager, FileMetadata } from "../../utils/file-manager";
import InternalControlViewerModal from "./InternalControlViewerModal";
import InternalControlForm from "./InternalControlForm";
import { InternalControlView } from "../internal-control";
import { CONTROL_TEMPLATES } from "../../constants/ControlTemplates";
import { CloudStorageTest } from "../cloud/CloudStorageTest";
import { CloudStorageDebug } from "../cloud/CloudStorageDebug";
import { CloudStructureTest } from "../cloud/CloudStructureTest";
import ControlOwnerFormModal from "./ControlOwnerFormModal";
import { DeficiencyFormModal } from "./DeficiencyFormModal";
import { DeficiencyDetailView } from "./DeficiencyDetailView";
import MisstatementDetailView from "../misstatements/MisstatementDetailView";
import { MisstatementFormModal } from "./MisstatementFormModal";
import { InformationLibraryFormModal } from "./InformationLibraryFormModal";
import { InformationDetailView } from "./InformationDetailView";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { useAuth } from "@/contexts/AuthContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { useExecutionEngagementDetails } from "@/contexts/ExecutionEngagementDetailsContext";
import { isLibraryHiddenForLimitedReview } from "../../utils/execution-limited-review-ui";
import { apiService } from "@/services/api-service";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { MultiSelectDropdown } from "../ui/multi-select-dropdown";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Checkbox } from "../ui/checkbox";

// Signoff badge for Control Library (horizontal list, same UX as Document List)
function getInitialsForSignoff(name: string): string {
  if (!name) return "";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

function ControlSignoffBadge({
  label,
  signoffArray,
  onSignoffClick,
  disabled,
}: {
  label: string;
  signoffArray: Array<{ name: string; metadata: string }>;
  onSignoffClick: () => void;
  disabled: boolean;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const validEntries = (signoffArray || []).filter((e) => e?.name && String(e.name).trim() !== "");
  const hasEntries = validEntries.length > 0;
  const formatDate = (s: string) => {
    if (!s) return "";
    try { return new Date(s).toLocaleString(); } catch { return s; }
  };
  return (
    <div className="relative inline-flex items-center gap-1">
      {hasEntries && (
        <div
          className="flex items-center gap-0.5"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {validEntries.slice(0, 3).map((entry, idx) => (
            <div
              key={idx}
              className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium cursor-help border border-blue-500/30"
              title={`${entry.name} - ${formatDate(entry.metadata || "")}`}
            >
              {getInitialsForSignoff(entry.name)}
            </div>
          ))}
          {validEntries.length > 3 && (
            <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
              +{validEntries.length - 3}
            </div>
          )}
        </div>
      )}
      <Button
        size="sm"
        variant="ghost"
        onClick={onSignoffClick}
        disabled={disabled}
        className={`h-6 w-6 p-0 ${disabled ? "text-white/30 cursor-not-allowed" : "text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"}`}
        title={disabled ? `You don't have permission to add ${label} signoff` : `Add ${label} signoff`}
      >
        <FileCheck className="h-3 w-3" />
      </Button>
      {showTooltip && hasEntries && (
        <div className="absolute z-10 bottom-full left-1/2 mb-2 min-w-[160px] max-w-xs -translate-x-1/2 rounded border border-white/10 bg-gray-900 px-2 py-1 text-xs text-white shadow-lg">
          <div className="font-semibold mb-1">{label} ({validEntries.length})</div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {validEntries.map((entry, idx) => (
              <div key={idx} className="border-b border-white/10 pb-1 last:border-b-0 last:pb-0">
                <div className="font-medium">{entry.name}</div>
                {entry.metadata && <div className="text-white/70 text-[10px]">{formatDate(entry.metadata)}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface LibraryItem {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
  component?: React.ComponentType<any>;
  disabled?: boolean;
}

interface ControlMetadata {
  typeOfControl: "Direct" | "Indirect" | "GITC";
  subtype: "Manual" | "Automated";
  controlId: string;
  controlName: string;
  workspace: string;
}

const libraryItems: LibraryItem[] = [
  {
    id: "confirmation-library",
    title: "Confirmation Library",
    description: "Manage audit confirmations",
    icon: FileCheck,
    accent: "from-emerald-500/20 to-teal-500/20",
  },
  {
    id: "sampling-library",
    title: "Sampling Library",
    description: "View sampling documentation and logs",
    icon: FileCheck,
    accent: "from-emerald-500/20 to-teal-500/20",
  },
  {
    id: "control-library",
    title: "Control Library",
    description: "View all controls created",
    icon: Shield,
    accent: "from-green-500/20 to-emerald-500/20",
  },
  {
    id: "control-owner-library",
    title: "Control Owner Library",
    description: "Control ownership and responsibility matrix",
    icon: Users,
    accent: "from-purple-500/20 to-violet-500/20",
  },
  {
    id: "deficiency-library",
    title: "Deficiency Library",
    description: "Track and manage audit deficiencies",
    icon: XCircle,
    accent: "from-red-500/20 to-rose-500/20",
  },
  {
    id: "document-library",
    title: "Document Library",
    description: "Upload and manage supporting documents",
    icon: FileText,
    accent: "from-slate-500/20 to-gray-500/20",
  },
  {
    id: "information-library",
    title: "Information Library",
    description: "Access to information resources and documentation",
    icon: BookOpen,
    accent: "from-blue-500/20 to-indigo-500/20",
  },
  {
    id: "misstatement-library",
    title: "Misstatement Library",
    description: "Track and manage misstatements",
    icon: AlertCircle,
    accent: "from-orange-500/20 to-amber-500/20",
  },
  {
    id: "rcm-library",
    title: "RCM Library",
    description: "Risk Control Matrix - Controls and their associated ROMMs",
    icon: Network,
    accent: "from-violet-500/20 to-purple-500/20",
  },
  {
    id: "review-note-library",
    title: "Review Note Library",
    description: "View all review notes and comments",
    icon: MessageSquare,
    accent: "from-yellow-500/20 to-amber-500/20",
  },
  {
    id: "risk-library",
    title: "Risk Library",
    description: "View all risks present",
    icon: AlertTriangle,
    accent: "from-red-500/20 to-orange-500/20",
  },
  // Disabled libraries at the end
  {
    id: "business-process-library",
    title: "Business Process Library",
    description: "Business process documentation and workflows",
    icon: Workflow,
    accent: "from-pink-500/20 to-rose-500/20",
    disabled: true,
  },
  {
    id: "it-elements-library",
    title: "IT Elements Library",
    description: "IT controls and system documentation",
    icon: Monitor,
    accent: "from-cyan-500/20 to-teal-500/20",
    disabled: true,
  },
].sort((a, b) => {
  // Sort enabled libraries alphabetically by title
  // Disabled libraries stay at the end
  if (a.disabled && !b.disabled) return 1;
  if (!a.disabled && b.disabled) return -1;
  if (a.disabled && b.disabled) return 0; // Keep disabled order
  return a.title.localeCompare(b.title);
});

interface RommData {
  id: string;
  risk: string;
  assertion: string;
  options: string[];
  section: string;
}

interface InternalControl {
  id: string;
  controlMetadata: {
    typeOfControl: string;
    subtype: string;
    controlId: string;
    controlName: string;
    workspace: string;
  };
  createdAt: string;
  template: string;
}

interface LibrariesPageProps {
  onBack?: () => void;
  activeSection?: string;
  setActiveSection?: (section: string) => void;
}

export default function LibrariesPage({ onBack, activeSection, setActiveSection }: LibrariesPageProps) {
  const { isArchivalMode } = useArchivalMode();
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const { isLimitedReviewExecution } = useExecutionEngagementDetails();
  const visibleLibraryItems = useMemo(
    () =>
      libraryItems.filter(
        (lib) => !isLibraryHiddenForLimitedReview(lib.id, isLimitedReviewExecution),
      ),
    [isLimitedReviewExecution],
  );
  const [selectedLibrary, setSelectedLibrary] = useState<string | null>(null);
  const [rommData, setRommData] = useState<RommData[]>([]);
  const [rommSearchQuery, setRommSearchQuery] = useState<string>("");
  const [libraryFiles, setLibraryFiles] = useState<FileMetadata[]>([]);
  const [internalControls, setInternalControls] = useState<InternalControl[]>([]);
  const [loading, setLoading] = useState(false);
  const [filesLoading, setFilesLoading] = useState(false);
  
  // RCM Library state
  const [rcmControls, setRcmControls] = useState<any[]>([]);
  const [rcmRomms, setRcmRomms] = useState<any[]>([]);
  const [rcmTableData, setRcmTableData] = useState<any[]>([]);
  const [rcmLoading, setRcmLoading] = useState(false);
  const [rcmImportLoading, setRcmImportLoading] = useState(false);
  const [rcmExportLoading, setRcmExportLoading] = useState(false);
  const rcmFileInputRef = React.useRef<HTMLInputElement>(null);

  // Risk Library state
  const [risks, setRisks] = useState<Array<{
    id: string;
    risk: string;
    assertion: string;
    workspace: string;
    controlIds: string[];
    fullData: any;
    signoff: {
      prep_so: Array<{ name: string; metadata: string }>;
      rev_so: Array<{ name: string; metadata: string }>;
      conc_so: Array<{ name: string; metadata: string }>;
      eqr_so: Array<{ name: string; metadata: string }>;
      partner_so: Array<{ name: string; metadata: string }>;
    };
  }>>([]);
  const [risksLoading, setRisksLoading] = useState(false);
  const [riskSignoffLoading, setRiskSignoffLoading] = useState(false);
  const [selectedRisksForSignoff, setSelectedRisksForSignoff] = useState<Set<string>>(new Set());
  const [isDeleteRommDialogOpen, setIsDeleteRommDialogOpen] = useState(false);
  const [selectedRommsToDelete, setSelectedRommsToDelete] = useState<Set<string>>(new Set());
  const [deletingRomms, setDeletingRomms] = useState(false);

  // Control Library state
  const [controls, setControls] = useState<any[]>([]);
  const [controlsLoading, setControlsLoading] = useState(false);
  const [isDeleteControlDialogOpen, setIsDeleteControlDialogOpen] = useState(false);
  const [selectedControlsToDelete, setSelectedControlsToDelete] = useState<Set<string>>(new Set());
  const [deletingControls, setDeletingControls] = useState(false);
  const [controlSignoffLoading, setControlSignoffLoading] = useState(false);
  const [selectedControlsForSignoff, setSelectedControlsForSignoff] = useState<Set<string>>(new Set());
  const [userEngagementRole, setUserEngagementRole] = useState<string | null>(null);

  // Load user engagement role (same rules as Document Library signoff)
  useEffect(() => {
    const loadUserEngagementRole = async () => {
      if (!currentEngagement || !user) {
        setUserEngagementRole(null);
        return;
      }
      try {
        const team = await apiService.getEngagementTeam(currentEngagement.id);
        const currentUserMember = team.find((member: any) => String(member.id) === String(user.id));
        const role = currentUserMember?.role || null;
        setUserEngagementRole(role);
      } catch (error) {
        console.error("Error loading user engagement role for control signoff:", error);
        setUserEngagementRole(null);
      }
    };
    loadUserEngagementRole();
  }, [currentEngagement, user]);

  // Review Note Library state
  const [reviewNotes, setReviewNotes] = useState<any[]>([]);
  const [reviewNotesLoading, setReviewNotesLoading] = useState(false);
  const [selectedReviewNotesToDelete, setSelectedReviewNotesToDelete] = useState<Set<string>>(new Set());
  const [deletingReviewNotes, setDeletingReviewNotes] = useState(false);

  // Confirmation Library state
  const [confirmationTab, setConfirmationTab] = useState<string>("confirmations");
  // Confirmations: { [confirmationId]: { partydetails: { area, sample_set, ConfirmingParty, ... }, templateDetails } }
  const [confirmationsData, setConfirmationsData] = useState<Record<string, any>>({});
  const [confirmationsLoading, setConfirmationsLoading] = useState(false);
  const [confirmationsActivityLog, setConfirmationsActivityLog] = useState<{
    timeline?: Array<{
      letter_id: string;
      action: string;
      stage?: string;
      timestamp?: string;
      performed_by?: string;
      details?: string;
      status?: string;
    }>;
  }>({});
  const [clientFolderFiles, setClientFolderFiles] = useState<{ name: string; url?: string; reference?: string }[]>([]);
  const [loadingClientFiles, setLoadingClientFiles] = useState(false);
  const [savingAlternateForId, setSavingAlternateForId] = useState<string | null>(null);
  const confirmationAlternateFileInputRef = React.useRef<HTMLInputElement>(null);
  const [confirmationIdForUpload, setConfirmationIdForUpload] = useState<string | null>(null);
  const [selectedConfirmationsWorkspace, setSelectedConfirmationsWorkspace] = useState<string>("__all__");
  const [selectedConfirmationsSet, setSelectedConfirmationsSet] = useState<string>("__all__");
  const [domainTestingData, setDomainTestingData] = useState<any[]>([]);
  const [domainTestingLoading, setDomainTestingLoading] = useState(false);
  const [domainTestingRawData, setDomainTestingRawData] = useState<any[]>([]); // Store full records for dialog
  const [selectedDomainRecord, setSelectedDomainRecord] = useState<any | null>(null);
  const [isDomainDetailsDialogOpen, setIsDomainDetailsDialogOpen] = useState(false);
  const [confirmationReconciliationDialogId, setConfirmationReconciliationDialogId] = useState<string | null>(null);
  const [viewingConfirmationId, setViewingConfirmationId] = useState<string | null>(null);
  type ReconRow = { particulars: string; amount: string };
  const [confirmationReconciliationForm, setConfirmationReconciliationForm] = useState<{
    amountAsPerSamplingData: string;
    amountAsPerBooksExtraRows: ReconRow[];
    addRows: ReconRow[];
    lessRows: ReconRow[];
    amountAsPerConfirmation: string;
    file: { fileName: string; type: "upload" | "link" } | null;
  }>({
    amountAsPerSamplingData: "",
    amountAsPerBooksExtraRows: [],
    addRows: [],
    lessRows: [],
    amountAsPerConfirmation: "",
    file: null,
  });
  const [savingConfirmationReconciliation, setSavingConfirmationReconciliation] = useState(false);
  const reconciliationFileInputRef = React.useRef<HTMLInputElement>(null);

  // Sampling Logs state (workspace -> sample set name -> entries[])
  const [samplingLogData, setSamplingLogData] = useState<Record<string, Record<string, any[]>>>({});
  const [samplingLogLoading, setSamplingLogLoading] = useState(false);
  const [selectedSamplingWorkspace, setSelectedSamplingWorkspace] = useState<string>("__all__");
  const [selectedSamplingSet, setSelectedSamplingSet] = useState<string>("__all__");
  const [selectedSamplingEntry, setSelectedSamplingEntry] = useState<{ workspace: string; sampleSet: string; entry: any; entryIndex: number } | null>(null);
  const [isSamplingEntryDialogOpen, setIsSamplingEntryDialogOpen] = useState(false);
  const [samplingLogReconciliationRows, setSamplingLogReconciliationRows] = useState<{ particulars: string; amount: string }[]>([]);
  const [savingReconciliation, setSavingReconciliation] = useState(false);
  const [trialBalanceMappingData, setTrialBalanceMappingData] = useState<Record<string, any[]>>({});
  const [trialBalanceMappingLoading, setTrialBalanceMappingLoading] = useState(false);
  const [selectedNoteLinesForFinancials, setSelectedNoteLinesForFinancials] = useState<string[]>([]);
  const [samplingLogSourceFile, setSamplingLogSourceFile] = useState<"Confirmation_SamplingLog.json" | "Sampling_SamplingLog.json">("Confirmation_SamplingLog.json");

  // Open a sampling file from the juggernaut folder in the default browser (Chrome)
  const handleOpenSamplingFile = async (fileName: string) => {
    if (!fileName) return;
    try {
      let fileUrl: string | undefined;

      // Try to get file URL from loadCloudFiles API first (client folder)
      if ((window as any).sharePointAPI?.loadCloudFiles) {
        const result = await (window as any).sharePointAPI.loadCloudFiles();
        if (result?.success && Array.isArray(result?.data?.files)) {
          const file = (result.data.files as { name: string; url?: string }[]).find(
            (f: any) => f.name === fileName
          );
          fileUrl = file?.url;
        }
      }

      // If URL not found, construct SharePoint URL pointing to juggernaut folder
      if (!fileUrl) {
        const fyYear = getFY_YEAR();
        const config = (window as any).sharePointConfig || {};
        const siteHostname = config.siteHostname || "";
        const sitePath = config.sitePath || "";
        const docLibrary = config.docLibrary || "";

        fileUrl = `https://${siteHostname}${sitePath}/_layouts/15/download.aspx?SourceUrl=${encodeURIComponent(`/${sitePath}/${docLibrary}/${fyYear}/juggernaut/${fileName}`)}`;
      }

      // Use Electron's shell.openExternal to open in default browser (Chrome)
      if ((window as any).electronWindow?.openExternal) {
        try {
          await (window as any).electronWindow.openExternal(fileUrl);
        } catch (error) {
          console.error("Error opening external URL:", error);
          // Fallback to window.open if Electron API fails
          window.open(fileUrl, "_blank", "noopener,noreferrer");
        }
      } else {
        // Fallback to window.open if Electron API is not available
        window.open(fileUrl, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      console.error("Error opening sampling file:", error);
    }
  };

  // Open a confirmation attachment file from the juggernaut/Confirmation folder
  const handleOpenConfirmationAttachment = async (fileName: string) => {
    if (!fileName) return;
    try {
      // Get file's webUrl from SharePoint using Graph API
      const fyYear = getFY_YEAR();
      const filePath = `${fyYear}/juggernaut/Confirmation/${fileName}`;
      
      const result = await (window as any).sharePointAPI?.getFileWebUrl?.(filePath);
      
      if (result?.success && result?.data?.webUrl) {
        const fileUrl = result.data.webUrl;
        
        // Use Electron's shell.openExternal to open in default browser
        if ((window as any).electronWindow?.openExternal) {
          try {
            await (window as any).electronWindow.openExternal(fileUrl);
          } catch (error) {
            console.error("Error opening external URL:", error);
            // Fallback to window.open if Electron API fails
            window.open(fileUrl, "_blank", "noopener,noreferrer");
          }
        } else {
          // Fallback to window.open if Electron API is not available
          window.open(fileUrl, "_blank", "noopener,noreferrer");
        }
      } else {
        alert(`Failed to get file URL: ${result?.error || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error opening confirmation attachment:", error);
      alert(`Error opening file: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  // RCM Filter state
  interface RcmFilter {
    id: string;
    column: string;
    operator: string;
    value: string;
  }
  const [rcmFilters, setRcmFilters] = useState<RcmFilter[]>([]);

  // Filter column options
  const rcmFilterColumns = [
    { value: "rommId", label: "RoMM ID" },
    { value: "rommDescription", label: "RoMM Description" },
    { value: "workspace", label: "Workspace" },
    { value: "assertion", label: "Assertion" },
    { value: "assessment", label: "Assessment" },
    { value: "controlId", label: "Control ID" },
    { value: "controlName", label: "Control Name" },
    { value: "controlType", label: "Control Type" },
    { value: "controlNature", label: "Control Nature" },
  ];

  // Filter operators
  const filterOperators = [
    { value: "contains", label: "Contains" },
    { value: "equals", label: "Equals" },
    { value: "startsWith", label: "Starts with" },
    { value: "endsWith", label: "Ends with" },
    { value: "notContains", label: "Does not contain" },
  ];

  // Information Library state
  const [informationEntries, setInformationEntries] = useState<Array<{
    information_name: string;
    workspace: string[];
    workspace_prefix: string[];
    file_name?: string;
    signoff?: {
      prep_so: Array<{ name: string; metadata: string }>;
      rev_so: Array<{ name: string; metadata: string }>;
      conc_so: Array<{ name: string; metadata: string }>;
      eqr_so: Array<{ name: string; metadata: string }>;
      partner_so: Array<{ name: string; metadata: string }>;
    };
  }>>([]);
  const [informationLoading, setInformationLoading] = useState(false);
  const [informationSignoffLoading, setInformationSignoffLoading] = useState(false);
  const [selectedInformationForSignoff, setSelectedInformationForSignoff] = useState<Set<string>>(new Set());
  const [isInformationFormOpen, setIsInformationFormOpen] = useState(false);
  const [selectedInformation, setSelectedInformation] = useState<string | null>(null);
  const [selectedInformationToDelete, setSelectedInformationToDelete] = useState<Set<string>>(new Set());
  const [isDeleteInformationDialogOpen, setIsDeleteInformationDialogOpen] = useState(false);
  const [deletingInformation, setDeletingInformation] = useState(false);

  // Apply filters to RCM table data
  const applyRcmFilters = (data: any[], filters: RcmFilter[]) => {
    if (filters.length === 0) return data;

    return data.filter((row) => {
      return filters.every((filter) => {
        const filterValue = filter.value.toLowerCase().trim();
        if (!filterValue) return true;

        switch (filter.column) {
          case "rommId":
            return applyOperator(row.rommId?.toLowerCase() || "", filter.operator, filterValue);
          
          case "rommDescription":
            return applyOperator(row.rommDescription?.toLowerCase() || "", filter.operator, filterValue);
          
          case "workspace":
            const workspaceStr = Array.isArray(row.workspace)
              ? row.workspace.map((w: string) => w.toLowerCase()).join(" ")
              : (row.workspace || "").toLowerCase();
            return applyOperator(workspaceStr, filter.operator, filterValue);
          
          case "assertion":
            const assertionStr = Array.isArray(row.assertion)
              ? row.assertion.map((a: string) => a.toLowerCase()).join(" ")
              : (row.assertion || "").toLowerCase();
            return applyOperator(assertionStr, filter.operator, filterValue);
          
          case "assessment":
            return applyOperator((row.assessment || "").toLowerCase(), filter.operator, filterValue);
          
          case "controlId":
            const controlIds = row.linkedControls?.map((c: any) => c.controlId?.toLowerCase() || "").join(" ") || "";
            return applyOperator(controlIds, filter.operator, filterValue);
          
          case "controlName":
            const controlNames = row.linkedControls?.map((c: any) => c.controlName?.toLowerCase() || "").join(" ") || "";
            return applyOperator(controlNames, filter.operator, filterValue);
          
          case "controlType":
            const controlTypes = row.linkedControls?.map((c: any) => c.typeOfControl?.toLowerCase() || "").join(" ") || "";
            return applyOperator(controlTypes, filter.operator, filterValue);
          
          case "controlNature":
            const controlNatures = row.linkedControls?.map((c: any) => c.nature?.toLowerCase() || "").join(" ") || "";
            return applyOperator(controlNatures, filter.operator, filterValue);
          
          default:
            return true;
        }
      });
    });
  };

  // Apply filter operator
  const applyOperator = (cellValue: string, operator: string, filterValue: string): boolean => {
    switch (operator) {
      case "contains":
        return cellValue.includes(filterValue);
      case "equals":
        return cellValue === filterValue;
      case "startsWith":
        return cellValue.startsWith(filterValue);
      case "endsWith":
        return cellValue.endsWith(filterValue);
      case "notContains":
        return !cellValue.includes(filterValue);
      default:
        return true;
    }
  };

  // Add new filter
  const addRcmFilter = () => {
    const newFilter: RcmFilter = {
      id: `filter-${Date.now()}`,
      column: "rommId",
      operator: "contains",
      value: "",
    };
    setRcmFilters([...rcmFilters, newFilter]);
  };

  // Remove filter
  const removeRcmFilter = (filterId: string) => {
    setRcmFilters(rcmFilters.filter((f) => f.id !== filterId));
  };

  // Update filter
  const updateRcmFilter = (filterId: string, updates: Partial<RcmFilter>) => {
    setRcmFilters(
      rcmFilters.map((f) => (f.id === filterId ? { ...f, ...updates } : f))
    );
  };

  // Clear all filters
  const clearAllRcmFilters = () => {
    setRcmFilters([]);
  };

  // Memoize filtered data for performance
  const filteredRcmData = useMemo(() => {
    return applyRcmFilters(rcmTableData, rcmFilters);
  }, [rcmTableData, rcmFilters]);
  
  // Control viewer modal state
  // Old modal state removed - using new InternalControlView instead
  
  // Control Owner Library state
  const [controlOwners, setControlOwners] = useState<any>({});
  const [controlOwnersLoading, setControlOwnersLoading] = useState(false);
  const [isControlOwnerFormOpen, setIsControlOwnerFormOpen] = useState(false);
  const [editingOwner, setEditingOwner] = useState<{ id: string; data: any } | null>(null);
  const [isDeleteControlOwnerDialogOpen, setIsDeleteControlOwnerDialogOpen] = useState(false);
  const [selectedControlOwnersToDelete, setSelectedControlOwnersToDelete] = useState<Set<string>>(new Set());
  const [deletingControlOwners, setDeletingControlOwners] = useState(false);

  // Deficiency Library state
  const [isDeficiencyFormOpen, setIsDeficiencyFormOpen] = useState(false);
  const [isDeleteDeficiencyDialogOpen, setIsDeleteDeficiencyDialogOpen] = useState(false);
  const [selectedDeficienciesToDelete, setSelectedDeficienciesToDelete] = useState<Set<string>>(new Set());
  const [deletingDeficiencies, setDeletingDeficiencies] = useState(false);
  
  // Misstatement Library state
  const [isMisstatementFormOpen, setIsMisstatementFormOpen] = useState(false);
  const [isDeleteMisstatementDialogOpen, setIsDeleteMisstatementDialogOpen] = useState(false);
  const [selectedMisstatementsToDelete, setSelectedMisstatementsToDelete] = useState<Set<string>>(new Set());
  const [deletingMisstatements, setDeletingMisstatements] = useState(false);
  const [deficiencies, setDeficiencies] = useState<
    Array<{
      id: string;
      control_relevance: string;
      control_id: string[];
      deficiency_type: string;
      description: string | null;
      root_cause: string | null;
      signoff: {
        prep_so: Array<{ name: string; metadata: string }>;
        rev_so: Array<{ name: string; metadata: string }>;
        conc_so: Array<{ name: string; metadata: string }>;
        eqr_so: Array<{ name: string; metadata: string }>;
        partner_so: Array<{ name: string; metadata: string }>;
      };
    }>
  >([]);
  const [deficienciesLoading, setDeficienciesLoading] = useState(false);
  const [deficiencySignoffLoading, setDeficiencySignoffLoading] = useState(false);
  const [selectedDeficienciesForSignoff, setSelectedDeficienciesForSignoff] = useState<Set<string>>(new Set());
  const [activeDeficiencyId, setActiveDeficiencyId] = useState<string | null>(null);

  // Misstatement Library state
  const [misstatements, setMisstatements] = useState<
    Array<{
      id: string;
      workspace: string;
      type: "Factual" | "Judgemental" | "Projected";
      data: any;
      signoff: {
        prep_so: Array<{ name: string; metadata: string }>;
        rev_so: Array<{ name: string; metadata: string }>;
        conc_so: Array<{ name: string; metadata: string }>;
        eqr_so: Array<{ name: string; metadata: string }>;
        partner_so: Array<{ name: string; metadata: string }>;
      };
    }>
  >([]);
  const [misstatementsLoading, setMisstatementsLoading] = useState(false);
  const [misstatementSignoffLoading, setMisstatementSignoffLoading] = useState(false);
  const [selectedMisstatementsForSignoff, setSelectedMisstatementsForSignoff] = useState<Set<string>>(new Set());
  const [activeMisstatement, setActiveMisstatement] = useState<{
    id: string;
    data: any;
  } | null>(null);
  
  // Control form state
  const [selectedControlForm, setSelectedControlForm] = useState<{
    metadata: ControlMetadata;
    template: any;
    existingData?: any;
  } | null>(null);

  // Internal Control View state (for integrated view)
  const [viewingControl, setViewingControl] = useState<{
    controlId: string;
    controlName: string;
    activeSection?: "control-summary" | "evaluate-design" | "prior-year" | "risk-associated" | "operating-effectiveness" | "rollforward";
  } | null>(null);

  // Handle section changes from LeftPanel and initialize viewingControl from activeSection
  useEffect(() => {
    if (activeSection && activeSection.startsWith("internal-control:")) {
      const parts = activeSection.split(":");
      const controlId = parts[1];
      const sectionId = (parts[2] || "control-summary") as
        | "control-summary"
        | "evaluate-design"
        | "prior-year"
        | "risk-associated"
        | "operating-effectiveness"
        | "rollforward";

      if (viewingControl && viewingControl.controlId === controlId) {
        // Only update when the section actually changed — avoid infinite setState loop
        if (viewingControl.activeSection !== sectionId) {
          setViewingControl({
            ...viewingControl,
            activeSection: sectionId,
          });
        }
      } else if (!viewingControl && controlId) {
        // Initialize viewingControl if not set (e.g., when navigating directly via URL or state)
        // Try to get control name from internalControls if available
        const control = internalControls.find(c =>
          (c.controlMetadata?.controlId || c.id) === controlId
        );
        const controlName = control?.controlMetadata?.controlName || (control as any)?.controlName || "Untitled Control";

        setViewingControl({
          controlId,
          controlName,
          activeSection: sectionId,
        });
      }
    } else if (!activeSection?.startsWith("internal-control:") && viewingControl) {
      // Clear viewingControl if we're no longer viewing a control
      setViewingControl(null);
    }
  }, [activeSection, viewingControl, internalControls]);

  // Load Control Owners from SharePoint
  const loadControlOwners = async () => {
    setControlOwnersLoading(true);
    try {
      if (window.sharePointAPI && typeof window.sharePointAPI.downloadControlOwnersJson === 'function') {
        const downloadResult = await window.sharePointAPI.downloadControlOwnersJson();

        if (downloadResult.success && downloadResult.data) {
          setControlOwners(downloadResult.data.controlOwners || {});
        } else {
          // File doesn't exist yet, set empty object
          setControlOwners({});
        }
      } else {
        setControlOwners({});
      }
    } catch (error) {
      console.error('Failed to load control owners:', error);
      setControlOwners({});
    } finally {
      setControlOwnersLoading(false);
    }
  };

  // Load Deficiencies from SharePoint
  const loadDeficiencies = async () => {
    setDeficienciesLoading(true);
    try {
      if (window.sharePointAPI && typeof window.sharePointAPI.downloadDeficiencyJson === "function") {
        const res = await window.sharePointAPI.downloadDeficiencyJson();
        if (res.success && res.data) {
          const raw = res.data.libraries_deficiency || {};
          const signoffDefault = {
            prep_so: [] as Array<{ name: string; metadata: string }>,
            rev_so: [] as Array<{ name: string; metadata: string }>,
            conc_so: [] as Array<{ name: string; metadata: string }>,
            eqr_so: [] as Array<{ name: string; metadata: string }>,
            partner_so: [] as Array<{ name: string; metadata: string }>,
          };
          const items = Object.entries(raw).map(([id, value]: [string, any]) => {
            const signoff = value.signoff || {};
            return {
              id,
              control_relevance: value.control_relevance || "",
              control_id: Array.isArray(value.control_id) ? value.control_id : [],
              deficiency_type: value.deficiency_type || "",
              description: value.description ?? null,
              root_cause: value.root_cause ?? null,
              signoff: {
                prep_so: Array.isArray(signoff.prep_so) ? signoff.prep_so : signoffDefault.prep_so,
                rev_so: Array.isArray(signoff.rev_so) ? signoff.rev_so : signoffDefault.rev_so,
                conc_so: Array.isArray(signoff.conc_so) ? signoff.conc_so : signoffDefault.conc_so,
                eqr_so: Array.isArray(signoff.eqr_so) ? signoff.eqr_so : signoffDefault.eqr_so,
                partner_so: Array.isArray(signoff.partner_so) ? signoff.partner_so : signoffDefault.partner_so,
              },
            };
          });
          setDeficiencies(items);
        } else {
          setDeficiencies([]);
        }
      } else {
        setDeficiencies([]);
      }
    } catch (error) {
      console.error("Failed to load deficiencies:", error);
      setDeficiencies([]);
    } finally {
      setDeficienciesLoading(false);
    }
  };

  // Load control owners when control-owner-library is selected
  useEffect(() => {
    if (selectedLibrary === 'control-owner-library') {
      loadControlOwners();
    }
  }, [selectedLibrary]);

  // Load deficiencies when deficiency-library is selected
  useEffect(() => {
    if (selectedLibrary === "deficiency-library") {
      loadDeficiencies();
    }
  }, [selectedLibrary]);

  // Load Misstatements from SharePoint
  const loadMisstatements = async () => {
    setMisstatementsLoading(true);
    try {
      if (window.sharePointAPI && typeof window.sharePointAPI.downloadMisstatementJson === "function") {
        const res = await window.sharePointAPI.downloadMisstatementJson();
        if (res.success && res.data) {
          const misstatementData = res.data;
          const librariesMisstatement =
            misstatementData.Libraries_Misstatement ||
            misstatementData.libraries_Misstatement ||
            {};
          const misstatementsObj = librariesMisstatement.misstatements || {};
          const signoffDefault = {
            prep_so: [] as Array<{ name: string; metadata: string }>,
            rev_so: [] as Array<{ name: string; metadata: string }>,
            conc_so: [] as Array<{ name: string; metadata: string }>,
            eqr_so: [] as Array<{ name: string; metadata: string }>,
            partner_so: [] as Array<{ name: string; metadata: string }>,
          };
          const items = Object.entries(misstatementsObj).map(([id, value]: [string, any]) => {
            const signoff = value.signoff || {};
            return {
              id,
              workspace: value.workspace || "",
              type: value.type || "Factual",
              data: value,
              signoff: {
                prep_so: Array.isArray(signoff.prep_so) ? signoff.prep_so : signoffDefault.prep_so,
                rev_so: Array.isArray(signoff.rev_so) ? signoff.rev_so : signoffDefault.rev_so,
                conc_so: Array.isArray(signoff.conc_so) ? signoff.conc_so : signoffDefault.conc_so,
                eqr_so: Array.isArray(signoff.eqr_so) ? signoff.eqr_so : signoffDefault.eqr_so,
                partner_so: Array.isArray(signoff.partner_so) ? signoff.partner_so : signoffDefault.partner_so,
              },
            };
          });
          setMisstatements(items);
        } else {
          setMisstatements([]);
        }
      } else {
        setMisstatements([]);
      }
    } catch (error) {
      console.error("Failed to load misstatements:", error);
      setMisstatements([]);
    } finally {
      setMisstatementsLoading(false);
    }
  };

  // Load misstatements when misstatement-library is selected
  useEffect(() => {
    if (selectedLibrary === "misstatement-library") {
      loadMisstatements();
    }
  }, [selectedLibrary]);

  // Debug: Log when modal state changes
  useEffect(() => {
    console.log('isControlOwnerFormOpen changed to:', isControlOwnerFormOpen);
  }, [isControlOwnerFormOpen]);

  // Load ROMM data from Structure.json
  useEffect(() => {
    const loadRommData = async () => {
      setLoading(true);
      try {
        // Load the Structure.json file from the payroll directory
        const response = await fetch("/payroll/Structure.json");
        if (!response.ok) {
          throw new Error("Failed to load Structure.json");
        }
        const structureData = await response.json();

        // Load Instructions.json to get ROMM details
        const instructionsResponse = await fetch("/payroll/Instructions.json");
        if (!instructionsResponse.ok) {
          throw new Error("Failed to load Instructions.json");
        }
        const instructionsData = await instructionsResponse.json();

        // Extract ROMM data from structure and instructions
        const romms: RommData[] = [];
        
        // Get ROMMs from Instructions.json
        if (instructionsData.romms) {
          instructionsData.romms.forEach((romm: any) => {
            romms.push({
              id: romm.id,
              risk: romm.risk,
              assertion: romm.assertion,
              options: romm.options,
              section: "Employee Benefits Expense" // Default section for now
            });
          });
        }

        // Also check Structure.json for any additional ROMMs
        Object.keys(structureData).forEach(key => {
          if (key.startsWith("EBE.SR.") && !romms.find(r => r.id === key)) {
            romms.push({
              id: key,
              risk: `Risk for ${key}`,
              assertion: "Unknown",
              options: ["Lower", "Higher", "Significant", "NRPMM"],
              section: "Employee Benefits Expense"
            });
          }
        });

        setRommData(romms);
      } catch (error) {
        console.error("Error loading ROMM data:", error);
        // Fallback to hardcoded data if file loading fails
        const fallbackData: RommData[] = [
          {
            id: "EBE.SR.001",
            risk: "Payroll expenses is recorded that did not occur",
            assertion: "Occurrence",
            options: ["Lower", "Higher", "Significant", "NRPMM"],
            section: "Employee Benefits Expense"
          },
          {
            id: "EBE.SR.002",
            risk: "Payroll expenses is incomplete",
            assertion: "Completeness",
            options: ["Lower", "Higher", "Significant", "NRPMM"],
            section: "Employee Benefits Expense"
          },
          {
            id: "EBE.SR.003",
            risk: "Payroll expenses is recorded in incorrect period",
            assertion: "Cutoff",
            options: ["Lower", "Higher", "Significant", "NRPMM"],
            section: "Employee Benefits Expense"
          },
          {
            id: "EBE.SR.004",
            risk: "Payroll expenses is not recorded at proper amount",
            assertion: "Accuracy",
            options: ["Lower", "Higher", "Significant", "NRPMM"],
            section: "Employee Benefits Expense"
          },
          {
            id: "EBE.SR.005",
            risk: "Payroll expenses is not recorded in proper account",
            assertion: "Classification",
            options: ["Lower", "Higher", "Significant", "NRPMM"],
            section: "Employee Benefits Expense"
          },
        ];
        setRommData(fallbackData);
      } finally {
        setLoading(false);
      }
    };

    loadRommData();
  }, []);

  // Determine selected library based on activeSection (and optional confirmation workspace filter)
  useEffect(() => {
    if (activeSection === "libraries") {
      setSelectedLibrary(null);
      return;
    }
    if (activeSection && activeSection.includes("-library")) {
      const libraryId = activeSection.includes(":") ? activeSection.split(":")[0] : activeSection;
      if (isLibraryHiddenForLimitedReview(libraryId, isLimitedReviewExecution)) {
        setSelectedLibrary(null);
        if (setActiveSection) {
          setActiveSection("libraries");
        }
        return;
      }
      setSelectedLibrary(libraryId);
      // If navigating to confirmation-library with workspace filter (e.g. "confirmation-library:Litigations & Claims")
      if (libraryId === "confirmation-library" && activeSection.includes(":")) {
        const encodedWorkspace = activeSection.slice("confirmation-library:".length);
        try {
          const workspace = decodeURIComponent(encodedWorkspace);
          setSelectedConfirmationsWorkspace(workspace);
          setConfirmationTab("confirmations");
        } catch {
          setSelectedConfirmationsWorkspace("__all__");
        }
      }
      // If navigating to sampling-library with workspace filter (e.g. "sampling-library:Trade Receivables")
      if (libraryId === "sampling-library" && activeSection.includes(":")) {
        const encodedWorkspace = activeSection.slice("sampling-library:".length);
        try {
          const workspace = decodeURIComponent(encodedWorkspace);
          setSelectedSamplingWorkspace(workspace);
          setSelectedSamplingSet("__all__");
        } catch {
          setSelectedSamplingWorkspace("__all__");
          setSelectedSamplingSet("__all__");
        }
      }
      // Load information entries when Information Library is selected from left panel
      if (libraryId === "information-library") {
        loadInformationEntries();
      }
    }
  }, [activeSection, isLimitedReviewExecution, setActiveSection]);

  // Load RCM data (both ROMMs and Controls)
  const loadRcmData = async () => {
    setRcmLoading(true);
    try {
      // Load Sections.json to get valid workspaces
      let validWorkspaces = new Set<string>();
      if ((window as any).sharePointAPI?.downloadSectionsJson) {
        try {
          const sectionsRes = await (window as any).sharePointAPI.downloadSectionsJson();
          if (sectionsRes.success && sectionsRes.data) {
            const sectionsData = sectionsRes.data;
            
            // Extract all workspace names from Planning, Execution, and ConcludingProcedures
            if (sectionsData.Planning) {
              Object.keys(sectionsData.Planning).forEach((workspace) => {
                validWorkspaces.add(workspace);
              });
            }
            if (sectionsData.Execution) {
              Object.keys(sectionsData.Execution).forEach((workspace) => {
                validWorkspaces.add(workspace);
              });
            }
            if (sectionsData.ConcludingProcedures) {
              Object.keys(sectionsData.ConcludingProcedures).forEach((workspace) => {
                validWorkspaces.add(workspace);
              });
            }
            
            console.log(`[RCM Library] Loaded ${validWorkspaces.size} valid workspaces from Sections.json`);
          }
        } catch (e) {
          console.error("Error loading Sections.json:", e);
          // Continue without filtering if Sections.json can't be loaded
        }
      }

      // Load ROMMs from Libraries_Romm.json
      let romms: any[] = [];
      if ((window as any).sharePointAPI?.readRommLibrary) {
        try {
          const rommRes = await (window as any).sharePointAPI.readRommLibrary();
          if (rommRes.success && rommRes.data?.romm_library) {
            romms = Array.isArray(rommRes.data.romm_library) 
              ? rommRes.data.romm_library 
              : [];
          }
        } catch (e) {
          console.error("Error loading ROMM library:", e);
        }
      }

      // Filter ROMMs: only include those whose workspace is in Sections.json
      romms = romms.filter((romm) => {
        const workspace = romm.workspace || "";
        const workspaceArray = Array.isArray(workspace) 
          ? workspace 
          : workspace 
          ? [workspace] 
          : [];
        
        // Check if at least one workspace in the ROMM's workspace array exists in Sections.json
        return workspaceArray.some((ws: string) => validWorkspaces.has(ws));
      });
      console.log(`[RCM Library] Filtered to ${romms.length} ROMMs with valid workspaces`);

      // Load Controls from Libraries_RCM.json
      let controls: any[] = [];
      if ((window as any).sharePointAPI?.downloadRcmJson) {
        try {
          const rcmRes = await (window as any).sharePointAPI.downloadRcmJson();
          if (rcmRes.success && rcmRes.data) {
            const data = rcmRes.data;
            const librariesRcm = data.libraries_RCM || data.libraries_rcm || {};
            const entries = Object.entries(librariesRcm) as [string, any][];

            controls = entries.map(([controlId, entry]) => {
              return {
                controlId,
                controlName: entry.control_name || entry.controlName || controlId,
                typeOfControl: entry.TypeOfControl || entry.typeOfControl || "Unknown",
                nature: entry.Nature || entry.subtype || "Unknown",
                rommIds: Array.isArray(entry.romm) ? entry.romm.map(String) : [],
              };
            });
          }
        } catch (e) {
          console.error("Error loading RCM controls:", e);
        }
      }

      setRcmRomms(romms);
      setRcmControls(controls);

      // Build table data: one row per unique ROMM ID
      const tableData: any[] = [];
      const processedRommIds = new Set<string>();

      romms.forEach((romm) => {
        const rommId = romm["romm-id"] || romm.romm_id || "";
        if (!rommId || processedRommIds.has(rommId)) return;
        processedRommIds.add(rommId);

        // Get control IDs from ROMM's control_id field (from Libraries_Romm.json)
        const rommControlIds = Array.isArray(romm.control_id)
          ? romm.control_id.map(String).filter((id: string) => id.trim())
          : romm.control_id
          ? [String(romm.control_id)]
          : [];

        // Find all controls linked to this ROMM from two sources:
        // 1. Controls that have this ROMM ID in their romm array (from Libraries_RCM.json)
        // 2. Controls whose ID is in the ROMM's control_id array (from Libraries_Romm.json)
        const linkedControls = controls.filter((control) => {
          // Check if control has this ROMM in its romm array
          const hasRommInControl = control.rommIds.includes(rommId);
          
          // Check if control ID is in ROMM's control_id array
          const hasControlInRomm = rommControlIds.some((id: string) => 
            String(id).trim() === String(control.controlId).trim()
          );
          
          return hasRommInControl || hasControlInRomm;
        });

        // Also include controls that are in ROMM's control_id but not found in controls list
        // (in case they exist in Libraries_Romm.json but not in Libraries_RCM.json)
        const missingControls = rommControlIds
          .filter((controlId: string) => {
            const found = controls.find((c) => 
              String(c.controlId).trim() === String(controlId).trim()
            );
            return !found;
          })
          .map((controlId: string) => ({
            controlId: controlId.trim(),
            controlName: controlId.trim(), // Use control ID as name if not found
            typeOfControl: "Unknown",
            nature: "Unknown",
          }));

        // Get workspace (can be string or array)
        const workspace = romm.workspace || "";
        const workspaceArray = Array.isArray(workspace) 
          ? workspace 
          : workspace 
          ? [workspace] 
          : [];

        tableData.push({
          rommId,
          rommDescription: romm.description || romm.romm_description || "",
          workspace: workspaceArray,
          assertion: romm.assertion || "",
          assessment: romm.assesment || romm.assessment || "",
          linkedControls: [
            ...linkedControls.map((c) => ({
              controlId: c.controlId,
              controlName: c.controlName,
              typeOfControl: c.typeOfControl,
              nature: c.nature,
            })),
            ...missingControls,
          ],
        });
      });

      setRcmTableData(tableData);
    } catch (e) {
      console.error("Error loading RCM data:", e);
      setRcmTableData([]);
    } finally {
      setRcmLoading(false);
    }
  };

  // Handle RCM Excel export
  const handleRcmExport = async () => {
    setRcmExportLoading(true);
    try {
      // Download Libraries_RCM.json from SharePoint
      if (!(window as any).sharePointAPI?.downloadRcmJson) {
        throw new Error("SharePoint API not available");
      }

      const rcmRes = await (window as any).sharePointAPI.downloadRcmJson();
      if (!rcmRes.success || !rcmRes.data) {
        throw new Error(rcmRes.error || "Failed to download RCM data");
      }

      const data = rcmRes.data;
      const librariesRcm = data.libraries_RCM || data.libraries_rcm || {};
      const entries = Object.entries(librariesRcm) as [string, any][];

      if (entries.length === 0) {
        alert("No controls found in RCM Library to export.");
        return;
      }

      // Prepare data for Excel export in the same format as import
      const exportData = entries.map(([controlId, entry]) => {
        // Get risks (romm field) - join with comma if multiple
        const rommIds = Array.isArray(entry.romm) 
          ? entry.romm.map(String).filter((id: string) => id.trim())
          : entry.romm 
          ? [String(entry.romm)]
          : [];
        const associatedRisks = rommIds.join(",");

        return {
          "Control ID (to mention each control separately)": controlId,
          "Associated Risks (for multiple risks separate the risk IDs with a comma (,))": associatedRisks,
          "Control Name": entry.control_name || entry.controlName || controlId,
          "Type of Control (Direct/Indirect/GITC)": entry.TypeOfControl || entry.typeOfControl || "Direct",
          "Control Nature (Manual/Automated)": entry.Nature || entry.subtype || "Manual"
        };
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "RCM Export");

      // Generate Excel file buffer
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

      // Create blob and download
      const blob = new Blob([excelBuffer], { 
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `RCM_Export_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      alert(` Successfully exported ${exportData.length} control(s) to Excel file!`);
    } catch (error) {
      console.error("Error exporting RCM data:", error);
      alert(` Error exporting RCM data: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setRcmExportLoading(false);
    }
  };

  // Handle RCM Excel import
  const handleRcmImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      alert("Please select an Excel file (.xlsx or .xls)");
      if (rcmFileInputRef.current) {
        rcmFileInputRef.current.value = '';
      }
      return;
    }

    setRcmImportLoading(true);
    try {
      // First, delete any previous RCM import files
      if ((window as any).sharePointAPI?.deletePreviousRcmImportFiles) {
        const deleteResult = await (window as any).sharePointAPI.deletePreviousRcmImportFiles();
        if (!deleteResult.success) {
          console.warn('Warning: Failed to delete previous RCM import files:', deleteResult.error);
          // Continue anyway - might be first import
        } else if (deleteResult.data?.deletedCount > 0) {
          console.log(`Deleted ${deleteResult.data.deletedCount} previous RCM import file(s)`);
        }
      }

      // Convert file to base64
      const base64Content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64 = result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });

      // Upload file to SharePoint
      if (!(window as any).sharePointAPI?.uploadFile) {
        throw new Error("SharePoint API not available");
      }

      // Get FY_YEAR from database dynamically
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI.uploadFile({
        fileName: file.name,
        fileContent: base64Content,
        folderName: 'juggernaut',
        referenceValue: `RCM Import: ${file.name}`,
        sectionList: ['Libraries_RCM'],
        fyYear: fyYear
      });

      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Failed to upload file');
      }

      // Trigger Python script to process the Excel file
      if ((window as any).sharePointAPI?.processRcmImport) {
        const processResult = await (window as any).sharePointAPI.processRcmImport(file.name);
        
        if (processResult.success) {
          alert(` Successfully imported controls from ${file.name}!\n\nCreated/Updated ${processResult.data?.controlsProcessed || 0} control(s).`);
          // Reload RCM data to show updates
          await loadRcmData();
        } else {
          throw new Error(processResult.error || 'Failed to process import');
        }
      } else {
        // Fallback: just show success message
        alert(` File uploaded successfully! Processing will begin shortly.`);
        await loadRcmData();
      }
    } catch (error) {
      console.error('Error importing RCM file:', error);
      alert(` Error importing file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setRcmImportLoading(false);
      if (rcmFileInputRef.current) {
        rcmFileInputRef.current.value = '';
      }
    }
  };

  // Load files when library is selected
  useEffect(() => {
    if (selectedLibrary) {
      loadLibraryFiles(selectedLibrary);
      if (selectedLibrary === 'rcm-library') {
        loadRcmData();
      } else if (selectedLibrary === 'risk-library') {
        loadRisks();
      } else if (selectedLibrary === 'control-library') {
        loadControls();
      } else if (selectedLibrary === 'review-note-library') {
        loadReviewNotes();
      }
    }
  }, [selectedLibrary]);

  // Load domain testing data when tab changes to domain-testing/confirmations
  useEffect(() => {
    if (selectedLibrary === 'confirmation-library' && (confirmationTab === 'domain-testing' || confirmationTab === 'confirmations')) {
      loadDomainTestingData();
    }
  }, [confirmationTab, selectedLibrary]);

  // Load sampling log data when tab changes to sampling-logs
  useEffect(() => {
    if (selectedLibrary === 'confirmation-library' && confirmationTab === 'sampling-logs') {
      loadSamplingLogData("Confirmation_SamplingLog.json");
    }
    if (selectedLibrary === 'sampling-library') {
      loadSamplingLogData("Sampling_SamplingLog.json");
    }
  }, [confirmationTab, selectedLibrary]);

  // Load confirmations data and client folder files when tab changes to confirmations
  useEffect(() => {
    if (selectedLibrary === 'confirmation-library' && confirmationTab === 'confirmations') {
      loadConfirmationsData();
      loadClientFolderFiles();
    }
  }, [confirmationTab, selectedLibrary]);

  // Initialize confirmation reconciliation form when dialog opens
  useEffect(() => {
    if (!confirmationReconciliationDialogId) return;
    const data = confirmationsData[confirmationReconciliationDialogId];
    const amount = data?.partydetails?.amount;
    const recon = data?.reconciliation;
    const amountAsPerSampling =
      recon?.amountAsPerSamplingData != null
        ? String(recon.amountAsPerSamplingData)
        : amount != null
          ? String(amount)
          : "";
    setConfirmationReconciliationForm({
      amountAsPerSamplingData: amountAsPerSampling,
      amountAsPerBooksExtraRows: Array.isArray(recon?.amountAsPerBooksExtraRows)
        ? recon.amountAsPerBooksExtraRows.map((r: any) => ({
            particulars: r.particulars ?? "",
            amount: r.amount != null ? String(r.amount) : "",
          }))
        : [],
      addRows: Array.isArray(recon?.addRows)
        ? recon.addRows.map((r: any) => ({
            particulars: r.particulars ?? "",
            amount: r.amount != null ? String(r.amount) : "",
          }))
        : [],
      lessRows: Array.isArray(recon?.lessRows)
        ? recon.lessRows.map((r: any) => ({
            particulars: r.particulars ?? "",
            amount: r.amount != null ? String(r.amount) : "",
          }))
        : [],
      amountAsPerConfirmation: recon?.amountAsPerConfirmation != null ? String(recon.amountAsPerConfirmation) : "",
      file: recon?.file && typeof recon.file === "object" ? recon.file : null,
    });
  }, [confirmationReconciliationDialogId, confirmationsData]);

  // Load risks from Libraries_Romms.json
  const loadRisks = async () => {
    setRisksLoading(true);
    try {
      // Load Sections.json to get valid workspaces
      let validWorkspaces = new Set<string>();
      if ((window as any).sharePointAPI?.downloadSectionsJson) {
        try {
          const sectionsRes = await (window as any).sharePointAPI.downloadSectionsJson();
          if (sectionsRes.success && sectionsRes.data) {
            const sectionsData = sectionsRes.data;
            
            // Extract all workspace names from Planning, Execution, and ConcludingProcedures
            if (sectionsData.Planning) {
              Object.keys(sectionsData.Planning).forEach((workspace) => {
                validWorkspaces.add(workspace);
              });
            }
            if (sectionsData.Execution) {
              Object.keys(sectionsData.Execution).forEach((workspace) => {
                validWorkspaces.add(workspace);
              });
            }
            if (sectionsData.ConcludingProcedures) {
              Object.keys(sectionsData.ConcludingProcedures).forEach((workspace) => {
                validWorkspaces.add(workspace);
              });
            }
            
            console.log(`[Risk Library] Loaded ${validWorkspaces.size} valid workspaces from Sections.json`);
          }
        } catch (e) {
          console.error("Error loading Sections.json:", e);
          // Continue without filtering if Sections.json can't be loaded
        }
      }

      const signoffDefault = {
        prep_so: [] as Array<{ name: string; metadata: string }>,
        rev_so: [] as Array<{ name: string; metadata: string }>,
        conc_so: [] as Array<{ name: string; metadata: string }>,
        eqr_so: [] as Array<{ name: string; metadata: string }>,
        partner_so: [] as Array<{ name: string; metadata: string }>,
      };

      // Prefer downloadRommJson so we get signoff from file; fallback to readRommLibrary
      let rommLibrary: any[] = [];
      if ((window as any).sharePointAPI?.downloadRommJson) {
        try {
          const res = await (window as any).sharePointAPI.downloadRommJson();
          if (res?.success && res?.data?.romm_library) {
            rommLibrary = Array.isArray(res.data.romm_library) ? res.data.romm_library : [];
          }
        } catch (_) {
          // fallback below
        }
      }
      if (rommLibrary.length === 0 && (window as any).sharePointAPI?.readRommLibrary) {
        const res = await (window as any).sharePointAPI.readRommLibrary();
        if (res?.success && res?.data?.romm_library) {
          rommLibrary = Array.isArray(res.data.romm_library) ? res.data.romm_library : [];
        }
      }

      if (rommLibrary.length > 0) {
        let risksList = rommLibrary.map((romm: any) => {
          const signoff = romm.signoff || {};
          return {
            id: romm["romm-id"],
            risk: romm.risk || romm.description || "",
            assertion: typeof romm.assertion === "string"
              ? romm.assertion
              : Array.isArray(romm.assertion)
                ? romm.assertion.join(", ")
                : romm.assertion ? String(romm.assertion) : "",
            workspace: romm.workspace || "",
            controlIds: Array.isArray(romm.control_id)
              ? romm.control_id
              : romm.control_id
                ? [romm.control_id]
                : [],
            fullData: romm,
            signoff: {
              prep_so: Array.isArray(signoff.prep_so) ? signoff.prep_so : signoffDefault.prep_so,
              rev_so: Array.isArray(signoff.rev_so) ? signoff.rev_so : signoffDefault.rev_so,
              conc_so: Array.isArray(signoff.conc_so) ? signoff.conc_so : signoffDefault.conc_so,
              eqr_so: Array.isArray(signoff.eqr_so) ? signoff.eqr_so : signoffDefault.eqr_so,
              partner_so: Array.isArray(signoff.partner_so) ? signoff.partner_so : signoffDefault.partner_so,
            },
          };
        });

        if (validWorkspaces.size > 0) {
          risksList = risksList.filter((risk: any) =>
            risk.workspace && validWorkspaces.has(risk.workspace)
          );
          console.log(`[Risk Library] Filtered to ${risksList.length} ROMMs with valid workspaces`);
        } else {
          console.warn("[Risk Library] No valid workspaces found in Sections.json, showing all ROMMs");
        }

        setRisks(risksList);
      } else {
        setRisks([]);
      }
    } catch (error) {
      console.error("Failed to load risks", error);
      setRisks([]);
    } finally {
      setRisksLoading(false);
    }
  };

  // Load controls from Libraries_RCM.json
  const loadControls = async () => {
    setControlsLoading(true);
    try {
      if ((window as any).sharePointAPI?.downloadRcmJson) {
        const res = await (window as any).sharePointAPI.downloadRcmJson();
        if (res?.success && res?.data) {
          const data = res.data;
          const librariesRcm = data.libraries_RCM || data.libraries_rcm || {};
          const entries = Object.entries(librariesRcm) as [string, any][];

          const controlsList = entries.map(([controlId, entry]) => {
            const signoff = entry.signoff || {};
            return {
              controlId,
              controlName: entry.control_name || controlId,
              typeOfControl: entry.TypeOfControl || "Unknown",
              nature: entry.Nature || "Unknown",
              workspace: entry.workspace || [],
              rommIds: Array.isArray(entry.romm)
                ? entry.romm.map(String).filter((id: string) => id.trim())
                : entry.romm
                ? [String(entry.romm)]
                : [],
              signoff: {
                prep_so: Array.isArray(signoff.prep_so) ? signoff.prep_so : [],
                rev_so: Array.isArray(signoff.rev_so) ? signoff.rev_so : [],
                conc_so: Array.isArray(signoff.conc_so) ? signoff.conc_so : [],
                eqr_so: Array.isArray(signoff.eqr_so) ? signoff.eqr_so : [],
                partner_so: Array.isArray(signoff.partner_so) ? signoff.partner_so : [],
              },
            };
          });
          setControls(controlsList);
        } else {
          setControls([]);
        }
      } else {
        setControls([]);
      }
    } catch (error) {
      console.error("Failed to load controls", error);
      setControls([]);
    } finally {
      setControlsLoading(false);
    }
  };

  // Load review notes from Review_Comments.json
  const loadReviewNotes = async () => {
    setReviewNotesLoading(true);
    try {
      const result = await (window as any).sharePointAPI?.downloadClientJson?.("Review_Comments.json");
      if (result?.success && result?.data) {
        const data = result.data;
        if (data.comments && Array.isArray(data.comments)) {
          setReviewNotes(data.comments);
        } else {
          setReviewNotes([]);
        }
      } else {
        setReviewNotes([]);
      }
    } catch (error) {
      console.error("Error loading review notes:", error);
      setReviewNotes([]);
    } finally {
      setReviewNotesLoading(false);
    }
  };

  // Load Domain Testing data from Confirmation_DomainTesting.json
  const loadDomainTestingData = async () => {
    setDomainTestingLoading(true);
    try {
      const result = await (window as any).sharePointAPI?.downloadClientJson?.("Confirmation_DomainTesting.json");
      if (result?.success && result?.data) {
        const data = result.data;
        // Handle domain_records array structure
        let entries: any[] = [];
        
        if (data.domain_records && Array.isArray(data.domain_records)) {
          entries = data.domain_records;
        } else if (Array.isArray(data)) {
          // Fallback: if data is directly an array
          entries = data;
        } else if (data.domain_info && data.confirming_party_info) {
          // Fallback: single entry object format
          entries = [data];
        }

        // Store raw data for dialog display
        setDomainTestingRawData(entries);

        // Map the data to table format
        const mappedData = entries.map((entry, index) => {
          const domainInfo = entry.domain_info || {};
          const confirmingPartyInfo = entry.confirming_party_info || {};
          
          return {
            id: entry.id || `domain-${index}`,
            index: index, // Store index to link back to raw data
            confirmingParty: confirmingPartyInfo.recipient_name || "",
            recipientEmail: confirmingPartyInfo.email_address || "",
            organization: confirmingPartyInfo.organization || "",
            testStatus: domainInfo.status || "",
          };
        });

        setDomainTestingData(mappedData);
      } else {
        setDomainTestingData([]);
      }
    } catch (error) {
      console.error("Error loading domain testing data:", error);
      setDomainTestingData([]);
    } finally {
      setDomainTestingLoading(false);
    }
  };

  // --- Control Library signoff helpers (mirroring Document Library rules) ---

  type ControlSignoffSection = "prep_so" | "rev_so" | "conc_so" | "eqr_so" | "partner_so";

  const canControlSignoffSection = (section: ControlSignoffSection): boolean => {
    if (!user) return false;
    const engagementRole = userEngagementRole;
    const userType = user.type;

    switch (section) {
      case "prep_so":
        return (
          engagementRole === "associate" ||
          engagementRole === "article" ||
          userType === "associate" ||
          userType === "article"
        );
      case "rev_so":
        return (
          engagementRole === "engagement_manager" ||
          userType === "manager" ||
          userType === "associate" ||
          engagementRole === "associate"
        );
      case "conc_so":
        return userType === "manager" || userType === "partner";
      case "eqr_so":
        return engagementRole === "eqr_partner" || engagementRole === "eqr_manager";
      case "partner_so":
        return engagementRole === "engagement_partner";
      default:
        return false;
    }
  };

  const isControlPrepCompleted = (signoff: any): boolean => {
    if (!signoff || !Array.isArray(signoff.prep_so)) return false;
    return signoff.prep_so.some((entry: any) => entry?.name && String(entry.name).trim() !== "");
  };

  const handleControlSignoff = async (controlId: string, section: ControlSignoffSection) => {
    if (!user || !user.user_name) return;
    try {
      setControlSignoffLoading(true);
      if (
        !(window as any).sharePointAPI?.downloadRcmJson ||
        !(window as any).sharePointAPI?.uploadRcmJson
      ) {
        alert("SharePoint RCM APIs are not available.");
        return;
      }

      const rcmResult = await (window as any).sharePointAPI.downloadRcmJson();
      if (!rcmResult?.success || !rcmResult.data) {
        alert(rcmResult?.error || "Failed to load Libraries_RCM.json");
        return;
      }

      const rcmData = rcmResult.data;
      if (!rcmData.libraries_RCM) {
        rcmData.libraries_RCM = {};
      }
      if (!rcmData.libraries_RCM[controlId]) {
        alert("Control not found in Libraries_RCM.json");
        return;
      }

      const entry = rcmData.libraries_RCM[controlId];
      if (!entry.signoff) {
        entry.signoff = {
          prep_so: [],
          rev_so: [],
          conc_so: [],
          eqr_so: [],
          partner_so: [],
        };
      }

      const signoffArr: any[] = Array.isArray(entry.signoff[section])
        ? entry.signoff[section]
        : [];

      signoffArr.push({
        name: user.user_name,
        metadata: new Date().toISOString(),
      });

      entry.signoff[section] = signoffArr;

      const uploadResult = await (window as any).sharePointAPI.uploadRcmJson(rcmData);
      if (!uploadResult?.success) {
        alert(uploadResult?.error || "Failed to save control signoff");
        return;
      }

      // Reload controls to reflect updated signoff
      await loadControls();
    } catch (e: any) {
      console.error("Error updating control signoff:", e);
      alert(e?.message || "Unexpected error while updating control signoff");
    } finally {
      setControlSignoffLoading(false);
    }
  };

  const isDeficiencyPrepCompleted = (signoff: any): boolean => {
    if (!signoff || !Array.isArray(signoff.prep_so)) return false;
    return signoff.prep_so.some((entry: any) => entry?.name && String(entry.name).trim() !== "");
  };

  const handleDeficiencySignoff = async (deficiencyId: string, section: ControlSignoffSection) => {
    if (!user || !user.user_name) return;
    try {
      setDeficiencySignoffLoading(true);
      if (
        !(window as any).sharePointAPI?.downloadDeficiencyJson ||
        !(window as any).sharePointAPI?.uploadDeficiencyJson
      ) {
        alert("SharePoint Deficiency APIs are not available.");
        return;
      }

      const defResult = await (window as any).sharePointAPI.downloadDeficiencyJson();
      if (!defResult?.success || !defResult.data) {
        alert(defResult?.error || "Failed to load Libraries_Deficiency.json");
        return;
      }

      const defData = defResult.data;
      if (!defData.libraries_deficiency) {
        defData.libraries_deficiency = {};
      }
      if (!defData.libraries_deficiency[deficiencyId]) {
        alert("Deficiency not found in Libraries_Deficiency.json");
        return;
      }

      const entry = defData.libraries_deficiency[deficiencyId];
      if (!entry.signoff) {
        entry.signoff = {
          prep_so: [],
          rev_so: [],
          conc_so: [],
          eqr_so: [],
          partner_so: [],
        };
      }

      const signoffArr: any[] = Array.isArray(entry.signoff[section])
        ? entry.signoff[section]
        : [];

      signoffArr.push({
        name: user.user_name,
        metadata: new Date().toISOString(),
      });

      entry.signoff[section] = signoffArr;

      const uploadResult = await (window as any).sharePointAPI.uploadDeficiencyJson(defData);
      if (!uploadResult?.success) {
        alert(uploadResult?.error || "Failed to save deficiency signoff");
        return;
      }

      await loadDeficiencies();
    } catch (e: any) {
      console.error("Error updating deficiency signoff:", e);
      alert(e?.message || "Unexpected error while updating deficiency signoff");
    } finally {
      setDeficiencySignoffLoading(false);
    }
  };

  const isInformationPrepCompleted = (signoff: any): boolean => {
    if (!signoff || !Array.isArray(signoff.prep_so)) return false;
    return signoff.prep_so.some((entry: any) => entry?.name && String(entry.name).trim() !== "");
  };

  const handleInformationSignoff = async (informationName: string, section: ControlSignoffSection) => {
    if (!user || !user.user_name) return;
    try {
      setInformationSignoffLoading(true);
      if (
        !(window as any).sharePointAPI?.downloadLibrariesInformation ||
        !(window as any).sharePointAPI?.uploadLibrariesInformation
      ) {
        alert("SharePoint Information APIs are not available.");
        return;
      }

      const infoResult = await (window as any).sharePointAPI.downloadLibrariesInformation();
      if (!infoResult?.success || !infoResult.data) {
        alert(infoResult?.error || "Failed to load Libraries_Information.json");
        return;
      }

      const infoData = infoResult.data || { information: [] };
      if (!Array.isArray(infoData.information)) {
        infoData.information = [];
      }

      let entry = infoData.information.find(
        (item: any) => item.information_name === informationName,
      );
      if (!entry) {
        entry = infoData.information.find(
          (item: any) =>
            String(item.information_name || "").toLowerCase() ===
            String(informationName || "").toLowerCase(),
        );
      }
      if (!entry) {
        alert("Information entry not found in Libraries_Information.json");
        return;
      }

      if (!entry.signoff) {
        entry.signoff = {
          prep_so: [],
          rev_so: [],
          conc_so: [],
          eqr_so: [],
          partner_so: [],
        };
      }

      const signoffArr: any[] = Array.isArray(entry.signoff[section])
        ? entry.signoff[section]
        : [];

      signoffArr.push({
        name: user.user_name,
        metadata: new Date().toISOString(),
      });

      entry.signoff[section] = signoffArr;

      const uploadResult = await (window as any).sharePointAPI.uploadLibrariesInformation(infoData);
      if (!uploadResult?.success) {
        alert(uploadResult?.error || "Failed to save information signoff");
        return;
      }

      await loadInformationEntries();
    } catch (e: any) {
      console.error("Error updating information signoff:", e);
      alert(e?.message || "Unexpected error while updating information signoff");
    } finally {
      setInformationSignoffLoading(false);
    }
  };

  const isRiskPrepCompleted = (signoff: any): boolean => {
    if (!signoff || !Array.isArray(signoff.prep_so)) return false;
    return signoff.prep_so.some((entry: any) => entry?.name && String(entry.name).trim() !== "");
  };

  const handleRiskSignoff = async (rommId: string, section: ControlSignoffSection) => {
    if (!user || !user.user_name) return;
    try {
      setRiskSignoffLoading(true);
      if (
        !(window as any).sharePointAPI?.downloadRommJson ||
        !(window as any).sharePointAPI?.uploadRommJson
      ) {
        alert("SharePoint ROMM APIs are not available.");
        return;
      }

      const rommResult = await (window as any).sharePointAPI.downloadRommJson();
      if (!rommResult?.success || !rommResult.data) {
        alert(rommResult?.error || "Failed to load Libraries_Romm.json");
        return;
      }

      const rommData = rommResult.data || { romm_library: [] };
      const library = Array.isArray(rommData.romm_library) ? rommData.romm_library : [];
      const entry = library.find((r: any) => (r["romm-id"] || r.rommId) === rommId);
      if (!entry) {
        alert("Risk (ROMM) not found in Libraries_Romm.json");
        return;
      }

      if (!entry.signoff) {
        entry.signoff = {
          prep_so: [],
          rev_so: [],
          conc_so: [],
          eqr_so: [],
          partner_so: [],
        };
      }

      const signoffArr: any[] = Array.isArray(entry.signoff[section]) ? entry.signoff[section] : [];
      signoffArr.push({ name: user.user_name, metadata: new Date().toISOString() });
      entry.signoff[section] = signoffArr;

      const uploadResult = await (window as any).sharePointAPI.uploadRommJson(rommData);
      if (!uploadResult?.success) {
        alert(uploadResult?.error || "Failed to save risk signoff");
        return;
      }

      await loadRisks();
    } catch (e: any) {
      console.error("Error updating risk signoff:", e);
      alert(e?.message || "Unexpected error while updating risk signoff");
    } finally {
      setRiskSignoffLoading(false);
    }
  };

  const isMisstatementPrepCompleted = (signoff: any): boolean => {
    if (!signoff || !Array.isArray(signoff.prep_so)) return false;
    return signoff.prep_so.some((entry: any) => entry?.name && String(entry.name).trim() !== "");
  };

  const handleMisstatementSignoff = async (misstatementId: string, section: ControlSignoffSection) => {
    if (!user || !user.user_name) return;
    try {
      setMisstatementSignoffLoading(true);
      if (
        !(window as any).sharePointAPI?.downloadMisstatementJson ||
        !(window as any).sharePointAPI?.uploadMisstatementJson
      ) {
        alert("SharePoint Misstatement APIs are not available.");
        return;
      }

      const msResult = await (window as any).sharePointAPI.downloadMisstatementJson();
      if (!msResult?.success || !msResult.data) {
        alert(msResult?.error || "Failed to load Libraries_Misstatement.json");
        return;
      }

      const msData = msResult.data;
      if (!msData.Libraries_Misstatement) {
        msData.Libraries_Misstatement = { misstatements: {}, responses: [] };
      }
      if (!msData.Libraries_Misstatement.misstatements) {
        msData.Libraries_Misstatement.misstatements = {};
      }
      if (!msData.Libraries_Misstatement.misstatements[misstatementId]) {
        alert("Misstatement not found in Libraries_Misstatement.json");
        return;
      }

      const entry = msData.Libraries_Misstatement.misstatements[misstatementId];
      if (!entry.signoff) {
        entry.signoff = {
          prep_so: [],
          rev_so: [],
          conc_so: [],
          eqr_so: [],
          partner_so: [],
        };
      }

      const signoffArr: any[] = Array.isArray(entry.signoff[section])
        ? entry.signoff[section]
        : [];

      signoffArr.push({
        name: user.user_name,
        metadata: new Date().toISOString(),
      });

      entry.signoff[section] = signoffArr;

      const uploadResult = await (window as any).sharePointAPI.uploadMisstatementJson(msData);
      if (!uploadResult?.success) {
        alert(uploadResult?.error || "Failed to save misstatement signoff");
        return;
      }

      await loadMisstatements();
    } catch (e: any) {
      console.error("Error updating misstatement signoff:", e);
      alert(e?.message || "Unexpected error while updating misstatement signoff");
    } finally {
      setMisstatementSignoffLoading(false);
    }
  };

  const handleBulkControlSignoff = async (section: ControlSignoffSection) => {
    if (!user?.user_name || selectedControlsForSignoff.size === 0) return;
    const ids = Array.from(selectedControlsForSignoff);
    try {
      setControlSignoffLoading(true);
      for (const controlId of ids) {
        await handleControlSignoff(controlId, section);
      }
      setSelectedControlsForSignoff(new Set());
    } finally {
      setControlSignoffLoading(false);
    }
  };

  const handleBulkDeficiencySignoff = async (section: ControlSignoffSection) => {
    if (!user?.user_name || selectedDeficienciesForSignoff.size === 0) return;
    const ids = Array.from(selectedDeficienciesForSignoff);
    try {
      setDeficiencySignoffLoading(true);
      for (const id of ids) {
        await handleDeficiencySignoff(id, section);
      }
      setSelectedDeficienciesForSignoff(new Set());
    } finally {
      setDeficiencySignoffLoading(false);
    }
  };

  const handleBulkMisstatementSignoff = async (section: ControlSignoffSection) => {
    if (!user?.user_name || selectedMisstatementsForSignoff.size === 0) return;
    const ids = Array.from(selectedMisstatementsForSignoff);
    try {
      setMisstatementSignoffLoading(true);
      for (const id of ids) {
        await handleMisstatementSignoff(id, section);
      }
      setSelectedMisstatementsForSignoff(new Set());
    } finally {
      setMisstatementSignoffLoading(false);
    }
  };

  const handleBulkRiskSignoff = async (section: ControlSignoffSection) => {
    if (!user?.user_name || selectedRisksForSignoff.size === 0) return;
    const ids = Array.from(selectedRisksForSignoff);
    try {
      setRiskSignoffLoading(true);
      for (const id of ids) {
        await handleRiskSignoff(id, section);
      }
      setSelectedRisksForSignoff(new Set());
    } finally {
      setRiskSignoffLoading(false);
    }
  };

  const handleBulkInformationSignoff = async (section: ControlSignoffSection) => {
    if (!user?.user_name || selectedInformationForSignoff.size === 0) return;
    const names = Array.from(selectedInformationForSignoff);
    try {
      setInformationSignoffLoading(true);
      for (const name of names) {
        await handleInformationSignoff(name, section);
      }
      setSelectedInformationForSignoff(new Set());
    } finally {
      setInformationSignoffLoading(false);
    }
  };

  // Load Sampling Log data (used by both Confirmation and Sampling libraries)
  const loadSamplingLogData = async (sourceFile: "Confirmation_SamplingLog.json" | "Sampling_SamplingLog.json") => {
    setSamplingLogSourceFile(sourceFile);
    setSamplingLogLoading(true);
    try {
      const result = await (window as any).sharePointAPI?.downloadClientJson?.(sourceFile);
      if (result?.success && result?.data && typeof result.data === "object") {
        setSamplingLogData(result.data);
      } else {
        setSamplingLogData({});
        setSelectedSamplingWorkspace("__all__");
        setSelectedSamplingSet("__all__");
      }
    } catch (error) {
      console.error("Error loading sampling log data:", error);
      setSamplingLogData({});
      setSelectedSamplingWorkspace("__all__");
      setSelectedSamplingSet("__all__");
    } finally {
      setSamplingLogLoading(false);
    }
  };

  // Load Confirmations data from confirmations.json (workspace -> sample set -> entries[])
  // Load Confirmations from Confirmation_Response.json (confirmationId -> { partydetails, templateDetails })
  const loadConfirmationsData = async () => {
    setConfirmationsLoading(true);
    try {
      const [responseResult, activityResult] = await Promise.all([
        (window as any).sharePointAPI?.downloadClientJson?.("Confirmation_Response.json"),
        (window as any).sharePointAPI?.downloadClientJson?.("Confirmation_ActivityLog.json"),
      ]);
      if (responseResult?.success && responseResult?.data && typeof responseResult.data === "object") {
        setConfirmationsData(responseResult.data);
      } else {
        setConfirmationsData({});
      }
      if (activityResult?.success && activityResult?.data && typeof activityResult.data === "object") {
        setConfirmationsActivityLog(activityResult.data);
      } else {
        setConfirmationsActivityLog({});
      }
    } catch (error) {
      console.error("Error loading confirmations data:", error);
      setConfirmationsData({});
      setConfirmationsActivityLog({});
    } finally {
      setConfirmationsLoading(false);
    }
  };

  // Load client folder files (for linking existing Excel in Exception/Non-Response)
  const loadClientFolderFiles = async () => {
    setLoadingClientFiles(true);
    try {
      const result = await (window as any).sharePointAPI?.loadCloudFiles?.();
      if (result?.success && Array.isArray(result?.data?.files)) {
        const excelFiles = (result.data.files as { name: string; url?: string; reference?: string }[])
          .filter((f: any) => /\.(xlsx|xls)$/i.test(f.name || ""));
        setClientFolderFiles(excelFiles);
      } else {
        setClientFolderFiles([]);
      }
    } catch (error) {
      console.error("Error loading client folder files:", error);
      setClientFolderFiles([]);
    } finally {
      setLoadingClientFiles(false);
    }
  };

  // Open alternate testing file (Excel) from client folder – resolve URL and open in browser/external
  const openAlternateTestingFile = async (fileName: string) => {
    let url = clientFolderFiles.find((f) => f.name === fileName)?.url;
    if (!url) {
      const result = await (window as any).sharePointAPI?.loadCloudFiles?.();
      if (result?.success && Array.isArray(result?.data?.files)) {
        const file = (result.data.files as { name: string; url?: string }[]).find((f: any) => f.name === fileName);
        url = file?.url;
      }
    }
    if (url) {
      if ((window as any).electronWindow?.openExternal) {
        try {
          await (window as any).electronWindow.openExternal(url);
        } catch {
          window.open(url, "_blank", "noopener,noreferrer");
        }
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } else {
      alert(`Could not find file "${fileName}" in client folder. Try refreshing the page.`);
    }
  };

  // Get section string for confirmation alternate file: "<Phase>_<workspace prefix>_Confirmation" from Sections.json (juggernaut)
  const getConfirmationSectionForWorkspace = async (workspace: string): Promise<string | null> => {
    if (!workspace?.trim()) return null;
    try {
      const res = await (window as any).sharePointAPI?.downloadSectionsJson?.();
      if (!res?.success || !res?.data) return null;
      const sections = res.data as { Planning?: Record<string, string>; Execution?: Record<string, string>; ConcludingProcedures?: Record<string, string> };
      const ws = String(workspace).trim();
      if (sections.Planning && sections.Planning[ws]) {
        return `Planning_${sections.Planning[ws]}_Confirmation`;
      }
      if (sections.Execution && sections.Execution[ws]) {
        return `Execution_${sections.Execution[ws]}_Confirmation`;
      }
      if (sections.ConcludingProcedures && sections.ConcludingProcedures[ws]) {
        return `ConcludingProcedures_${sections.ConcludingProcedures[ws]}_Confirmation`;
      }
      return null;
    } catch (e) {
      console.error("Failed to get confirmation section from Sections.json", e);
      return null;
    }
  };

  // Save alternateTesting for a confirmation ID to Confirmation_Response.json and update db.json section when workspace is provided
  const saveAlternateTesting = async (
    confirmationId: string,
    alternateTesting: { fileName: string; type: "upload" | "link" },
    workspace?: string
  ) => {
    setSavingAlternateForId(confirmationId);
    try {
      const updated = JSON.parse(JSON.stringify(confirmationsData));
      if (!updated[confirmationId]) updated[confirmationId] = {};
      updated[confirmationId].alternateTesting = alternateTesting;
      const result = await (window as any).sharePointAPI?.uploadClientJson?.("Confirmation_Response.json", updated);
      if (result?.success) {
        setConfirmationsData(updated);
        // Update db.json section for the linked/uploaded file: <Phase>_<workspace prefix>_Confirmation
        if (workspace?.trim() && (window as any).sharePointAPI?.updateFileSections) {
          const sectionToAdd = await getConfirmationSectionForWorkspace(workspace);
          if (sectionToAdd) {
            await (window as any).sharePointAPI.updateFileSections({
              fileNames: [alternateTesting.fileName],
              sectionToAdd,
              folderName: "client",
            });
          }
        }
      } else {
        alert(`Failed to save: ${result?.error || "Unknown error"}`);
      }
    } catch (e: any) {
      console.error("Failed to save alternateTesting", e);
      alert(`Error saving: ${e?.message || "Unknown error"}`);
    } finally {
      setSavingAlternateForId(null);
    }
  };

  // De-link alternate testing file for a confirmation ID (remove from Confirmation_Response.json and remove section from db.json)
  const clearAlternateTesting = async (confirmationId: string, fileName?: string, workspace?: string) => {
    setSavingAlternateForId(confirmationId);
    try {
      const updated = JSON.parse(JSON.stringify(confirmationsData));
      if (updated[confirmationId]) {
        delete updated[confirmationId].alternateTesting;
      }
      const result = await (window as any).sharePointAPI?.uploadClientJson?.("Confirmation_Response.json", updated);
      if (result?.success) {
        setConfirmationsData(updated);
        // Remove confirmation section from db.json for this file
        if (fileName && workspace?.trim() && (window as any).sharePointAPI?.removeFileSection) {
          const sectionToRemove = await getConfirmationSectionForWorkspace(workspace);
          if (sectionToRemove) {
            await (window as any).sharePointAPI.removeFileSection({
              fileName,
              sectionToRemove,
            });
          }
        }
      } else {
        alert(`Failed to de-link: ${result?.error || "Unknown error"}`);
      }
    } catch (e: any) {
      console.error("Failed to clear alternateTesting", e);
      alert(`Error: ${e?.message || "Unknown error"}`);
    } finally {
      setSavingAlternateForId(null);
    }
  };

  // Check if confirmation was received: scan the entire activity log for any entry with this letter_id and action "Confirmation received"
  const isConfirmationReceived = (confirmationId: string): boolean => {
    const timeline = confirmationsActivityLog?.timeline;
    if (!Array.isArray(timeline)) return false;
    const idTrimmed = String(confirmationId || "").trim();
    for (let i = 0; i < timeline.length; i++) {
      const entry = timeline[i];
      const entryLetterId = String(entry?.letter_id ?? "").trim();
      const entryAction = String(entry?.action ?? "").trim().toLowerCase();
      if (entryLetterId === idTrimmed && entryAction === "confirmation received") {
        return true;
      }
    }
    return false;
  };

  const flattenedConfirmations = useMemo(() => {
    const rows: { confirmationId: string; workspace: string; sampleSet: string; partydetails: any }[] = [];
    Object.entries(confirmationsData).forEach(([confirmationId, data]) => {
      const partydetails = data?.partydetails ?? {};
      rows.push({
        confirmationId,
        workspace: partydetails.area ?? "",
        sampleSet: partydetails.sample_set ?? "",
        partydetails,
      });
    });
    return rows;
  }, [confirmationsData]);

  const filteredConfirmations = useMemo(() => {
    return flattenedConfirmations.filter((row) => {
      if (selectedConfirmationsWorkspace !== "__all__" && row.workspace !== selectedConfirmationsWorkspace) return false;
      if (selectedConfirmationsSet !== "__all__" && row.sampleSet !== selectedConfirmationsSet) return false;
      return true;
    });
  }, [flattenedConfirmations, selectedConfirmationsWorkspace, selectedConfirmationsSet]);

  const confirmationSummary = useMemo(() => {
    const workspaceMap = new Map<string, { total: number; received: number; notReceived: number }>();
    const sampleSetSet = new Set<string>();
    let received = 0;
    let alternateTesting = 0;

    filteredConfirmations.forEach((row) => {
      const rowData = confirmationsData[row.confirmationId];
      const isReceived = isConfirmationReceived(row.confirmationId);
      if (isReceived) received += 1;
      if (rowData?.alternateTesting?.fileName) alternateTesting += 1;
      if (row.sampleSet) sampleSetSet.add(String(row.sampleSet));

      const key = String(row.workspace || "Unspecified");
      const current = workspaceMap.get(key) || { total: 0, received: 0, notReceived: 0 };
      current.total += 1;
      if (isReceived) current.received += 1;
      else current.notReceived += 1;
      workspaceMap.set(key, current);
    });

    const workspaceBreakdown = Array.from(workspaceMap.entries())
      .map(([workspace, value]) => ({ workspace, ...value }))
      .sort((a, b) => b.total - a.total || a.workspace.localeCompare(b.workspace));

    const total = filteredConfirmations.length;
    const notReceived = Math.max(total - received, 0);

    return {
      total,
      received,
      notReceived,
      alternateTesting,
      uniqueWorkspaces: workspaceMap.size,
      uniqueSampleSets: sampleSetSet.size,
      workspaceBreakdown,
      receivedPieData: [
        { name: "Received", value: received, color: "#34d399" },
        { name: "Not Received", value: notReceived, color: "#f87171" },
      ].filter((d) => d.value > 0),
    };
  }, [filteredConfirmations, confirmationsData, confirmationsActivityLog]);

  const domainSummary = useMemo(() => {
    let active = 0;
    let notFound = 0;
    let generalDomain = 0;
    let others = 0;

    domainTestingRawData.forEach((entry: any) => {
      const status = String(entry?.domain_info?.status || "").trim().toLowerCase();
      if (status === "active") active += 1;
      else if (status === "not found" || status === "notfound") notFound += 1;
      else if (status === "general domain" || status === "generaldomain") generalDomain += 1;
      else if (status) others += 1;
    });

    return {
      total: domainTestingRawData.length,
      active,
      notFound,
      generalDomain,
      others,
      statusPieData: [
        { name: "Active", value: active, color: "#34d399" },
        { name: "Not Found", value: notFound, color: "#f87171" },
        { name: "General Domain", value: generalDomain, color: "#fbbf24" },
        { name: "Others", value: others, color: "#a78bfa" },
      ].filter((d) => d.value > 0),
    };
  }, [domainTestingRawData]);

  // Load Trial Balance Mapping for Population as per Financials
  const loadTrialBalanceMapping = async () => {
    setTrialBalanceMappingLoading(true);
    try {
      const result = await (window as any).sharePointAPI?.downloadClientJson?.("FinData_TrialBalance_Mapping.json");
      if (result?.success && result?.data && typeof result.data === "object") {
        setTrialBalanceMappingData(result.data);
      } else {
        setTrialBalanceMappingData({});
      }
    } catch (error) {
      console.error("Error loading trial balance mapping:", error);
      setTrialBalanceMappingData({});
    } finally {
      setTrialBalanceMappingLoading(false);
    }
  };

  // Parse date key "DD/MM/YY" to Date for comparison
  const parseAmountDateKey = (key: string): Date | null => {
    const parts = key.trim().split("/");
    if (parts.length !== 3) return null;
    const dd = parseInt(parts[0], 10);
    const mm = parseInt(parts[1], 10) - 1;
    const yy = parseInt(parts[2], 10);
    if (isNaN(dd) || isNaN(mm) || isNaN(yy)) return null;
    const year = yy < 100 ? 2000 + yy : yy;
    const d = new Date(year, mm, dd);
    return isNaN(d.getTime()) ? null : d;
  };

  // Get latest date key from amount object (keys like "31/03/25")
  const getLatestAmountFromLedger = (amountObj: Record<string, number> | undefined): number => {
    if (!amountObj || typeof amountObj !== "object") return 0;
    const keys = Object.keys(amountObj);
    if (keys.length === 0) return 0;
    let latestKey: string | null = null;
    let latestDate: Date | null = null;
    for (const k of keys) {
      const d = parseAmountDateKey(k);
      if (d && (!latestDate || d.getTime() > latestDate.getTime())) {
        latestDate = d;
        latestKey = k;
      }
    }
    if (latestKey == null) return 0;
    const val = amountObj[latestKey];
    return typeof val === "number" && !isNaN(val) ? val : 0;
  };

  // Compute Population as per Financials: sum of latest-date amounts for all ledgers matching selected note_line across all books
  const computePopulationAsPerFinancials = (): number => {
    if (selectedNoteLinesForFinancials.length === 0) return 0;
    let total = 0;
    for (const bookLedgers of Object.values(trialBalanceMappingData)) {
      if (!Array.isArray(bookLedgers)) continue;
      for (const ledger of bookLedgers) {
        const noteLine = ledger?.note_line;
        if (noteLine && selectedNoteLinesForFinancials.includes(noteLine)) {
          total += getLatestAmountFromLedger(ledger?.amount);
        }
      }
    }
    return total;
  };

  // Unique note_line values from trial balance mapping (for dropdown)
  const uniqueNoteLines = useMemo(() => {
    const lines = new Set<string>();
    for (const bookLedgers of Object.values(trialBalanceMappingData)) {
      if (!Array.isArray(bookLedgers)) continue;
      for (const ledger of bookLedgers) {
        if (ledger?.note_line) lines.add(ledger.note_line);
      }
    }
    return Array.from(lines).sort();
  }, [trialBalanceMappingData]);

  // Helper function to get user initials
  const getInitials = (name: string): string => {
    if (!name) return "";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Helper function to format date/time
  const formatDateTime = (dateString: string): string => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  // Helper function to format date only (DD-MM-YYYY)
  const formatDateOnly = (dateString: string): string => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return dateString;
    }
  };

  // Helper function to format Indian number
  const formatIndianNumber = (num: number | string): string => {
    if (num === null || num === undefined || num === "") return "0";
    const numValue = typeof num === "string" ? parseFloat(num.replace(/,/g, "")) : num;
    if (isNaN(numValue)) return "0";
    return numValue.toLocaleString("en-IN");
  };

  // Handle control click from Control Library - open control file
  const handleControlLibraryClick = async (control: any) => {
    try {
      const controlId = control.controlId;
      const controlName = control.controlName || "Untitled Control";
      
      // Set viewing control state to show InternalControlView
      setViewingControl({
        controlId,
        controlName,
        activeSection: "control-summary",
      });

      // Update activeSection to indicate we're viewing a control
      // This will be used by LeftPanel to show Internal Control navigation
      if (setActiveSection) {
        setActiveSection(`internal-control:${controlId}:control-summary`);
      } else if ((window as any).setActiveSection) {
        (window as any).setActiveSection(`internal-control:${controlId}:control-summary`);
      }

      console.log("Internal Control accessed:", controlId);
    } catch (error) {
      console.error("Failed to open control", error);
      alert("Failed to open control. Please try again.");
    }
  };

  // handleControlLibrarySave removed - using new InternalControlView instead

  const loadLibraryFiles = async (libraryId: string) => {
    setFilesLoading(true);
    try {
      const files = await fileManager.getFilesForLibrary(libraryId);
      setLibraryFiles(files);
    } catch (error) {
      console.error("Error loading library files:", error);
      setLibraryFiles([]);
    } finally {
      setFilesLoading(false);
    }
  };

  const loadInternalControls = async () => {
    try {
      const controls: any[] = [];
      
      // Load from cloud storage
      console.log('Loading internal controls from cloud storage...');
      console.log('window.cloud:', window.cloud);
      console.log('typeof window.cloud.list:', typeof window.cloud?.list);
      
      if (window.cloud && typeof window.cloud.list === 'function') {
        try {
          const result = await window.cloud.list({ container: 'juggernaut' });
          console.log('Cloud list result:', result);
          console.log('Cloud list success:', result.success);
          console.log('Cloud list files:', result.files);
          
          if (result.success && result.files) {
            console.log('Total files in juggernaut container:', result.files.length);
            
            // Filter for internal control files with the specific naming pattern
            const controlFiles = result.files.filter((file: any) => 
              file.name.startsWith('Libraries_InternalControlResponses_') && 
              file.name.endsWith('.json')
            );
            
            console.log('Found internal control files:', controlFiles.length);
            console.log('Control files:', controlFiles.map(f => f.name));
            
            // Load each control file
            for (const file of controlFiles) {
              try {
                const downloadResult = await window.cloud.download({
                  container: 'juggernaut',
                  filename: file.name,
                  downloadPath: `temp_${file.name}`
                });
                
                if (downloadResult.success) {
                  const tempFilePath = downloadResult.filePath!;
                  
                  // Read the downloaded file content using IPC
                  const readResult = await window.cloud.readTempFile(tempFilePath);
                  
                  if (readResult.success) {
                    const controlData = JSON.parse(readResult.content!);
                    
                    // Clean up temp file
                    await window.cloud.deleteTempFile(tempFilePath);
                    
                    // Add to controls list - parse the new JSON structure
                    console.log('Parsing control data:', controlData);
                    
                    // Extract controlId from the file name (this is the user-entered ControlID)
                    console.log('Processing file:', file.name);
                    const controlId = file.name.replace('Libraries_InternalControlResponses_', '').replace('.json', '');
                    console.log('Extracted controlId:', controlId);
                    
                    // Extract other details from the JSON structure
                    const controlName = controlData.InternalControls?.Control?.ControlSummary?.ControlDetails?.ControlName || 
                                      'Unknown Control';
                    
                    const typeOfControl = controlData.InternalControls?.Control?.ControlSummary?.ControlAttributes?.TypeOfControl || 
                                        'Unknown';
                    
                    const subtype = controlData.InternalControls?.Control?.ControlSummary?.ControlAttributes?.Nature || 
                                  'Unknown';
                    
                    const workspace = controlData.InternalControls?.Control?.ControlSummary?.ControlDetails?.WorkspaceLinked?.[0] || 
                                    'Unknown';
                    
                    controls.push({
                      id: controlId,
                      controlMetadata: {
                        typeOfControl,
                        subtype,
                        controlId,
                        controlName,
                        workspace
                      },
                      ...controlData,
                      createdAt: controlId, // Use controlId as timestamp for now
                      template: subtype.toLowerCase(),
                      originalFileName: file.name // Store original filename for edit mode
                    });
                  } else {
                    console.error(`Error reading control file ${file.name}:`, readResult.error);
                  }
                }
              } catch (fileError) {
                console.error(`Error loading control file ${file.name}:`, fileError);
              }
            }
          }
        } catch (cloudError) {
          console.error('Error loading from cloud storage:', cloudError);
        }
      }
      
      console.log('=== LOAD INTERNAL CONTROLS COMPLETE ===');
      console.log('Total controls loaded:', controls.length);
      console.log('Controls data:', controls);
      console.log('Controls array:', JSON.stringify(controls, null, 2));
      
      // Fallback to localStorage if cloud storage fails or no controls found
      if (controls.length === 0) {
        console.log('No controls found, trying localStorage fallback...');
        const savedControls = localStorage.getItem('internalControls');
        if (savedControls) {
          const parsedControls = JSON.parse(savedControls);
          controls.push(...parsedControls);
        } else {
          // Load from the existing payroll internal control library as examples
          const response = await fetch('/payroll/InternalControlLibrary.json');
          if (response.ok) {
            const existingControls = await response.json();
            const formattedControls = existingControls.map((control: any, index: number) => ({
              id: `ic-${index}`,
              controlMetadata: {
                typeOfControl: 'Direct',
                subtype: control.control_type === 'Detective' ? 'Manual' : 'Manual',
                controlId: control.control_id,
                controlName: control.control_name,
                workspace: 'Payroll'
              },
              createdAt: new Date().toISOString(),
              template: 'manual'
            }));
            controls.push(...formattedControls);
          }
        }
      }
      
      console.log('Setting internal controls state with:', controls.length, 'controls');
      setInternalControls(controls);
      console.log('Internal controls state set successfully');
    } catch (error) {
      console.error('Error loading internal controls:', error);
      setInternalControls([]);
    }
  };

  // Load Information Library entries
  const loadInformationEntries = async () => {
    setInformationLoading(true);
    try {
      if ((window as any).sharePointAPI?.downloadLibrariesInformation) {
        const result = await (window as any).sharePointAPI.downloadLibrariesInformation();
        if (result?.success && result?.data) {
          const raw = Array.isArray(result.data.information) ? result.data.information : [];
          const signoffDefault = {
            prep_so: [] as Array<{ name: string; metadata: string }>,
            rev_so: [] as Array<{ name: string; metadata: string }>,
            conc_so: [] as Array<{ name: string; metadata: string }>,
            eqr_so: [] as Array<{ name: string; metadata: string }>,
            partner_so: [] as Array<{ name: string; metadata: string }>,
          };
          const entries = raw.map((item: any) => {
            const signoff = item.signoff || {};
            return {
              information_name: item.information_name || "",
              workspace: Array.isArray(item.workspace) ? item.workspace : [],
              workspace_prefix: Array.isArray(item.workspace_prefix) ? item.workspace_prefix : [],
              file_name: item.file_name,
              signoff: {
                prep_so: Array.isArray(signoff.prep_so) ? signoff.prep_so : signoffDefault.prep_so,
                rev_so: Array.isArray(signoff.rev_so) ? signoff.rev_so : signoffDefault.rev_so,
                conc_so: Array.isArray(signoff.conc_so) ? signoff.conc_so : signoffDefault.conc_so,
                eqr_so: Array.isArray(signoff.eqr_so) ? signoff.eqr_so : signoffDefault.eqr_so,
                partner_so: Array.isArray(signoff.partner_so) ? signoff.partner_so : signoffDefault.partner_so,
              },
            };
          });
          setInformationEntries(entries);
        } else {
          setInformationEntries([]);
        }
      } else {
        setInformationEntries([]);
      }
    } catch (error) {
      console.error("Failed to load information entries:", error);
      setInformationEntries([]);
    } finally {
      setInformationLoading(false);
    }
  };

  const handleLibrarySelect = (libraryId: string) => {
    if (isLibraryHiddenForLimitedReview(libraryId, isLimitedReviewExecution)) {
      return;
    }
    setSelectedLibrary(libraryId);
    // Sync left panel so the selected library is highlighted there too
    if (setActiveSection) {
      setActiveSection(libraryId);
    } else if ((window as any).setActiveSection) {
      (window as any).setActiveSection(libraryId);
    }
    console.log("Library accessed:", libraryId);
    
    // Load information entries when Information Library is selected
    if (libraryId === "information-library") {
      loadInformationEntries();
    }
  };

  const handleRommSelect = (rommId: string) => {
    // Navigate to the specific ROMM in execution
    console.log("ROMM accessed:", rommId);
    // Navigate to execution-payroll and then to the specific ROMM
    if ((window as any).setActiveSection) {
      (window as any).setActiveSection("execution-payroll");
      // After a short delay, navigate to the specific ROMM
      setTimeout(() => {
        if ((window as any).setActiveSection) {
          (window as any).setActiveSection("payroll-romms");
        }
      }, 100);
    }
  };

  const handleControlClick = (control: InternalControl) => {
    try {
      // Set viewing control state to show InternalControlView
      const controlId = control.controlMetadata?.controlId || control.id;
      const controlName = control.controlMetadata?.controlName || control.controlName || "Untitled Control";
      
      // Store where we came from so we can return here when closing the control
      sessionStorage.setItem('control-view-referrer', 'control-library');
      
      setViewingControl({
        controlId,
        controlName,
        activeSection: "control-summary",
      });

      // Update activeSection to indicate we're viewing a control
      // This will be used by LeftPanel to show Internal Control navigation
      if (activeSection && setActiveSection) {
        setActiveSection(`internal-control:${controlId}:control-summary`);
      } else if ((window as any).setActiveSection) {
        (window as any).setActiveSection(`internal-control:${controlId}:control-summary`);
      }
    } catch (error) {
      console.error("Error preparing control template for edit:", error);
    }
  };

  const handleCreateControl = (type: "manual" | "automated") => {
    // Clear any cached data to ensure fresh template
    console.log('=== Clearing cache and creating new control ===');
    
    const metadata: ControlMetadata = {
      typeOfControl: "Direct",
      subtype: type === "manual" ? "Manual" : "Automated",
      controlId: `IC${Date.now()}`,
      controlName: `New ${type} Control`,
      workspace: "General"
    };

    // Get the appropriate template - force fresh import
    const template = type === "manual" 
      ? CONTROL_TEMPLATES.manual
      : CONTROL_TEMPLATES.automated;

    console.log('=== Creating new control with template ===');
    console.log('Type:', type);
    console.log('Template structure:', template);
    console.log('Control Summary section:', template.sections?.[0]);
    console.log('Control Summary panes:', template.sections?.[0]?.panes);
    console.log('Pane count:', template.sections?.[0]?.panes?.length);
    
    // Debug the detailed description pane specifically
    const detailedDescPane = template.sections?.[0]?.panes?.find((p: any) => p.id === 'detailed_description');
    if (detailedDescPane) {
      console.log('Detailed Description pane found:', detailedDescPane);
      console.log('Steps field in detailed description:', detailedDescPane.fields?.find((f: any) => f.id === 'steps'));
    } else {
      console.log('Detailed Description pane NOT found');
    }

    setSelectedControlForm({ metadata, template });
  };

  const handleSaveControl = (controlData: any) => {
    const newControl = {
      id: controlData.controlMetadata?.controlId || controlData.id || 'unknown',
      ...controlData
    };
    
    setInternalControls(prev => {
      const updated = [...prev, newControl];
      // Also save to localStorage as backup
      localStorage.setItem('internalControls', JSON.stringify(updated));
      return updated;
    });
    
    // Refresh the library from cloud storage to show the new control
    console.log('Refreshing library from cloud storage after save...');
    setTimeout(() => {
      loadInternalControls();
    }, 1000); // Small delay to ensure cloud upload is complete
    
    setSelectedControlForm(null);
  };

  const handleControlSave = (updatedControl: any) => {
    // Update the control in localStorage
    try {
      const savedControls = JSON.parse(localStorage.getItem('internalControls') || '[]');
      const updatedControls = savedControls.map((c: any) => 
        c.id === updatedControl.id ? updatedControl : c
      );
      localStorage.setItem('internalControls', JSON.stringify(updatedControls));
      
      // Refresh the controls list
      setInternalControls(updatedControls);
      
      logger.dataSave("Internal Control Update", updatedControl.controlMetadata?.controlId);
    } catch (error) {
      console.error('Error saving updated control:', error);
    }
  };

  const handleDeleteAllControls = async () => {
    const confirmed = window.confirm(
      ' WARNING: This will permanently delete ALL existing controls!\n\n' +
      'This action cannot be undone.\n\n' +
      'Are you sure you want to continue?'
    );
    
    if (!confirmed) {
      console.log('Delete cancelled by user');
      return;
    }
    
    try {
      console.log('=== Deleting all existing controls ===');
      
      // Clear local state
      setInternalControls([]);
      
      // Clear localStorage
      localStorage.removeItem('internalControls');
      
      // Clear cloud storage if available
      if (window.cloud) {
        try {
          const result = await window.cloud.list({ container: 'juggernaut' });
          if (result.success && result.files) {
            // Delete all control files from cloud storage
            for (const file of result.files) {
              if (file.name.includes('internal_control_') || file.name.includes('control_')) {
                console.log('Deleting cloud file:', file.name);
                await window.cloud.delete({ 
                  container: 'juggernaut', 
                  fileName: file.name 
                });
              }
            }
          }
        } catch (error) {
          console.error('Error clearing cloud storage:', error);
        }
      }
      
      console.log('All controls deleted successfully');
      alert(' All controls have been deleted successfully!');
      
    } catch (error) {
      console.error('Error deleting controls:', error);
      alert(' Error deleting controls. Please try again.');
    }
  };

  // ConfirmationFormView component - renders the confirmation template and response
  const ConfirmationFormView = ({ confirmationData }: { confirmationData: any }) => {
    if (!confirmationData) return null;

    const templateDetails = confirmationData.templateDetails || {};
    const partydetails = confirmationData.partydetails || {};
    const periodEndDate = partydetails.periodenddate || "";

    // Helper function to replace placeholders in text
    const replacePlaceholders = (text: string): string => {
      if (!text || typeof text !== "string") return text;
      const recipientName = confirmationData.recipientName || "";
      const confirmingParty = partydetails.ConfirmingParty || "";
      const clientName = partydetails.clientName || "";
      const clientDisplayName = clientName && clientName.trim() ? clientName : "N/A";
      
      return text
        .replace(/\[Name of the Recipient\]/g, recipientName || "[Name of the Recipient]")
        .replace(/\[Recipientname\]/g, recipientName || "[Recipient Name]")
        .replace(/\[Period-end Date\]/g, periodEndDate || "[Period-end Date]")
        .replace(/\[Period end date\]/g, periodEndDate || "[Period end date]")
        .replace(/\[Period End Date\]/g, periodEndDate || "[Period End Date]")
        .replace(/\[Period-end date\]/g, periodEndDate || "[Period-end date]")
        .replace(/\[Name of the Client\]/g, clientDisplayName)
        .replace(/\[Name of Client Company\]/g, clientDisplayName)
        .replace(/\[Client Organization\]/g, clientDisplayName)
        .replace(/\[Client Organization name\]/g, clientDisplayName)
        .replace(/\[Client Organization Name\]/g, clientDisplayName)
        .replace(/\[Confirming Party\]/g, confirmingParty || "[Confirming Party]");
    };

    // Render dynamic template structure
    const renderDynamicTemplate = () => {
      if (!templateDetails || Object.keys(templateDetails).length === 0) {
        return <div className="text-center py-8 text-white/60">No form data submitted yet.</div>;
      }

      const renderedElements: JSX.Element[] = [];
      const entries = Object.entries(templateDetails);

      // Sort entries to maintain order
      const getSortOrder = (key: string): number => {
        const systemFields = ["remarks", "attachments", "confirmingpartystatement", "confirmingpartydetails", "actions"];
        if (systemFields.includes(key)) return 10000 + systemFields.indexOf(key);
        
        if (key.startsWith("textbox_")) {
          if (key === "textbox_1") return 1;
          if (key === "textbox_interest") return 200;
          const match = key.match(/textbox_(\d+)/);
          return match ? 100 + parseInt(match[1], 10) : 300;
        }
        
        if (key.startsWith("table_")) {
          if (key === "table_10a") return 11;
          if (key === "table_10b") return 12;
          if (key === "table_12") return 14;
          const numMatch = key.match(/table_(\d+)/);
          if (numMatch) {
            const num = parseInt(numMatch[1], 10);
            if (num >= 1 && num <= 9) return 1 + num;
          }
          return 400;
        }
        
        if (key.startsWith("question_")) {
          const match = key.match(/question_(\d+)/);
          return match ? 300 + parseInt(match[1], 10) : 350;
        }
        
        return 500;
      };

      const sortedEntries = entries.sort(([keyA], [keyB]) => getSortOrder(keyA) - getSortOrder(keyB));

      for (const [key, value] of sortedEntries) {
        // Skip system fields
        if (["remarks", "attachments", "confirmingpartystatement", "confirmingpartydetails", "actions"].includes(key)) {
          continue;
        }

        // Render textboxes
        if (key.startsWith("textbox_")) {
          let textboxValue = "";
          if (typeof value === "string") {
            textboxValue = value;
          } else if (typeof value === "object" && value !== null) {
            textboxValue = (value as any).value || "";
          }
          
          const displayValue = replacePlaceholders(textboxValue);
          const textboxFootnote = typeof value === "object" && value !== null 
            ? ((value as any).footnote || (value as any).footnote_1)
            : null;
          
          renderedElements.push(
            <div key={key} className="space-y-2">
              {typeof value === "object" && value !== null && (value as any).heading && (
                <h3 className="font-semibold text-white">{replacePlaceholders((value as any).heading)}</h3>
              )}
              {typeof value === "object" && value !== null && (value as any).description && (
                <p className="text-sm text-white/60">{replacePlaceholders((value as any).description)}</p>
              )}
              <div className="bg-white/5 p-3 rounded-md border border-white/10">
                <p className="text-sm text-white whitespace-pre-wrap">{displayValue || "(No response)"}</p>
              </div>
              {textboxFootnote && textboxFootnote.trim() !== "" && (
                <div className="mt-2 pt-2 border-t border-white/10 space-y-1">
                  <p className="text-xs text-white/60 whitespace-pre-line font-medium">Footnotes:</p>
                  <p className="text-xs text-white/60 whitespace-pre-line">{replacePlaceholders(textboxFootnote)}</p>
                </div>
              )}
            </div>
          );
        }

        // Render tables
        if (key.startsWith("table_")) {
          const table = value as any;
          
          if (table && table.columns) {
            const tableRows = table.rows || [];
            
            // Filter out empty rows
            const nonEmptyRows = tableRows.filter((row: any) => {
              if (!row || typeof row !== "object") return false;
              return table.columns.some((col: string) => {
                const val = row[col];
                if (val === null || val === undefined) return false;
                const strVal = String(val).trim();
                return strVal !== "" && strVal !== "-";
              });
            });
            
            renderedElements.push(
              <div key={key} className="space-y-2">
                {table.heading && <h3 className="font-semibold text-white">{replacePlaceholders(table.heading)}</h3>}
                {table.subheading && <p className="text-sm text-white/60">{replacePlaceholders(table.subheading)}</p>}
                {nonEmptyRows.length > 0 ? (
                  <>
                    <div className="rounded-md border border-white/10 overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-white/10">
                            {table.columns.map((col: string, idx: number) => (
                              <TableHead key={idx} className="text-white/80 text-xs break-words">{replacePlaceholders(col)}</TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {nonEmptyRows.map((row: any, rowIdx: number) => (
                            <TableRow key={rowIdx} className="border-white/10">
                              {table.columns.map((col: string, colIdx: number) => {
                                const cellValue = row[col];
                                
                                // Format numbers for Amount column
                                if (col === "Amount" && cellValue) {
                                  const numValue = typeof cellValue === "number" 
                                    ? cellValue 
                                    : parseFloat(String(cellValue).replace(/,/g, ""));
                                  if (!isNaN(numValue)) {
                                    return (
                                      <TableCell key={colIdx} className="text-white/90 font-medium text-xs break-words">
                                        {formatIndianNumber(numValue)}
                                      </TableCell>
                                    );
                                  }
                                }
                                
                                return (
                                  <TableCell key={colIdx} className="text-white/90 text-xs break-words">
                                    {cellValue || "-"}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {((table.footnote && String(table.footnote).trim() !== "") || (table.footnote_1 && String(table.footnote_1).trim() !== "")) && (
                      <div className="mt-2 pt-2 border-t border-white/10 space-y-1">
                        <p className="text-xs font-semibold text-white/60 mb-1">Footnotes:</p>
                        {table.footnote && String(table.footnote).trim() !== "" && (
                          <p className="text-xs text-white/60 whitespace-pre-line">{replacePlaceholders(String(table.footnote))}</p>
                        )}
                        {table.footnote_1 && String(table.footnote_1).trim() !== "" && (
                          <p className="text-xs text-white/60 whitespace-pre-line">{replacePlaceholders(String(table.footnote_1))}</p>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-white/60">No data provided</p>
                )}
              </div>
            );
          }
        }

        // Render questions
        if (key.startsWith("question_")) {
          const question = value as any;
          const questionResponse = question.response || "";
          
          renderedElements.push(
            <div key={key} className="space-y-2">
              {question.statement && (
                <label className="text-base font-medium text-white">{replacePlaceholders(question.statement)}</label>
              )}
              <div className="bg-white/5 p-3 rounded-md border border-white/10">
                <p className="font-medium text-white">{questionResponse || "(No response)"}</p>
              </div>
            </div>
          );
        }
      }

      return renderedElements;
    };

    const savedConfirmingPartyDetails = templateDetails.confirmingpartydetails || {};
    const savedConfirmingPartyStatement = templateDetails.confirmingpartystatement || {};

    return (
      <div className="space-y-6">
        <div className="space-y-2 border-b border-white/10 pb-4">
          {renderDynamicTemplate()}
        </div>

        {/* Confirming Party Statement */}
        {savedConfirmingPartyStatement && (
          <div className="space-y-2 pt-4 border-t border-white/10">
            <div className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-400 mt-0.5" />
              <div className="flex-1">
                {typeof savedConfirmingPartyStatement === "object" && savedConfirmingPartyStatement.statement ? (
                  <p className="text-sm text-white">{savedConfirmingPartyStatement.statement}</p>
                ) : (
                  <p className="text-sm text-white">
                    We certify that the above particulars (read alongwith the attachments if any) are full and correct.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Confirming Party Details */}
        {(savedConfirmingPartyDetails.name || savedConfirmingPartyDetails.designation || savedConfirmingPartyDetails.organizationName) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-white/10">
            {savedConfirmingPartyDetails.organizationName && (
              <div className="break-words">
                <p className="text-sm text-white/60">Organization Name</p>
                <p className="font-medium text-white break-words">{savedConfirmingPartyDetails.organizationName}</p>
              </div>
            )}
            {savedConfirmingPartyDetails.name && (
              <div className="break-words">
                <p className="text-sm text-white/60">Name</p>
                <p className="font-medium text-white break-words">{savedConfirmingPartyDetails.name}</p>
              </div>
            )}
            {savedConfirmingPartyDetails.designation && (
              <div className="break-words">
                <p className="text-sm text-white/60">Designation</p>
                <p className="font-medium text-white break-words">{savedConfirmingPartyDetails.designation}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderLibraryContent = () => {
    if (!selectedLibrary) return null;

    const library = libraryItems.find(l => l.id === selectedLibrary);
    if (!library) return null;

    switch (selectedLibrary) {
      case "document-library":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedLibrary(null)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Libraries
              </Button>
              <div>
                <h2 className="text-xl font-semibold text-white">Document Library</h2>
                <p className="text-sm text-white/60">
                  Upload and manage supporting documents
                </p>
              </div>
            </div>
            <DocumentsPage hideHeader />
          </div>
        );

      case "rcm-library":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedLibrary(null)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Libraries
              </Button>
              <div>
                <h2 className="text-xl font-semibold text-white">RCM Library</h2>
                <p className="text-sm text-white/60">
                  Risk Control Matrix - View controls and their associated ROMMs
                </p>
              </div>
            </div>

            {/* Advanced Filters */}
            <Card className="border-white/10 bg-white/5">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-white/60" />
                    <CardTitle className="text-base font-medium text-white">Filters</CardTitle>
                    {rcmFilters.length > 0 && (
                      <Badge variant="outline" className="text-xs">
                        {rcmFilters.length} active
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {rcmFilters.length > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={clearAllRcmFilters}
                        className="text-xs text-white/60 hover:text-white hover:bg-white/10"
                      >
                        Clear All
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={addRcmFilter}
                      className="text-white border-white/20 hover:bg-white/10"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Filter
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {rcmFilters.length === 0 ? (
                  <div className="text-center py-4 text-white/40 text-sm">
                    No filters applied. Click "Add Filter" to start filtering.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {rcmFilters.map((filter) => (
                      <div
                        key={filter.id}
                        className="flex items-center gap-2 p-3 rounded-md border border-white/10 bg-black/20"
                      >
                        <Select
                          value={filter.column}
                          onValueChange={(value) =>
                            updateRcmFilter(filter.id, { column: value })
                          }
                        >
                          <SelectTrigger className="w-[180px] h-9 text-sm">
                            <SelectValue placeholder="Select column" />
                          </SelectTrigger>
                          <SelectContent>
                            {rcmFilterColumns.map((col) => (
                              <SelectItem key={col.value} value={col.value}>
                                {col.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Select
                          value={filter.operator}
                          onValueChange={(value) =>
                            updateRcmFilter(filter.id, { operator: value })
                          }
                        >
                          <SelectTrigger className="w-[140px] h-9 text-sm">
                            <SelectValue placeholder="Operator" />
                          </SelectTrigger>
                          <SelectContent>
                            {filterOperators.map((op) => (
                              <SelectItem key={op.value} value={op.value}>
                                {op.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Input
                          type="text"
                          placeholder="Filter value..."
                          value={filter.value}
                          onChange={(e) =>
                            updateRcmFilter(filter.id, { value: e.target.value })
                          }
                          className="flex-1 h-9 bg-black/20 border-white/10 text-white placeholder:text-white/40"
                        />

                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeRcmFilter(filter.id)}
                          className="h-9 w-9 p-0 text-white/60 hover:text-white hover:bg-white/10"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">Risk Control Matrix</h3>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {rcmFilters.length > 0
                      ? `${filteredRcmData.length} of ${rcmTableData.length} ROMM(s)`
                      : `${rcmTableData.length} ROMM(s)`}
                  </Badge>
                  <input
                    ref={rcmFileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleRcmImport}
                    className="hidden"
                  />
                  <Button
                    size="sm"
                    onClick={() => rcmFileInputRef.current?.click()}
                    variant="outline"
                    disabled={rcmImportLoading}
                    className="text-white border-white/20 hover:bg-white/10"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {rcmImportLoading ? "Importing..." : "Import"}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleRcmExport}
                    variant="outline"
                    disabled={rcmExportLoading}
                    className="text-white border-white/20 hover:bg-white/10"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {rcmExportLoading ? "Exporting..." : "Export"}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => loadRcmData()}
                    variant="outline"
                    className="text-white border-white/20 hover:bg-white/10"
                  >
                    Refresh
                  </Button>
                </div>
              </div>

              {rcmLoading ? (
                <div className="text-center text-white/60 py-8">
                  Loading RCM data...
                </div>
              ) : filteredRcmData.length === 0 ? (
                <Card className="border-white/10 bg-white/5 text-white">
                  <CardContent className="p-6">
                    <div className="text-center text-white/60">
                      <Network className="mx-auto h-12 w-12 mb-4 text-white/40" />
                      <p>No ROMMs found in RCM Library.</p>
                      <p className="text-sm mt-2">
                        {rcmFilters.length > 0
                          ? "No ROMMs match the applied filters. Try adjusting your filters."
                          : "ROMMs will appear here once they are created and linked to controls."}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="rounded-md border border-white/10 bg-black/20 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-white/5">
                        <TableHead className="text-white/80 min-w-[120px]">RoMM ID</TableHead>
                        <TableHead className="text-white/80 min-w-[200px]">RoMM Description</TableHead>
                        <TableHead className="text-white/80 min-w-[150px]">Workspace</TableHead>
                        <TableHead className="text-white/80 min-w-[100px]">Assertion</TableHead>
                        <TableHead className="text-white/80 min-w-[120px]">Assessment</TableHead>
                        <TableHead className="text-white/80 min-w-[150px]">Control(s) Linked</TableHead>
                        <TableHead className="text-white/80 min-w-[200px]">Control Name(s)</TableHead>
                        <TableHead className="text-white/80 min-w-[120px]">Control Type</TableHead>
                        <TableHead className="text-white/80 min-w-[120px]">Control Nature</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRcmData.map((row, index) => (
                          <TableRow 
                            key={row.rommId || index} 
                            className="border-white/10 hover:bg-white/5"
                          >
                            <TableCell className="text-white font-medium">
                              {row.rommId}
                            </TableCell>
                            <TableCell className="text-white whitespace-normal break-words max-w-[200px]">
                              {row.rommDescription || "-"}
                            </TableCell>
                            <TableCell className="text-white">
                              {row.workspace.length > 0 ? (
                                <div className="flex flex-col gap-1">
                                  {row.workspace.map((ws: string, idx: number) => (
                                    <Badge key={idx} variant="outline" className="text-xs w-fit">
                                      {ws}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell className="text-white">
                              {row.assertion ? (
                                <div className="flex flex-wrap gap-1">
                                  {(() => {
                                    // Handle both string and array types
                                    const assertions = Array.isArray(row.assertion)
                                      ? row.assertion
                                      : typeof row.assertion === 'string'
                                      ? row.assertion.split(',').map((a: string) => a.trim())
                                      : [String(row.assertion)];
                                    
                                    return assertions.map((assertion: string, idx: number) => {
                                      const trimmed = String(assertion).trim();
                                      return trimmed ? (
                                        <Badge key={idx} variant="outline" className="text-xs">
                                          {getAssertionAbbreviation(trimmed)}
                                        </Badge>
                                      ) : null;
                                    });
                                  })()}
                                </div>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell className="text-white">
                              {row.assessment || "-"}
                            </TableCell>
                            <TableCell className="text-white">
                              {row.linkedControls.length > 0 ? (
                                <div className="flex flex-col gap-1">
                                  {row.linkedControls.map((control: any, idx: number) => (
                                    <span key={idx} className="text-xs">
                                      {control.controlId}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell className="text-white whitespace-normal break-words max-w-[200px]">
                              {row.linkedControls.length > 0 ? (
                                <div className="flex flex-col gap-1">
                                  {row.linkedControls.map((control: any, idx: number) => (
                                    <span key={idx} className="text-xs">
                                      {control.controlName}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell className="text-white">
                              {row.linkedControls.length > 0 ? (
                                <div className="flex flex-col gap-1">
                                  {row.linkedControls.map((control: any, idx: number) => (
                                    <Badge key={idx} variant="outline" className="text-xs w-fit">
                                      {control.typeOfControl}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell className="text-white">
                              {row.linkedControls.length > 0 ? (
                                <div className="flex flex-col gap-1">
                                  {row.linkedControls.map((control: any, idx: number) => (
                                    <Badge key={idx} variant="outline" className="text-xs w-fit">
                                      {control.nature}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        );

      case "risk-library":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedLibrary(null)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Libraries
              </Button>
              <div>
                <h2 className="text-xl font-semibold text-white">Risk Library</h2>
                <p className="text-sm text-white/60">
                  View all risks from Libraries_Romms.json
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">Risks</h3>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {risks.length} risk(s)
                  </Badge>
                  {!isArchivalMode && selectedRisksForSignoff.size > 0 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={riskSignoffLoading}
                          className="border-blue-500/50 text-blue-300 hover:bg-blue-500/10 hover:text-blue-200"
                        >
                          {riskSignoffLoading ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <FileCheck className="h-4 w-4 mr-2" />
                          )}
                          Sign off on selected ({selectedRisksForSignoff.size})
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-gray-900 border-white/10 text-white min-w-[200px]" align="end">
                        {(() => {
                          const selected = Array.from(selectedRisksForSignoff);
                          const allHavePrep = selected.length > 0 && selected.every((id) => {
                            const r = risks.find((x) => x.id === id);
                            return r && isRiskPrepCompleted(r.signoff || {});
                          });
                          const canPrep = canControlSignoffSection("prep_so");
                          const canRev = canControlSignoffSection("rev_so") && allHavePrep;
                          const canConc = canControlSignoffSection("conc_so") && allHavePrep;
                          const canEqr = canControlSignoffSection("eqr_so") && allHavePrep;
                          const canPartner = canControlSignoffSection("partner_so") && allHavePrep;
                          return (
                            <>
                              <DropdownMenuItem disabled={!canPrep} onClick={() => canPrep && handleBulkRiskSignoff("prep_so")}>Preparer</DropdownMenuItem>
                              <DropdownMenuItem disabled={!canRev} onClick={() => canRev && handleBulkRiskSignoff("rev_so")}>Reviewer</DropdownMenuItem>
                              <DropdownMenuItem disabled={!canConc} onClick={() => canConc && handleBulkRiskSignoff("conc_so")}>Concurrent</DropdownMenuItem>
                              <DropdownMenuItem disabled={!canEqr} onClick={() => canEqr && handleBulkRiskSignoff("eqr_so")}>EQR</DropdownMenuItem>
                              <DropdownMenuItem disabled={!canPartner} onClick={() => canPartner && handleBulkRiskSignoff("partner_so")}>Partner</DropdownMenuItem>
                            </>
                          );
                        })()}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  {!isArchivalMode && (
                    <Button
                      size="sm"
                      type="button"
                      onClick={() => {
                        setSelectedRommsToDelete(new Set());
                        setIsDeleteRommDialogOpen(true);
                      }}
                      variant="destructive"
                      className="bg-red-600 text-white hover:bg-red-700"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete RoMM
                    </Button>
                  )}
                </div>
              </div>

              {risksLoading ? (
                <div className="text-center text-white/60 py-8">
                  Loading risks...
                </div>
              ) : risks.length === 0 ? (
                <Card className="border-white/10 bg-white/5 text-white">
                  <CardContent className="p-6">
                    <div className="text-center text-white/60">
                      <AlertTriangle className="mx-auto h-12 w-12 mb-4 text-white/40" />
                      <p>No risks found.</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {!riskSignoffLoading && (
                    <div className="flex items-center gap-4 rounded-md border border-white/10 bg-white/5 px-3 py-2">
                      {!isArchivalMode && (
                        <div className="w-10 shrink-0 flex items-center justify-center">
                          <Checkbox
                            checked={risks.length > 0 && risks.every((r) => selectedRisksForSignoff.has(r.id))}
                            onCheckedChange={() => {
                              if (risks.length > 0 && risks.every((r) => selectedRisksForSignoff.has(r.id))) {
                                setSelectedRisksForSignoff(new Set());
                              } else {
                                setSelectedRisksForSignoff(new Set(risks.map((r) => r.id)));
                              }
                            }}
                            aria-label="Select all risks"
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0 text-xs font-semibold text-white/70">
                        Risk
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <div className="w-24 text-center text-xs font-semibold text-white/70" title="Preparer">Preparer</div>
                        <div className="w-24 text-center text-xs font-semibold text-white/70" title="Reviewer">Reviewer</div>
                        <div className="w-24 text-center text-xs font-semibold text-white/70" title="Concurrent">Concurrent</div>
                        <div className="w-24 text-center text-xs font-semibold text-white/70" title="EQR">EQR</div>
                        <div className="w-24 text-center text-xs font-semibold text-white/70" title="Partner">Partner</div>
                      </div>
                      <div className="w-8 flex-shrink-0" />
                    </div>
                  )}
                  {risks.map((risk) => {
                    const signoff = risk.signoff || {
                      prep_so: [],
                      rev_so: [],
                      conc_so: [],
                      eqr_so: [],
                      partner_so: [],
                    };
                    const prepCompleted = isRiskPrepCompleted(signoff);
                    const canPrep = canControlSignoffSection("prep_so");
                    const canRev = canControlSignoffSection("rev_so") && prepCompleted;
                    const canConc = canControlSignoffSection("conc_so") && prepCompleted;
                    const canEqr = canControlSignoffSection("eqr_so") && prepCompleted;
                    const canPartner = canControlSignoffSection("partner_so") && prepCompleted;

                    return (
                      <div
                        key={risk.id}
                        className="flex items-center gap-4 rounded-md border border-white/10 bg-white/5 p-3 hover:border-white/20 hover:bg-white/10"
                      >
                        {!isArchivalMode && (
                          <div className="w-10 shrink-0 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedRisksForSignoff.has(risk.id)}
                              onCheckedChange={() => {
                                setSelectedRisksForSignoff((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(risk.id)) next.delete(risk.id);
                                  else next.add(risk.id);
                                  return next;
                                });
                              }}
                              aria-label={`Select ${risk.id}`}
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-sm font-medium text-white truncate">
                              {risk.id}
                            </span>
                            {risk.workspace && (
                              <Badge variant="outline" className="text-[10px] shrink-0">
                                {risk.workspace}
                              </Badge>
                            )}
                            {risk.assertion && (
                              <Badge variant="outline" className="text-[10px] shrink-0">
                                {typeof risk.assertion === "string" ? getAssertionAbbreviation(risk.assertion) : risk.assertion}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-white/80 truncate line-clamp-2">
                            {risk.risk}
                          </p>
                          {risk.controlIds && risk.controlIds.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {risk.controlIds.slice(0, 3).map((controlId: string, idx: number) => (
                                <Badge key={idx} variant="outline" className="text-[9px]">
                                  {controlId}
                                </Badge>
                              ))}
                              {risk.controlIds.length > 3 && (
                                <span className="text-[9px] text-white/50">+{risk.controlIds.length - 3}</span>
                              )}
                            </div>
                          )}
                        </div>
                        {!riskSignoffLoading && (
                          <div className="flex items-center gap-4 flex-shrink-0">
                            {isArchivalMode ? (
                              <>
                                <div className="w-24 flex justify-center">
                                  {signoff.prep_so?.length > 0 && (
                                    <div className="flex items-center gap-0.5">
                                      {signoff.prep_so.slice(0, 3).map((entry: any, i: number) => (
                                        <div key={i} className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30" title={entry.metadata}>
                                          {getInitialsForSignoff(entry.name)}
                                        </div>
                                      ))}
                                      {signoff.prep_so.length > 3 && (
                                        <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">+{signoff.prep_so.length - 3}</div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="w-24 flex justify-center">
                                  {signoff.rev_so?.length > 0 && (
                                    <div className="flex items-center gap-0.5">
                                      {signoff.rev_so.slice(0, 3).map((entry: any, i: number) => (
                                        <div key={i} className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30" title={entry.metadata}>
                                          {getInitialsForSignoff(entry.name)}
                                        </div>
                                      ))}
                                      {signoff.rev_so.length > 3 && (
                                        <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">+{signoff.rev_so.length - 3}</div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="w-24 flex justify-center">
                                  {signoff.conc_so?.length > 0 && (
                                    <div className="flex items-center gap-0.5">
                                      {signoff.conc_so.slice(0, 3).map((entry: any, i: number) => (
                                        <div key={i} className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30" title={entry.metadata}>
                                          {getInitialsForSignoff(entry.name)}
                                        </div>
                                      ))}
                                      {signoff.conc_so.length > 3 && (
                                        <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">+{signoff.conc_so.length - 3}</div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="w-24 flex justify-center">
                                  {signoff.eqr_so?.length > 0 && (
                                    <div className="flex items-center gap-0.5">
                                      {signoff.eqr_so.slice(0, 3).map((entry: any, i: number) => (
                                        <div key={i} className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30" title={entry.metadata}>
                                          {getInitialsForSignoff(entry.name)}
                                        </div>
                                      ))}
                                      {signoff.eqr_so.length > 3 && (
                                        <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">+{signoff.eqr_so.length - 3}</div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="w-24 flex justify-center">
                                  {signoff.partner_so?.length > 0 && (
                                    <div className="flex items-center gap-0.5">
                                      {signoff.partner_so.slice(0, 3).map((entry: any, i: number) => (
                                        <div key={i} className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30" title={entry.metadata}>
                                          {getInitialsForSignoff(entry.name)}
                                        </div>
                                      ))}
                                      {signoff.partner_so.length > 3 && (
                                        <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">+{signoff.partner_so.length - 3}</div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="w-24 flex justify-center">
                                  <ControlSignoffBadge label="Preparer" signoffArray={signoff.prep_so || []} onSignoffClick={() => handleRiskSignoff(risk.id, "prep_so")} disabled={!canPrep} />
                                </div>
                                <div className="w-24 flex justify-center">
                                  <ControlSignoffBadge label="Reviewer" signoffArray={signoff.rev_so || []} onSignoffClick={() => handleRiskSignoff(risk.id, "rev_so")} disabled={!canRev} />
                                </div>
                                <div className="w-24 flex justify-center">
                                  <ControlSignoffBadge label="Concurrent" signoffArray={signoff.conc_so || []} onSignoffClick={() => handleRiskSignoff(risk.id, "conc_so")} disabled={!canConc} />
                                </div>
                                <div className="w-24 flex justify-center">
                                  <ControlSignoffBadge label="EQR" signoffArray={signoff.eqr_so || []} onSignoffClick={() => handleRiskSignoff(risk.id, "eqr_so")} disabled={!canEqr} />
                                </div>
                                <div className="w-24 flex justify-center">
                                  <ControlSignoffBadge label="Partner" signoffArray={signoff.partner_so || []} onSignoffClick={() => handleRiskSignoff(risk.id, "partner_so")} disabled={!canPartner} />
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        <div className="w-8 flex-shrink-0" />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );

      case "control-library":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedLibrary(null)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Libraries
              </Button>
              <div>
                <h2 className="text-xl font-semibold text-white">Control Library</h2>
                <p className="text-sm text-white/60">
                  View all controls created
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">Controls</h3>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {controls.length} control(s)
                  </Badge>
                  {!isArchivalMode && selectedControlsForSignoff.size > 0 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={controlSignoffLoading}
                          className="border-blue-500/50 text-blue-300 hover:bg-blue-500/10 hover:text-blue-200"
                        >
                          {controlSignoffLoading ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <FileCheck className="h-4 w-4 mr-2" />
                          )}
                          Sign off on selected ({selectedControlsForSignoff.size})
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-gray-900 border-white/10 text-white min-w-[200px]" align="end">
                        {(() => {
                          const selected = Array.from(selectedControlsForSignoff);
                          const allHavePrep = selected.length > 0 && selected.every((id) => {
                            const c = controls.find((x) => x.controlId === id);
                            return c && isControlPrepCompleted(c.signoff || {});
                          });
                          const canPrep = canControlSignoffSection("prep_so");
                          const canRev = canControlSignoffSection("rev_so") && allHavePrep;
                          const canConc = canControlSignoffSection("conc_so") && allHavePrep;
                          const canEqr = canControlSignoffSection("eqr_so") && allHavePrep;
                          const canPartner = canControlSignoffSection("partner_so") && allHavePrep;
                          return (
                            <>
                              <DropdownMenuItem disabled={!canPrep} onClick={() => canPrep && handleBulkControlSignoff("prep_so")}>Preparer</DropdownMenuItem>
                              <DropdownMenuItem disabled={!canRev} onClick={() => canRev && handleBulkControlSignoff("rev_so")}>Reviewer</DropdownMenuItem>
                              <DropdownMenuItem disabled={!canConc} onClick={() => canConc && handleBulkControlSignoff("conc_so")}>Concurrent</DropdownMenuItem>
                              <DropdownMenuItem disabled={!canEqr} onClick={() => canEqr && handleBulkControlSignoff("eqr_so")}>EQR</DropdownMenuItem>
                              <DropdownMenuItem disabled={!canPartner} onClick={() => canPartner && handleBulkControlSignoff("partner_so")}>Partner</DropdownMenuItem>
                            </>
                          );
                        })()}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  {!isArchivalMode && (
                    <Button
                      size="sm"
                      type="button"
                      onClick={() => {
                        setSelectedControlsToDelete(new Set());
                        setIsDeleteControlDialogOpen(true);
                      }}
                      variant="destructive"
                      className="bg-red-600 text-white hover:bg-red-700"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Control
                    </Button>
                  )}
                </div>
              </div>

              {controlsLoading ? (
                <div className="text-center text-white/60 py-8">
                  Loading controls...
                </div>
              ) : controls.length === 0 ? (
                <Card className="border-white/10 bg-white/5 text-white">
                  <CardContent className="p-6">
                    <div className="text-center text-white/60">
                      <Shield className="mx-auto h-12 w-12 mb-4 text-white/40" />
                      <p>No controls found.</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {/* Header row with column headings (outside the list, like Document List) */}
                  {!controlSignoffLoading && (
                    <div className="flex items-center gap-4 rounded-md border border-white/10 bg-white/5 px-3 py-2">
                      {!isArchivalMode && (
                        <div className="w-10 shrink-0 flex items-center justify-center">
                          <Checkbox
                            checked={controls.length > 0 && controls.every((c) => selectedControlsForSignoff.has(c.controlId))}
                            onCheckedChange={() => {
                              if (controls.length > 0 && controls.every((c) => selectedControlsForSignoff.has(c.controlId))) {
                                setSelectedControlsForSignoff(new Set());
                              } else {
                                setSelectedControlsForSignoff(new Set(controls.map((c) => c.controlId)));
                              }
                            }}
                            aria-label="Select all controls"
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0 text-xs font-semibold text-white/70">
                        Control
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <div className="w-24 text-center text-xs font-semibold text-white/70" title="Preparer">Preparer</div>
                        <div className="w-24 text-center text-xs font-semibold text-white/70" title="Reviewer">Reviewer</div>
                        <div className="w-24 text-center text-xs font-semibold text-white/70" title="Concurrent">Concurrent</div>
                        <div className="w-24 text-center text-xs font-semibold text-white/70" title="EQR">EQR</div>
                        <div className="w-24 text-center text-xs font-semibold text-white/70" title="Partner">Partner</div>
                      </div>
                      <div className="w-8 flex-shrink-0" />
                    </div>
                  )}
                  {controls.map((control) => {
                    const signoff = control.signoff || {
                      prep_so: [],
                      rev_so: [],
                      conc_so: [],
                      eqr_so: [],
                      partner_so: [],
                    };
                    const prepCompleted = isControlPrepCompleted(signoff);
                    const canPrep = canControlSignoffSection("prep_so");
                    const canRev = canControlSignoffSection("rev_so") && prepCompleted;
                    const canConc = canControlSignoffSection("conc_so") && prepCompleted;
                    const canEqr = canControlSignoffSection("eqr_so") && prepCompleted;
                    const canPartner = canControlSignoffSection("partner_so") && prepCompleted;

                    return (
                      <div
                        key={control.controlId}
                        className="flex items-center gap-4 rounded-md border border-white/10 bg-white/5 p-3 hover:border-white/20 hover:bg-white/10"
                      >
                        {!isArchivalMode && (
                          <div className="w-10 shrink-0 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedControlsForSignoff.has(control.controlId)}
                              onCheckedChange={() => {
                                setSelectedControlsForSignoff((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(control.controlId)) next.delete(control.controlId);
                                  else next.add(control.controlId);
                                  return next;
                                });
                              }}
                              aria-label={`Select ${control.controlId}`}
                            />
                          </div>
                        )}
                        <button
                          type="button"
                          className="flex-1 min-w-0 text-left"
                          onClick={() => handleControlLibraryClick(control)}
                        >
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-sm font-medium text-white truncate">
                              {control.controlId}
                            </span>
                            <Badge variant="outline" className="text-[10px] shrink-0">
                              {control.typeOfControl}
                            </Badge>
                            <Badge variant="outline" className="text-[10px] shrink-0">
                              {control.nature}
                            </Badge>
                          </div>
                          <p className="text-xs text-white/80 truncate">
                            {control.controlName}
                          </p>
                          {control.workspace?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {control.workspace.slice(0, 3).map((ws: string, idx: number) => (
                                <Badge key={idx} variant="outline" className="text-[9px]">
                                  {ws}
                                </Badge>
                              ))}
                              {control.workspace.length > 3 && (
                                <span className="text-[9px] text-white/50">+{control.workspace.length - 3}</span>
                              )}
                            </div>
                          )}
                        </button>
                        {!controlSignoffLoading && (
                          <div className="flex items-center gap-4 flex-shrink-0">
                            {isArchivalMode ? (
                              <>
                                <div className="w-24 flex justify-center">
                                  {signoff.prep_so && signoff.prep_so.length > 0 && (
                                    <div className="flex items-center gap-0.5">
                                      {signoff.prep_so.slice(0, 3).map((entry: any, i: number) => (
                                        <div
                                          key={i}
                                          className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                          title={entry.metadata}
                                        >
                                          {getInitialsForSignoff(entry.name)}
                                        </div>
                                      ))}
                                      {signoff.prep_so.length > 3 && (
                                        <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                          +{signoff.prep_so.length - 3}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="w-24 flex justify-center">
                                  {signoff.rev_so && signoff.rev_so.length > 0 && (
                                    <div className="flex items-center gap-0.5">
                                      {signoff.rev_so.slice(0, 3).map((entry: any, i: number) => (
                                        <div
                                          key={i}
                                          className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                          title={entry.metadata}
                                        >
                                          {getInitialsForSignoff(entry.name)}
                                        </div>
                                      ))}
                                      {signoff.rev_so.length > 3 && (
                                        <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                          +{signoff.rev_so.length - 3}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="w-24 flex justify-center">
                                  {signoff.conc_so && signoff.conc_so.length > 0 && (
                                    <div className="flex items-center gap-0.5">
                                      {signoff.conc_so.slice(0, 3).map((entry: any, i: number) => (
                                        <div
                                          key={i}
                                          className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                          title={entry.metadata}
                                        >
                                          {getInitialsForSignoff(entry.name)}
                                        </div>
                                      ))}
                                      {signoff.conc_so.length > 3 && (
                                        <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                          +{signoff.conc_so.length - 3}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="w-24 flex justify-center">
                                  {signoff.eqr_so && signoff.eqr_so.length > 0 && (
                                    <div className="flex items-center gap-0.5">
                                      {signoff.eqr_so.slice(0, 3).map((entry: any, i: number) => (
                                        <div
                                          key={i}
                                          className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                          title={entry.metadata}
                                        >
                                          {getInitialsForSignoff(entry.name)}
                                        </div>
                                      ))}
                                      {signoff.eqr_so.length > 3 && (
                                        <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                          +{signoff.eqr_so.length - 3}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="w-24 flex justify-center">
                                  {signoff.partner_so && signoff.partner_so.length > 0 && (
                                    <div className="flex items-center gap-0.5">
                                      {signoff.partner_so.slice(0, 3).map((entry: any, i: number) => (
                                        <div
                                          key={i}
                                          className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                          title={entry.metadata}
                                        >
                                          {getInitialsForSignoff(entry.name)}
                                        </div>
                                      ))}
                                      {signoff.partner_so.length > 3 && (
                                        <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                          +{signoff.partner_so.length - 3}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="w-24 flex justify-center">
                                  <ControlSignoffBadge
                                    label="Preparer"
                                    signoffArray={signoff.prep_so || []}
                                    onSignoffClick={() => handleControlSignoff(control.controlId, "prep_so")}
                                    disabled={!canPrep}
                                  />
                                </div>
                                <div className="w-24 flex justify-center">
                                  <ControlSignoffBadge
                                    label="Reviewer"
                                    signoffArray={signoff.rev_so || []}
                                    onSignoffClick={() => handleControlSignoff(control.controlId, "rev_so")}
                                    disabled={!canRev}
                                  />
                                </div>
                                <div className="w-24 flex justify-center">
                                  <ControlSignoffBadge
                                    label="Concurrent"
                                    signoffArray={signoff.conc_so || []}
                                    onSignoffClick={() => handleControlSignoff(control.controlId, "conc_so")}
                                    disabled={!canConc}
                                  />
                                </div>
                                <div className="w-24 flex justify-center">
                                  <ControlSignoffBadge
                                    label="EQR"
                                    signoffArray={signoff.eqr_so || []}
                                    onSignoffClick={() => handleControlSignoff(control.controlId, "eqr_so")}
                                    disabled={!canEqr}
                                  />
                                </div>
                                <div className="w-24 flex justify-center">
                                  <ControlSignoffBadge
                                    label="Partner"
                                    signoffArray={signoff.partner_so || []}
                                    onSignoffClick={() => handleControlSignoff(control.controlId, "partner_so")}
                                    disabled={!canPartner}
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        <div className="w-8 flex-shrink-0 flex justify-end">
                          <ChevronRight className="h-4 w-4 text-white/40" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      
      case "review-note-library":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedLibrary(null)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Libraries
              </Button>
              <div>
                <h2 className="text-xl font-semibold text-white">Review Note Library</h2>
                <p className="text-sm text-white/60">
                  View all review notes and comments
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">Review Notes</h3>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {reviewNotes.reduce((total, comment) => {
                      const repliesCount = comment.replies?.length || 0;
                      return total + 1 + repliesCount;
                    }, 0)} note(s)
                  </Badge>
                  {!isArchivalMode && (
                    <Button
                      size="sm"
                      type="button"
                      onClick={async () => {
                        if (selectedReviewNotesToDelete.size === 0) {
                          alert("Please select at least one review note to delete");
                          return;
                        }

                        const confirmed = window.confirm(
                          `Are you sure you want to delete ${selectedReviewNotesToDelete.size} selected review note(s)?\n\nThis action cannot be undone.`
                        );

                        if (!confirmed) return;

                        try {
                          setDeletingReviewNotes(true);
                          
                          // Create a copy of review notes to modify
                          const updatedComments = [...reviewNotes];
                          
                          // Process deletions
                          const itemsToDelete = Array.from(selectedReviewNotesToDelete);
                          
                          // First, identify which main comments are being deleted
                          const mainCommentIndicesToDelete = new Set<number>();
                          itemsToDelete.forEach((itemId) => {
                            if (itemId.startsWith("comment-") && !itemId.includes("-reply-")) {
                              const commentIndex = parseInt(itemId.replace("comment-", ""));
                              if (!isNaN(commentIndex)) {
                                mainCommentIndicesToDelete.add(commentIndex);
                              }
                            }
                          });
                          
                          // Then, identify individual replies to delete (excluding those whose parent comment is being deleted)
                          const replyDeletions: { commentIndex: number; replyIndex: number }[] = [];
                          itemsToDelete.forEach((itemId) => {
                            if (itemId.startsWith("comment-") && itemId.includes("-reply-")) {
                              const parts = itemId.split("-");
                              const commentIndex = parseInt(parts[1]);
                              const replyIndex = parseInt(parts[3]);
                              if (!isNaN(commentIndex) && !isNaN(replyIndex)) {
                                // Only add if the parent comment is NOT being deleted
                                if (!mainCommentIndicesToDelete.has(commentIndex)) {
                                  replyDeletions.push({ commentIndex, replyIndex });
                                }
                              }
                            }
                          });
                          
                          // Remove individual replies first (before removing main comments)
                          // Sort reply deletions by commentIndex and replyIndex in reverse to maintain indices
                          replyDeletions.sort((a, b) => {
                            if (a.commentIndex !== b.commentIndex) {
                              return b.commentIndex - a.commentIndex;
                            }
                            return b.replyIndex - a.replyIndex;
                          });
                          
                          replyDeletions.forEach(({ commentIndex, replyIndex }) => {
                            if (updatedComments[commentIndex] && updatedComments[commentIndex].replies) {
                              updatedComments[commentIndex].replies.splice(replyIndex, 1);
                            }
                          });
                          
                          // Remove main comments (in reverse order to maintain indices)
                          // When a main comment is deleted, all its replies are automatically deleted
                          const sortedIndices = Array.from(mainCommentIndicesToDelete).sort((a, b) => b - a);
                          sortedIndices.forEach((index) => {
                            updatedComments.splice(index, 1);
                          });
                          
                          // Upload updated comments to SharePoint
                          const result = await (window as any).sharePointAPI?.uploadClientJson?.("Review_Comments.json", {
                            comments: updatedComments
                          });
                          
                          if (result?.success) {
                            setReviewNotes(updatedComments);
                            setSelectedReviewNotesToDelete(new Set());
                            alert("Review notes deleted successfully");
                          } else {
                            alert(`Failed to delete review notes: ${result?.error || "Unknown error"}`);
                          }
                        } catch (e: any) {
                          console.error("Failed to delete review notes", e);
                          alert(`Error deleting review notes: ${e?.message || "Unknown error"}`);
                        } finally {
                          setDeletingReviewNotes(false);
                        }
                      }}
                      variant="destructive"
                      disabled={deletingReviewNotes || selectedReviewNotesToDelete.size === 0}
                      className="bg-red-600 text-white hover:bg-red-700"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {deletingReviewNotes ? "Deleting..." : "Delete Selected"}
                    </Button>
                  )}
                </div>
              </div>

              {reviewNotesLoading ? (
                <div className="text-center text-white/60 py-8">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                  Loading review notes...
                </div>
              ) : reviewNotes.length === 0 ? (
                <Card className="border-white/10 bg-white/5 text-white">
                  <CardContent className="p-6">
                    <div className="text-center text-white/60">
                      <MessageSquare className="mx-auto h-12 w-12 mb-4 text-white/40" />
                      <p>No review notes found.</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="rounded-md border border-white/10 bg-black/20 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-white/5">
                        <TableHead className="text-white/80 w-12">
                          <input
                            type="checkbox"
                            checked={
                              reviewNotes.length > 0 && 
                              reviewNotes.every((_, idx) => selectedReviewNotesToDelete.has(`comment-${idx}`)) &&
                              reviewNotes.every((comment, idx) => {
                                if (!comment.replies || !Array.isArray(comment.replies)) return true;
                                return comment.replies.every((_: any, replyIdx: number) => 
                                  selectedReviewNotesToDelete.has(`comment-${idx}-reply-${replyIdx}`)
                                );
                              })
                            }
                            onChange={(e) => {
                              const newSet = new Set(selectedReviewNotesToDelete);
                              if (e.target.checked) {
                                reviewNotes.forEach((comment, idx) => {
                                  newSet.add(`comment-${idx}`);
                                  // Also select all replies
                                  if (comment.replies && Array.isArray(comment.replies)) {
                                    comment.replies.forEach((_: any, replyIdx: number) => {
                                      newSet.add(`comment-${idx}-reply-${replyIdx}`);
                                    });
                                  }
                                });
                              } else {
                                reviewNotes.forEach((comment, idx) => {
                                  newSet.delete(`comment-${idx}`);
                                  // Also deselect all replies
                                  if (comment.replies && Array.isArray(comment.replies)) {
                                    comment.replies.forEach((_: any, replyIdx: number) => {
                                      newSet.delete(`comment-${idx}-reply-${replyIdx}`);
                                    });
                                  }
                                });
                              }
                              setSelectedReviewNotesToDelete(newSet);
                            }}
                            className="w-4 h-4 rounded border-white/20 bg-black/40 text-red-500 focus:ring-red-500"
                          />
                        </TableHead>
                        <TableHead className="text-white/80 min-w-[400px]">Comment</TableHead>
                        <TableHead className="text-white/80 min-w-[150px]">User</TableHead>
                        <TableHead className="text-white/80 min-w-[200px]">Date & Time</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reviewNotes.map((comment: any, commentIndex: number) => {
                        const commentId = `comment-${commentIndex}`;
                        const isMainCommentSelected = selectedReviewNotesToDelete.has(commentId);
                        
                        return (
                          <React.Fragment key={commentIndex}>
                            {/* Main comment row */}
                            <TableRow className={`border-white/10 hover:bg-white/5 ${isMainCommentSelected ? "bg-red-500/10" : ""}`}>
                              <TableCell className="py-3">
                                <input
                                  type="checkbox"
                                  checked={isMainCommentSelected}
                                  onChange={(e) => {
                                    const newSet = new Set(selectedReviewNotesToDelete);
                                    if (e.target.checked) {
                                      // Select main comment (replies will be deleted automatically when main comment is deleted)
                                      newSet.add(commentId);
                                    } else {
                                      // Deselect main comment
                                      newSet.delete(commentId);
                                    }
                                    setSelectedReviewNotesToDelete(newSet);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-4 h-4 rounded border-white/20 bg-black/40 text-red-500 focus:ring-red-500"
                                />
                              </TableCell>
                              <TableCell className="text-white/80 py-3">
                                {comment.text || ""}
                              </TableCell>
                              <TableCell className="text-white/80 py-3">
                                <div className="flex items-center gap-2">
                                  <div className="h-8 w-8 rounded-full bg-blue-500/20 flex items-center justify-center text-xs font-medium text-blue-300">
                                    {getInitials(comment.user_name || "")}
                                  </div>
                                  <span className="text-sm">{comment.user_name || ""}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-white/80 py-3">
                                {formatDateTime(comment.datetime || "")}
                              </TableCell>
                            </TableRow>
                            {/* Reply rows */}
                            {comment.replies && Array.isArray(comment.replies) && comment.replies.map((reply: any, replyIndex: number) => {
                              const replyId = `${commentId}-reply-${replyIndex}`;
                              const isReplySelected = selectedReviewNotesToDelete.has(replyId);
                              
                              return (
                                <TableRow key={replyId} className={`border-white/5 hover:bg-white/5 bg-black/10 ${isReplySelected ? "bg-red-500/10" : ""}`}>
                                  <TableCell className="py-3">
                                    <input
                                      type="checkbox"
                                      checked={isReplySelected}
                                      onChange={(e) => {
                                        const newSet = new Set(selectedReviewNotesToDelete);
                                        if (e.target.checked) {
                                          newSet.add(replyId);
                                        } else {
                                          newSet.delete(replyId);
                                          // If deselecting a reply, also deselect the main comment if it was selected
                                          if (isMainCommentSelected) {
                                            newSet.delete(commentId);
                                          }
                                        }
                                        setSelectedReviewNotesToDelete(newSet);
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      className="w-4 h-4 rounded border-white/20 bg-black/40 text-red-500 focus:ring-red-500"
                                    />
                                  </TableCell>
                                  <TableCell className="text-white/70 py-3">
                                    <div className="flex items-start gap-2 pl-6">
                                      <ArrowRight className="h-4 w-4 text-white/40 mt-1 flex-shrink-0" />
                                      <span className="text-sm">{reply.text || ""}</span>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-white/70 py-3">
                                    <div className="flex items-center gap-2">
                                      <div className="h-8 w-8 rounded-full bg-blue-500/20 flex items-center justify-center text-xs font-medium text-blue-300">
                                        {getInitials(reply.user_name || "")}
                                      </div>
                                      <span className="text-sm">{reply.user_name || ""}</span>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-white/70 py-3">
                                    {formatDateTime(reply.datetime || "")}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        );

      case "control-owner-library":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedLibrary(null)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Libraries
              </Button>
              <div>
                <h2 className="text-xl font-semibold text-white">Control Owner Library</h2>
                <p className="text-sm text-white/60">
                  Control ownership and responsibility matrix
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">Control Owners</h3>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {Object.keys(controlOwners).length} owner(s)
                  </Badge>
                  <Button
                    type="button"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setEditingOwner(null); // Clear editing state for new owner
                      setIsControlOwnerFormOpen(true);
                    }}
                    className="bg-[#4da3ff] text-white hover:bg-[#4da3ff]/80"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Control Owner
                  </Button>
                  {!isArchivalMode && (
                    <Button
                      type="button"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedControlOwnersToDelete(new Set());
                        setIsDeleteControlOwnerDialogOpen(true);
                      }}
                      variant="destructive"
                      className="bg-red-600 text-white hover:bg-red-700"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Owner
                    </Button>
                  )}
                </div>
              </div>

              {controlOwnersLoading ? (
                <div className="text-center text-white/60 py-8">
                  Loading control owners...
                </div>
              ) : Object.keys(controlOwners).length === 0 ? (
                <Card className="border-white/10 bg-white/5 text-white">
                  <CardContent className="p-6">
                    <div className="text-center text-white/60">
                      <Users className="mx-auto h-12 w-12 mb-4 text-white/40" />
                      <p>No control owners created yet.</p>
                      <p className="text-sm mt-2">Use the "Add Control Owner" button to create new control owners.</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {Object.entries(controlOwners).map(([id, owner]: [string, any]) => (
                    <Card
                      key={id}
                      className="border-white/10 bg-white/5 text-white cursor-pointer hover:bg-white/10 transition-colors"
                      onClick={() => {
                        setEditingOwner({ id, data: owner });
                        setIsControlOwnerFormOpen(true);
                      }}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-sm font-medium text-white">
                                {id}
                              </h3>
                              <Badge variant="outline" className="text-xs">
                                {owner.name}
                              </Badge>
                            </div>
                            <p className="text-sm text-white/80 mb-1">
                              <span className="font-medium">Designation:</span> {owner.designation}
                            </p>
                            {owner.emailid && (
                              <p className="text-sm text-white/80 mb-1">
                                <span className="font-medium">Email:</span> {owner.emailid}
                              </p>
                            )}
                            {owner.authorityCompetence && (
                              <div className="mt-2">
                                <p className="text-xs font-medium text-white/60 mb-1">Authority & Competence:</p>
                                <div 
                                  className="text-sm text-white/70 prose prose-invert max-w-none"
                                  dangerouslySetInnerHTML={{ 
                                    __html: typeof owner.authorityCompetence === 'object' 
                                      ? owner.authorityCompetence.html 
                                      : owner.authorityCompetence 
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case "confirmation-library":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedLibrary(null)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Libraries
              </Button>
              <div>
                <h2 className="text-xl font-semibold text-white">Confirmation Library</h2>
                <p className="text-sm text-white/60">
                  Manage audit confirmations
                </p>
              </div>
            </div>

            <Tabs value={confirmationTab} onValueChange={setConfirmationTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="confirmations">Confirmations</TabsTrigger>
                <TabsTrigger value="domain-testing">Domain Testing</TabsTrigger>
                <TabsTrigger value="sampling-logs">Sampling Logs</TabsTrigger>
              </TabsList>

              <TabsContent value="confirmations" className="mt-6">
                <Card className="border-white/10 bg-white/5 text-white">
                  <CardHeader>
                    <CardTitle className="text-base font-semibold text-white">Confirmations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {confirmationsLoading ? (
                      <div className="flex items-center justify-center gap-2 py-12 text-white/60">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>Loading confirmations...</span>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex flex-wrap items-center gap-4">
                          <div className="flex flex-col gap-1.5">
                            <label className="text-xs text-white/60 uppercase tracking-wide">Workspace</label>
                            <Select
                              value={selectedConfirmationsWorkspace}
                              onValueChange={(value) => {
                                setSelectedConfirmationsWorkspace(value);
                                setSelectedConfirmationsSet("__all__");
                              }}
                              data-allow-archival-edit={true}
                            >
                              <SelectTrigger className="w-[220px] h-9 placeholder:text-white/40">
                                <SelectValue placeholder="All workspaces" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__all__" className="text-white focus:bg-white/10 focus:text-white">
                                  All workspaces
                                </SelectItem>
                                {(() => {
                                  const workspaces = [...new Set(Object.values(confirmationsData).map((d: any) => d?.partydetails?.area).filter(Boolean))].sort();
                                  return workspaces.map((workspace) => (
                                    <SelectItem key={workspace} value={String(workspace)} className="text-white focus:bg-white/10 focus:text-white">
                                      {String(workspace)}
                                    </SelectItem>
                                  ));
                                })()}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <label className="text-xs text-white/60 uppercase tracking-wide">Sample Set</label>
                            <Select
                              value={selectedConfirmationsSet}
                              onValueChange={setSelectedConfirmationsSet}
                              disabled={selectedConfirmationsWorkspace === "__all__"}
                              data-allow-archival-edit={true}
                            >
                              <SelectTrigger className="w-[220px] h-9 placeholder:text-white/40 disabled:opacity-50">
                                <SelectValue placeholder={selectedConfirmationsWorkspace !== "__all__" ? "All sample sets" : "Select workspace first"} />
                              </SelectTrigger>
                              <SelectContent>
                                {selectedConfirmationsWorkspace && selectedConfirmationsWorkspace !== "__all__" && (() => {
                                  const sampleSets = [...new Set(
                                    Object.values(confirmationsData)
                                      .filter((d: any) => d?.partydetails?.area === selectedConfirmationsWorkspace)
                                      .map((d: any) => d?.partydetails?.sample_set)
                                      .filter(Boolean)
                                  )].sort();
                                  return (
                                    <>
                                      <SelectItem value="__all__" className="text-white focus:bg-white/10 focus:text-white">
                                        All sample sets
                                      </SelectItem>
                                      {sampleSets.map((setName) => (
                                        <SelectItem key={String(setName)} value={String(setName)} className="text-white focus:bg-white/10 focus:text-white">
                                          {String(setName)}
                                        </SelectItem>
                                      ))}
                                    </>
                                  );
                                })()}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="space-y-5">
                          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                            <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                              <p className="text-xs text-white/60 mb-3 uppercase tracking-wide">Confirmations</p>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <p className="text-xs text-white/60">Total confirmations</p>
                                  <p className="text-xl font-semibold text-white">{confirmationSummary.total}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-white/60">Received</p>
                                  <p className="text-xl font-semibold text-emerald-400">{confirmationSummary.received}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-white/60">Not received</p>
                                  <p className="text-xl font-semibold text-rose-400">{confirmationSummary.notReceived}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-white/60">Alt testing linked</p>
                                  <p className="text-xl font-semibold text-indigo-400">{confirmationSummary.alternateTesting}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 mt-3">
                                <Badge variant="outline" className="border-white/20 text-white/80">
                                  Workspaces: {confirmationSummary.uniqueWorkspaces}
                                </Badge>
                                <Badge variant="outline" className="border-white/20 text-white/80">
                                  Sample Sets: {confirmationSummary.uniqueSampleSets}
                                </Badge>
                              </div>
                            </div>

                            <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                              <p className="text-xs text-white/60 mb-3 uppercase tracking-wide">Confirmation Receipt Split</p>
                              <div className="h-56">
                                {confirmationSummary.receivedPieData.length > 0 ? (
                                  <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                      <Pie data={confirmationSummary.receivedPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={78}>
                                        {confirmationSummary.receivedPieData.map((entry) => (
                                          <Cell key={entry.name} fill={entry.color} />
                                        ))}
                                      </Pie>
                                      <Tooltip />
                                      <Legend />
                                    </PieChart>
                                  </ResponsiveContainer>
                                ) : (
                                  <div className="h-full flex items-center justify-center text-white/50 text-sm">
                                    No confirmation data
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                            <p className="text-xs text-white/60 mb-3 uppercase tracking-wide">Workspace-wise Confirmation Status</p>
                            {confirmationSummary.workspaceBreakdown.length > 0 ? (
                              <div className="overflow-x-auto">
                                <Table>
                                  <TableHeader>
                                    <TableRow className="border-white/10 hover:bg-transparent">
                                      <TableHead className="text-white/80 font-medium">Workspace</TableHead>
                                      <TableHead className="text-white/80 font-medium text-right">Total</TableHead>
                                      <TableHead className="text-white/80 font-medium text-right">Received</TableHead>
                                      <TableHead className="text-white/80 font-medium text-right">Not Received</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {confirmationSummary.workspaceBreakdown.map((row) => (
                                      <TableRow key={row.workspace} className="border-white/10 hover:bg-white/5">
                                        <TableCell className="text-white/90">{row.workspace}</TableCell>
                                        <TableCell className="text-white/90 text-right">{row.total}</TableCell>
                                        <TableCell className="text-emerald-400 text-right">{row.received}</TableCell>
                                        <TableCell className="text-rose-400 text-right">{row.notReceived}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            ) : (
                              <div className="text-white/50 text-sm">No confirmation entries found.</div>
                            )}
                          </div>
                        </div>

                        {(() => {
                          const filtered = filteredConfirmations;
                          return (
                            <div className="border border-white/10 rounded-lg overflow-x-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow className="border-white/10 hover:bg-transparent">
                                    <TableHead className="text-white/80 font-medium whitespace-nowrap">Workspace</TableHead>
                                    <TableHead className="text-white/80 font-medium whitespace-nowrap">Sample Set</TableHead>
                                    <TableHead className="text-white/80 font-medium whitespace-nowrap">Confirmation ID</TableHead>
                                    <TableHead className="text-white/80 font-medium whitespace-nowrap">Confirming Party</TableHead>
                                    <TableHead className="text-white/80 font-medium whitespace-nowrap">Recipient Name</TableHead>
                                    <TableHead className="text-white/80 font-medium whitespace-nowrap">Recipient Email</TableHead>
                                    <TableHead className="text-white/80 font-medium whitespace-nowrap">Status</TableHead>
                                    <TableHead className="text-white/80 font-medium w-[80px]">View</TableHead>
                                    <TableHead className="text-white/80 font-medium whitespace-nowrap">Exception/Non-response</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {filtered.length === 0 ? (
                                    <TableRow className="border-white/10 hover:bg-transparent">
                                      <TableCell colSpan={9} className="text-center text-white/50 py-8 text-sm">
                                        {Object.keys(confirmationsData).length === 0
                                          ? "No data"
                                          : "No entries match the selected filters."}
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    filtered.map((row) => {
                                      const p = row.partydetails;
                                      const confirmationData = confirmationsData[row.confirmationId];
                                      const confirmingParty = p?.ConfirmingParty ?? "";
                                      const recipientName = confirmationData?.recipientName ?? "";
                                      const recipientEmail = confirmationData?.recipientEmail ?? "";
                                      const exceptionNonResponse = p?.exception_non_response ?? p?.exceptionNonResponse ?? p?.exception ?? "";
                                      const received = isConfirmationReceived(row.confirmationId);
                                      const alternateTesting = confirmationsData[row.confirmationId]?.alternateTesting;
                                      const isSaving = savingAlternateForId === row.confirmationId;
                                      return (
                                        <TableRow key={row.confirmationId} className="border-white/10 hover:bg-white/5">
                                          <TableCell className="text-white/90 text-sm whitespace-nowrap">{row.workspace || "-"}</TableCell>
                                          <TableCell className="text-white/90 text-sm whitespace-nowrap">{row.sampleSet || "-"}</TableCell>
                                          <TableCell className="text-white/90 text-sm">{row.confirmationId || "-"}</TableCell>
                                          <TableCell className="text-white/90 text-sm">{confirmingParty || "-"}</TableCell>
                                          <TableCell className="text-white/80 text-sm">{recipientName || "-"}</TableCell>
                                          <TableCell className="text-white/80 text-sm">{recipientEmail || "-"}</TableCell>
                                          <TableCell className="text-white/80 text-sm">
                                            {received ? (
                                              <span className="inline-flex items-center gap-1.5 rounded-full bg-green-500/20 px-2.5 py-0.5 text-xs font-medium text-green-400 border border-green-500/30">
                                                <CheckCircle className="h-3.5 w-3.5" />
                                                Received
                                              </span>
                                            ) : (
                                              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/20 px-2.5 py-0.5 text-xs font-medium text-red-400 border border-red-500/30">
                                                <XCircle className="h-3.5 w-3.5" />
                                                Not Received
                                              </span>
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                                              onClick={() => setViewingConfirmationId(row.confirmationId)}
                                              title="View details"
                                              data-allow-archival-edit={true}
                                            >
                                              <Eye className="h-4 w-4" />
                                            </Button>
                                          </TableCell>
                                          <TableCell className="text-white/80 text-sm align-top">
                                            {received ? (
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 text-xs text-white/70 hover:text-white hover:bg-white/10 px-2"
                                                onClick={() => setConfirmationReconciliationDialogId(row.confirmationId)}
                                              >
                                                Open
                                              </Button>
                                            ) : (
                                              <div className="flex flex-col gap-1.5 min-w-[200px]">
                                                {alternateTesting?.fileName && (
                                                  <div className="flex items-center gap-1.5 flex-wrap">
                                                    <span className="text-white/70 text-xs truncate max-w-[140px]" title={alternateTesting.fileName}>
                                                      {alternateTesting.fileName}
                                                    </span>
                                                    <Button
                                                      type="button"
                                                      variant="ghost"
                                                      size="sm"
                                                      className="h-6 w-6 p-0 text-white/70 hover:text-white hover:bg-white/10 shrink-0"
                                                      title="Open file"
                                                      onClick={() => openAlternateTestingFile(alternateTesting.fileName)}
                                                      disabled={isSaving}
                                                    >
                                                      <ExternalLink className="h-3.5 w-3.5" />
                                                    </Button>
                                                    <Button
                                                      type="button"
                                                      variant="ghost"
                                                      size="sm"
                                                      className="h-6 w-6 p-0 text-white/70 hover:text-red-400 hover:bg-red-500/10 shrink-0"
                                                      title="De-link file"
                                                      onClick={() => clearAlternateTesting(row.confirmationId, alternateTesting.fileName, row.workspace)}
                                                      disabled={isSaving}
                                                    >
                                                      <X className="h-3.5 w-3.5" />
                                                    </Button>
                                                  </div>
                                                )}
                                                {isSaving ? (
                                                  <span className="text-white/50 text-xs flex items-center gap-1">
                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                    Saving...
                                                  </span>
                                                ) : (
                                                  <div className="flex flex-wrap gap-1">
                                                    <input
                                                      ref={confirmationAlternateFileInputRef}
                                                      type="file"
                                                      accept=".xlsx,.xls"
                                                      className="hidden"
                                                      onChange={async (e) => {
                                                        const file = e.target.files?.[0];
                                                        const cid = confirmationIdForUpload;
                                                        e.target.value = "";
                                                        setConfirmationIdForUpload(null);
                                                        if (!file || !cid) return;
                                                        setSavingAlternateForId(cid);
                                                        try {
                                                          const reader = new FileReader();
                                                          reader.onload = async () => {
                                                            const base64 = (reader.result as string)?.split(",")?.[1] || "";
                                                            const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
                                                              fileName: file.name,
                                                              fileContent: base64,
                                                              folderName: "client",
                                                              fyYear: getFY_YEAR?.(),
                                                            });
                                                            if (uploadResult?.success) {
                                                              const workspace = confirmationsData[cid]?.partydetails?.area;
                                                              await saveAlternateTesting(cid, { fileName: file.name, type: "upload" }, workspace);
                                                            } else {
                                                              alert(uploadResult?.error || "Upload failed");
                                                              setSavingAlternateForId(null);
                                                            }
                                                          };
                                                          reader.readAsDataURL(file);
                                                        } catch (err: any) {
                                                          alert(err?.message || "Upload failed");
                                                          setSavingAlternateForId(null);
                                                        }
                                                      }}
                                                    />
                                                    <Button
                                                      type="button"
                                                      variant="ghost"
                                                      size="sm"
                                                      className="h-7 text-xs text-white/70 hover:text-white hover:bg-white/10 px-2"
                                                      onClick={() => {
                                                        setConfirmationIdForUpload(row.confirmationId);
                                                        confirmationAlternateFileInputRef.current?.click();
                                                      }}
                                                    >
                                                      <Upload className="h-3.5 w-3.5 mr-1" />
                                                      Upload Excel
                                                    </Button>
                                                    <Select
                                                      value=""
                                                      onValueChange={async (fileName) => {
                                                        if (!fileName) return;
                                                        await saveAlternateTesting(row.confirmationId, { fileName, type: "link" }, row.workspace);
                                                      }}
                                                    >
                                                      <SelectTrigger className="h-7 w-[140px] text-xs">
                                                        <SelectValue placeholder={loadingClientFiles ? "Loading..." : "Link existing"} />
                                                      </SelectTrigger>
                                                      <SelectContent>
                                                        {clientFolderFiles.map((f) => (
                                                          <SelectItem key={f.name} value={f.name} className="text-white focus:bg-white/10 focus:text-white text-xs">
                                                            {f.name}
                                                          </SelectItem>
                                                        ))}
                                                        {!loadingClientFiles && clientFolderFiles.length === 0 && (
                                                          <SelectItem value="__none" disabled className="text-white/50 text-xs">
                                                            No Excel files in client folder
                                                          </SelectItem>
                                                        )}
                                                      </SelectContent>
                                                    </Select>
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })
                                  )}
                                </TableBody>
                              </Table>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Confirmation Reconciliation dialog (for Received confirmations) */}
              <Dialog open={!!confirmationReconciliationDialogId} onOpenChange={(open) => !open && setConfirmationReconciliationDialogId(null)}>
                <DialogContent className="max-w-2xl max-h-[85vh] bg-black/90 border-white/10 text-white overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Confirmation Reconciliation</DialogTitle>
                    <DialogDescription className="text-white/60 text-sm">
                      {confirmationReconciliationDialogId && (
                        <>Confirmation ID: {confirmationReconciliationDialogId}</>
                      )}
                    </DialogDescription>
                  </DialogHeader>
                  {confirmationReconciliationDialogId && (() => {
                    const form = confirmationReconciliationForm;
                    const sumBooks =
                      (parseFloat(form.amountAsPerSamplingData) || 0) +
                      form.amountAsPerBooksExtraRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
                    const sumAdd = form.addRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
                    const sumLess = form.lessRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
                    const adjustedAmountAsPerBooks = sumBooks + sumAdd - sumLess;
                    const amountAsPerConfirmationNum = parseFloat(form.amountAsPerConfirmation) || 0;
                    const difference = adjustedAmountAsPerBooks - amountAsPerConfirmationNum;
                    const workspace = confirmationsData[confirmationReconciliationDialogId]?.partydetails?.area ?? "";
                    return (
                      <>
                        <div className="border border-white/10 rounded-lg overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="border-white/10 bg-white/5">
                                <TableHead className="text-white/80 font-medium">Particulars</TableHead>
                                <TableHead className="text-white/80 font-medium w-[160px] text-right">Amount</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {/* Amount as per Books */}
                              <TableRow className="border-white/10 hover:bg-white/5">
                                <TableCell className="text-white/90 text-sm">Amount as per Sampling data</TableCell>
                                <TableCell className="text-white/90 text-sm text-right p-1">
                                  <Input
                                    type="number"
                                    value={form.amountAsPerSamplingData}
                                    onChange={(e) =>
                                      setConfirmationReconciliationForm((prev) => ({
                                        ...prev,
                                        amountAsPerSamplingData: e.target.value,
                                      }))
                                    }
                                    className="h-8 text-right w-full max-w-[140px] ml-auto bg-black/20 border-white/10 text-white text-sm"
                                  />
                                </TableCell>
                              </TableRow>
                              {form.amountAsPerBooksExtraRows.map((row, idx) => (
                                <TableRow key={`books-${idx}`} className="border-white/10 hover:bg-white/5">
                                  <TableCell className="text-white/90 text-sm p-1">
                                    <div className="flex items-center gap-1">
                                      <Input
                                        type="text"
                                        placeholder="Particulars"
                                        value={row.particulars}
                                        onChange={(e) => {
                                          const next = [...form.amountAsPerBooksExtraRows];
                                          next[idx] = { ...next[idx], particulars: e.target.value };
                                          setConfirmationReconciliationForm((prev) => ({ ...prev, amountAsPerBooksExtraRows: next }));
                                        }}
                                        className="h-8 flex-1 bg-black/20 border-white/10 text-white placeholder:text-white/40 text-sm"
                                      />
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-white/50 hover:text-white hover:bg-white/10 shrink-0"
                                        onClick={() =>
                                          setConfirmationReconciliationForm((prev) => ({
                                            ...prev,
                                            amountAsPerBooksExtraRows: form.amountAsPerBooksExtraRows.filter((_, i) => i !== idx),
                                          }))
                                        }
                                        title="Remove"
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-white/90 text-sm text-right p-1">
                                    <Input
                                      type="number"
                                      placeholder="Amount"
                                      value={row.amount}
                                      onChange={(e) => {
                                        const next = [...form.amountAsPerBooksExtraRows];
                                        next[idx] = { ...next[idx], amount: e.target.value };
                                        setConfirmationReconciliationForm((prev) => ({ ...prev, amountAsPerBooksExtraRows: next }));
                                      }}
                                      className="h-8 text-right w-full max-w-[140px] ml-auto bg-black/20 border-white/10 text-white text-sm"
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                              <TableRow className="border-white/10 hover:bg-transparent">
                                <TableCell colSpan={2} className="py-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="text-white/70 hover:text-white hover:bg-white/10 text-xs h-7"
                                    onClick={() =>
                                      setConfirmationReconciliationForm((prev) => ({
                                        ...prev,
                                        amountAsPerBooksExtraRows: [...prev.amountAsPerBooksExtraRows, { particulars: "", amount: "" }],
                                      }))
                                    }
                                  >
                                    <Plus className="h-3.5 w-3 mr-1" />
                                    Add row
                                  </Button>
                                </TableCell>
                              </TableRow>
                              {/* Spacer */}
                              <TableRow className="border-white/10 hover:bg-transparent">
                                <TableCell colSpan={2} className="h-2" />
                              </TableRow>
                              {/* Add: */}
                              <TableRow className="border-white/10 hover:bg-transparent">
                                <TableCell colSpan={2} className="text-white/80 font-medium text-sm pb-1">Add:</TableCell>
                              </TableRow>
                              {form.addRows.map((row, idx) => (
                                <TableRow key={`add-${idx}`} className="border-white/10 hover:bg-white/5">
                                  <TableCell className="text-white/90 text-sm p-1">
                                    <div className="flex items-center gap-1">
                                      <Input
                                        type="text"
                                        placeholder="Particulars"
                                        value={row.particulars}
                                        onChange={(e) => {
                                          const next = [...form.addRows];
                                          next[idx] = { ...next[idx], particulars: e.target.value };
                                          setConfirmationReconciliationForm((prev) => ({ ...prev, addRows: next }));
                                        }}
                                        className="h-8 flex-1 bg-black/20 border-white/10 text-white placeholder:text-white/40 text-sm"
                                      />
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-white/50 hover:text-white hover:bg-white/10 shrink-0"
                                        onClick={() =>
                                          setConfirmationReconciliationForm((prev) => ({
                                            ...prev,
                                            addRows: form.addRows.filter((_, i) => i !== idx),
                                          }))
                                        }
                                        title="Remove"
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-white/90 text-sm text-right p-1">
                                    <Input
                                      type="number"
                                      placeholder="Amount"
                                      value={row.amount}
                                      onChange={(e) => {
                                        const next = [...form.addRows];
                                        next[idx] = { ...next[idx], amount: e.target.value };
                                        setConfirmationReconciliationForm((prev) => ({ ...prev, addRows: next }));
                                      }}
                                      className="h-8 text-right w-full max-w-[140px] ml-auto bg-black/20 border-white/10 text-white text-sm"
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                              <TableRow className="border-white/10 hover:bg-transparent">
                                <TableCell colSpan={2} className="py-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="text-white/70 hover:text-white hover:bg-white/10 text-xs h-7"
                                    onClick={() =>
                                      setConfirmationReconciliationForm((prev) => ({
                                        ...prev,
                                        addRows: [...prev.addRows, { particulars: "", amount: "" }],
                                      }))
                                    }
                                  >
                                    <Plus className="h-3.5 w-3 mr-1" />
                                    Add row
                                  </Button>
                                </TableCell>
                              </TableRow>
                              {/* Spacer */}
                              <TableRow className="border-white/10 hover:bg-transparent">
                                <TableCell colSpan={2} className="h-2" />
                              </TableRow>
                              {/* Less: */}
                              <TableRow className="border-white/10 hover:bg-transparent">
                                <TableCell colSpan={2} className="text-white/80 font-medium text-sm pb-1">Less:</TableCell>
                              </TableRow>
                              {form.lessRows.map((row, idx) => (
                                <TableRow key={`less-${idx}`} className="border-white/10 hover:bg-white/5">
                                  <TableCell className="text-white/90 text-sm p-1">
                                    <div className="flex items-center gap-1">
                                      <Input
                                        type="text"
                                        placeholder="Particulars"
                                        value={row.particulars}
                                        onChange={(e) => {
                                          const next = [...form.lessRows];
                                          next[idx] = { ...next[idx], particulars: e.target.value };
                                          setConfirmationReconciliationForm((prev) => ({ ...prev, lessRows: next }));
                                        }}
                                        className="h-8 flex-1 bg-black/20 border-white/10 text-white placeholder:text-white/40 text-sm"
                                      />
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-white/50 hover:text-white hover:bg-white/10 shrink-0"
                                        onClick={() =>
                                          setConfirmationReconciliationForm((prev) => ({
                                            ...prev,
                                            lessRows: form.lessRows.filter((_, i) => i !== idx),
                                          }))
                                        }
                                        title="Remove"
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-white/90 text-sm text-right p-1">
                                    <Input
                                      type="number"
                                      placeholder="Amount"
                                      value={row.amount}
                                      onChange={(e) => {
                                        const next = [...form.lessRows];
                                        next[idx] = { ...next[idx], amount: e.target.value };
                                        setConfirmationReconciliationForm((prev) => ({ ...prev, lessRows: next }));
                                      }}
                                      className="h-8 text-right w-full max-w-[140px] ml-auto bg-black/20 border-white/10 text-white text-sm"
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                              <TableRow className="border-white/10 hover:bg-transparent">
                                <TableCell colSpan={2} className="py-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="text-white/70 hover:text-white hover:bg-white/10 text-xs h-7"
                                    onClick={() =>
                                      setConfirmationReconciliationForm((prev) => ({
                                        ...prev,
                                        lessRows: [...prev.lessRows, { particulars: "", amount: "" }],
                                      }))
                                    }
                                  >
                                    <Plus className="h-3.5 w-3 mr-1" />
                                    Add row
                                  </Button>
                                </TableCell>
                              </TableRow>
                              {/* Spacer */}
                              <TableRow className="border-white/10 hover:bg-transparent">
                                <TableCell colSpan={2} className="h-2" />
                              </TableRow>
                              {/* Adjusted amount as per books */}
                              <TableRow className="border-white/10 bg-white/5">
                                <TableCell className="text-white font-medium text-sm">Adjusted amount as per books</TableCell>
                                <TableCell className="text-white font-medium text-sm text-right">
                                  {adjustedAmountAsPerBooks.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                </TableCell>
                              </TableRow>
                              {/* Amount as per Confirmation */}
                              <TableRow className="border-white/10 hover:bg-white/5">
                                <TableCell className="text-white/90 text-sm">Amount as per Confirmation</TableCell>
                                <TableCell className="text-white/90 text-sm text-right p-1">
                                  <Input
                                    type="number"
                                    value={form.amountAsPerConfirmation}
                                    onChange={(e) =>
                                      setConfirmationReconciliationForm((prev) => ({
                                        ...prev,
                                        amountAsPerConfirmation: e.target.value,
                                      }))
                                    }
                                    className="h-8 text-right w-full max-w-[140px] ml-auto bg-black/20 border-white/10 text-white text-sm"
                                  />
                                </TableCell>
                              </TableRow>
                              {/* Difference */}
                              <TableRow className="border-white/10 bg-white/5">
                                <TableCell className="text-white font-medium text-sm">Difference</TableCell>
                                <TableCell className="text-white font-medium text-sm text-right">
                                  {difference.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </div>

                        {/* File link/upload */}
                        <div className="mt-4 space-y-2">
                          <label className="text-xs text-white/60 uppercase tracking-wide">Link or upload file</label>
                          <div className="flex flex-wrap items-center gap-2">
                            {form.file?.fileName && (
                              <>
                                <span className="text-white/80 text-sm truncate max-w-[180px]" title={form.file.fileName}>
                                  {form.file.fileName}
                                </span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-xs text-white/70 hover:text-white"
                                  onClick={() => openAlternateTestingFile(form.file!.fileName)}
                                  title="Open file"
                                >
                                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                                  Open
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-xs text-white/70 hover:text-red-400"
                                  title="De-link file"
                                  disabled={savingConfirmationReconciliation}
                                  onClick={async () => {
                                    const fileName = form.file?.fileName;
                                    if (!fileName) return;
                                    setSavingConfirmationReconciliation(true);
                                    try {
                                      if (workspace?.trim() && (window as any).sharePointAPI?.removeFileSection) {
                                        const sectionToRemove = await getConfirmationSectionForWorkspace(workspace);
                                        if (sectionToRemove) {
                                          await (window as any).sharePointAPI.removeFileSection({
                                            fileName,
                                            sectionToRemove,
                                          });
                                        }
                                      }
                                      setConfirmationReconciliationForm((prev) => ({ ...prev, file: null }));
                                      const updated = JSON.parse(JSON.stringify(confirmationsData));
                                      const cid = confirmationReconciliationDialogId;
                                      if (updated[cid]?.reconciliation) {
                                        updated[cid].reconciliation = { ...updated[cid].reconciliation, file: undefined };
                                        const result = await (window as any).sharePointAPI?.uploadClientJson?.("Confirmation_Response.json", updated);
                                        if (result?.success) setConfirmationsData(updated);
                                      }
                                    } catch (e: any) {
                                      console.error("Failed to de-link and remove section", e);
                                      alert(`Error: ${e?.message || "Unknown error"}`);
                                    } finally {
                                      setSavingConfirmationReconciliation(false);
                                    }
                                  }}
                                >
                                  <X className="h-3.5 w-3.5 mr-1" />
                                  De-link
                                </Button>
                              </>
                            )}
                            <input
                              ref={reconciliationFileInputRef}
                              type="file"
                              accept=".xlsx,.xls"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                e.target.value = "";
                                if (!file || !confirmationReconciliationDialogId) return;
                                try {
                                  const reader = new FileReader();
                                  reader.onload = async () => {
                                    const base64 = (reader.result as string)?.split(",")?.[1] || "";
                                    const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
                                      fileName: file.name,
                                      fileContent: base64,
                                      folderName: "client",
                                      fyYear: getFY_YEAR?.(),
                                    });
                                    if (uploadResult?.success) {
                                      setConfirmationReconciliationForm((prev) => ({ ...prev, file: { fileName: file.name, type: "upload" } }));
                                    } else {
                                      alert(uploadResult?.error || "Upload failed");
                                    }
                                  };
                                  reader.readAsDataURL(file);
                                } catch (err: any) {
                                  alert(err?.message || "Upload failed");
                                }
                              }}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 border-white/20 text-white hover:bg-white/10 text-xs"
                              onClick={() => reconciliationFileInputRef.current?.click()}
                              disabled={savingConfirmationReconciliation}
                            >
                              <Upload className="h-3.5 w-3.5 mr-1" />
                              Upload Excel
                            </Button>
                            <Select
                              value=""
                              onValueChange={(fileName) => {
                                if (!fileName) return;
                                setConfirmationReconciliationForm((prev) => ({ ...prev, file: { fileName, type: "link" } }));
                              }}
                            >
                              <SelectTrigger className="h-8 w-[160px] text-xs">
                                <SelectValue placeholder={loadingClientFiles ? "Loading..." : "Link existing"} />
                              </SelectTrigger>
                              <SelectContent>
                                {clientFolderFiles.map((f) => (
                                  <SelectItem key={f.name} value={f.name} className="text-white focus:bg-white/10 focus:text-white text-xs">
                                    {f.name}
                                  </SelectItem>
                                ))}
                                {!loadingClientFiles && clientFolderFiles.length === 0 && (
                                  <SelectItem value="__none" disabled className="text-white/50 text-xs">
                                    No Excel files in client folder
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <DialogFooter className="gap-2 flex-shrink-0">
                          <Button
                            variant="outline"
                            className="border-white/20 text-white hover:bg-white/10"
                            onClick={() => setConfirmationReconciliationDialogId(null)}
                            disabled={savingConfirmationReconciliation}
                          >
                            Close
                          </Button>
                          <Button
                            className="bg-green-600 text-white hover:bg-green-700"
                            disabled={savingConfirmationReconciliation}
                            onClick={async () => {
                              if (!confirmationReconciliationDialogId) return;
                              setSavingConfirmationReconciliation(true);
                              try {
                                const updated = JSON.parse(JSON.stringify(confirmationsData));
                                const cid = confirmationReconciliationDialogId;
                                if (!updated[cid]) updated[cid] = {};
                                const reconPayload = {
                                  amountAsPerSamplingData: parseFloat(form.amountAsPerSamplingData) || 0,
                                  amountAsPerBooksExtraRows: form.amountAsPerBooksExtraRows.map((r) => ({
                                    particulars: r.particulars,
                                    amount: parseFloat(r.amount) || 0,
                                  })),
                                  addRows: form.addRows.map((r) => ({
                                    particulars: r.particulars,
                                    amount: parseFloat(r.amount) || 0,
                                  })),
                                  lessRows: form.lessRows.map((r) => ({
                                    particulars: r.particulars,
                                    amount: parseFloat(r.amount) || 0,
                                  })),
                                  adjustedAmountAsPerBooks,
                                  amountAsPerConfirmation: amountAsPerConfirmationNum,
                                  difference,
                                  file: form.file || undefined,
                                };
                                updated[cid].reconciliation = reconPayload;
                                const result = await (window as any).sharePointAPI?.uploadClientJson?.("Confirmation_Response.json", updated);
                                if (result?.success) {
                                  setConfirmationsData(updated);
                                  if (form.file && workspace?.trim() && (window as any).sharePointAPI?.updateFileSections) {
                                    const sectionToAdd = await getConfirmationSectionForWorkspace(workspace);
                                    if (sectionToAdd) {
                                      await (window as any).sharePointAPI.updateFileSections({
                                        fileNames: [form.file.fileName],
                                        sectionToAdd,
                                        folderName: "client",
                                      });
                                    }
                                  }
                                  alert("Reconciliation saved successfully.");
                                } else {
                                  alert(`Failed to save: ${result?.error || "Unknown error"}`);
                                }
                              } catch (e: any) {
                                console.error("Failed to save confirmation reconciliation", e);
                                alert(`Error: ${e?.message || "Unknown error"}`);
                              } finally {
                                setSavingConfirmationReconciliation(false);
                              }
                            }}
                          >
                            {savingConfirmationReconciliation ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Saving...
                              </>
                            ) : (
                              "Save"
                            )}
                          </Button>
                        </DialogFooter>
                      </>
                    );
                  })()}
                </DialogContent>
              </Dialog>

              <TabsContent value="domain-testing" className="mt-6">
                <Card className="border-white/10 bg-white/5 text-white">
                  <CardHeader>
                    <CardTitle className="text-base font-semibold text-white">Domain Testing</CardTitle>
                    <p className="text-xs text-white/60 mt-1">
                      Manage domain testing records
                    </p>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-5">
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                        <p className="text-xs text-white/60 mb-3 uppercase tracking-wide">Domain Testing Summary</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs text-white/60">Total records</p>
                            <p className="text-xl font-semibold text-white">{domainSummary.total}</p>
                          </div>
                          <div>
                            <p className="text-xs text-white/60">Active</p>
                            <p className="text-xl font-semibold text-emerald-400">{domainSummary.active}</p>
                          </div>
                          <div>
                            <p className="text-xs text-white/60">Not Found</p>
                            <p className="text-xl font-semibold text-rose-400">{domainSummary.notFound}</p>
                          </div>
                          <div>
                            <p className="text-xs text-white/60">General Domain</p>
                            <p className="text-xl font-semibold text-amber-400">{domainSummary.generalDomain}</p>
                          </div>
                        </div>
                        {domainSummary.others > 0 && (
                          <p className="text-xs text-white/60 mt-3">
                            Other status records: <span className="text-white">{domainSummary.others}</span>
                          </p>
                        )}
                      </div>

                      <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                        <p className="text-xs text-white/60 mb-3 uppercase tracking-wide">Domain Status Split</p>
                        <div className="h-56">
                          {domainSummary.statusPieData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie data={domainSummary.statusPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={78}>
                                  {domainSummary.statusPieData.map((entry) => (
                                    <Cell key={entry.name} fill={entry.color} />
                                  ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                              </PieChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="h-full flex items-center justify-center text-white/50 text-sm">
                              No domain testing data
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="rounded-lg border border-white/10 overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-white/10 hover:bg-white/5">
                            <TableHead className="text-white/80 font-medium">Confirming Party</TableHead>
                            <TableHead className="text-white/80 font-medium">Recipient Email</TableHead>
                            <TableHead className="text-white/80 font-medium">Organization</TableHead>
                            <TableHead className="text-white/80 font-medium">Test Status</TableHead>
                            <TableHead className="text-white/80 font-medium w-[80px]">View</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {domainTestingLoading ? (
                            <TableRow className="border-white/10 hover:bg-white/5">
                              <TableCell
                                colSpan={5}
                                className="text-center py-8 text-white/50 text-sm"
                              >
                                <div className="flex items-center justify-center gap-2">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Loading domain testing data...
                                </div>
                              </TableCell>
                            </TableRow>
                          ) : domainTestingData.length === 0 ? (
                            <TableRow className="border-white/10 hover:bg-white/5">
                              <TableCell
                                colSpan={5}
                                className="text-center py-8 text-white/50 text-sm"
                              >
                                No domain testing records yet. Add records to get started.
                              </TableCell>
                            </TableRow>
                          ) : (
                            domainTestingData.map((row) => {
                              // Determine status styling
                              const status = row.testStatus || "";
                              const isActive = status.toLowerCase() === "active";
                              const isNotFound = status.toLowerCase() === "not found" || status.toLowerCase() === "notfound";
                              const isGeneralDomain = status.toLowerCase() === "general domain" || status.toLowerCase() === "generaldomain";
                              
                              return (
                                <TableRow
                                  key={row.id}
                                  className="border-white/10 hover:bg-white/5"
                                >
                                  <TableCell className="text-white/90">{row.confirmingParty || "-"}</TableCell>
                                  <TableCell className="text-white/80">{row.recipientEmail || "-"}</TableCell>
                                  <TableCell className="text-white/80">{row.organization || "-"}</TableCell>
                                  <TableCell className="text-white/80">
                                    {status ? (
                                      <div className="flex items-center gap-2">
                                        {isActive && (
                                          <Badge variant="outline" className="text-xs bg-green-500/20 text-green-400 border-green-500/30 flex items-center gap-1">
                                            <CheckCircle className="h-3 w-3" />
                                            {status}
                                          </Badge>
                                        )}
                                        {isNotFound && (
                                          <Badge variant="outline" className="text-xs bg-red-500/20 text-red-400 border-red-500/30 flex items-center gap-1">
                                            <XCircle className="h-3 w-3" />
                                            {status}
                                          </Badge>
                                        )}
                                        {(isGeneralDomain || (!isActive && !isNotFound)) && (
                                          <Badge variant="outline" className="text-xs bg-white/10 text-white/60 border-white/20">
                                            {status}
                                          </Badge>
                                        )}
                                      </div>
                                    ) : (
                                      "-"
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10 cursor-pointer"
                                      onClick={() => {
                                        try {
                                          // Find the full record from raw data
                                          const recordIndex = row.index !== undefined ? row.index : (parseInt(row.id.replace("domain-", "")) || 0);
                                          if (domainTestingRawData && domainTestingRawData.length > recordIndex) {
                                            const fullRecord = domainTestingRawData[recordIndex];
                                            if (fullRecord) {
                                              setSelectedDomainRecord(fullRecord);
                                              setIsDomainDetailsDialogOpen(true);
                                            }
                                          }
                                        } catch (error) {
                                          console.error("Error opening domain details:", error);
                                        }
                                      }}
                                      title="View domain details"
                                      data-allow-archival-edit={true}
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="sampling-logs" className="mt-6">
                <Card className="border-white/10 bg-white/5 text-white">
                  <CardHeader>
                    <CardTitle className="text-base font-semibold text-white">Sampling Logs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {samplingLogLoading ? (
                      <div className="flex items-center justify-center gap-2 py-12 text-white/60">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>Loading sampling logs...</span>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex flex-wrap items-center gap-4">
                          <div className="flex flex-col gap-1.5">
                            <label className="text-xs text-white/60 uppercase tracking-wide">Workspace</label>
                            <Select
                              value={selectedSamplingWorkspace}
                              onValueChange={(value) => {
                                setSelectedSamplingWorkspace(value);
                                setSelectedSamplingSet("__all__");
                              }}
                              data-allow-archival-edit={true}
                            >
                              <SelectTrigger className="w-[220px] h-9 placeholder:text-white/40">
                                <SelectValue placeholder="All workspaces" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__all__" className="text-white focus:bg-white/10 focus:text-white">
                                  All workspaces
                                </SelectItem>
                                {Object.keys(samplingLogData).sort().map((workspace) => (
                                  <SelectItem key={workspace} value={workspace} className="text-white focus:bg-white/10 focus:text-white">
                                    {workspace}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <label className="text-xs text-white/60 uppercase tracking-wide">Sample Set</label>
                            <Select
                              value={selectedSamplingSet}
                              onValueChange={setSelectedSamplingSet}
                              disabled={selectedSamplingWorkspace === "__all__"}
                              data-allow-archival-edit={true}
                            >
                              <SelectTrigger className="w-[220px] h-9 placeholder:text-white/40 disabled:opacity-50">
                                <SelectValue placeholder={selectedSamplingWorkspace !== "__all__" ? "All sample sets" : "Select workspace first"} />
                              </SelectTrigger>
                              <SelectContent>
                                {selectedSamplingWorkspace && selectedSamplingWorkspace !== "__all__" && (
                                  <>
                                    <SelectItem value="__all__" className="text-white focus:bg-white/10 focus:text-white">
                                      All sample sets
                                    </SelectItem>
                                    {selectedSamplingWorkspace !== "__all__" && Object.keys(samplingLogData[selectedSamplingWorkspace] || {}).sort().map((setName) => (
                                      <SelectItem key={setName} value={setName} className="text-white focus:bg-white/10 focus:text-white">
                                        {setName}
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {Object.keys(samplingLogData).length > 0 && (() => {
                          // Flatten: all entries from all workspaces and sample sets, with workspace, sampleSet, entryIndex on each row
                          const flattened: { workspace: string; sampleSet: string; entry: any; entryIndex: number }[] = [];
                          Object.entries(samplingLogData).forEach(([workspace, sets]) => {
                            Object.entries(sets || {}).forEach(([sampleSet, entries]) => {
                              (entries || []).forEach((entry: any, entryIndex: number) => {
                                flattened.push({ workspace, sampleSet, entry, entryIndex });
                              });
                            });
                          });
                          // Apply filters
                          const filtered = flattened.filter((row) => {
                            if (selectedSamplingWorkspace !== "__all__" && row.workspace !== selectedSamplingWorkspace) return false;
                            if (selectedSamplingSet !== "__all__" && row.sampleSet !== selectedSamplingSet) return false;
                            return true;
                          });
                          return (
                            <div className="border border-white/10 rounded-lg overflow-x-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow className="border-white/10 hover:bg-transparent">
                                    <TableHead className="text-white/80 font-medium whitespace-nowrap">Workspace</TableHead>
                                    <TableHead className="text-white/80 font-medium whitespace-nowrap">Sample Set</TableHead>
                                    <TableHead className="text-white/80 font-medium whitespace-nowrap">Sampling Type</TableHead>
                                    <TableHead className="text-white/80 font-medium whitespace-nowrap">Date / Time</TableHead>
                                    <TableHead className="text-white/80 font-medium whitespace-nowrap">Assessed Risk</TableHead>
                                    <TableHead className="text-white/80 font-medium whitespace-nowrap">Reliance on Controls</TableHead>
                                    <TableHead className="text-white/80 font-medium w-[80px]">View</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {filtered.length === 0 ? (
                                    <TableRow className="border-white/10 hover:bg-transparent">
                                      <TableCell colSpan={7} className="text-center text-white/50 py-8 text-sm">
                                        No entries match the selected filters.
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    filtered.map((row, idx) => {
                                      const e = row.entry;
                                      return (
                                        <TableRow key={`${row.workspace}-${row.sampleSet}-${idx}`} className="border-white/10 hover:bg-white/5">
                                          <TableCell className="text-white/90 text-sm whitespace-nowrap">{row.workspace}</TableCell>
                                          <TableCell className="text-white/90 text-sm whitespace-nowrap">{row.sampleSet}</TableCell>
                                          <TableCell className="text-white/90 text-sm">{e["sampling type"] ?? "-"}</TableCell>
                                          <TableCell className="text-white/80 text-sm">{e.date_time ?? "-"}</TableCell>
                                          <TableCell className="text-white/80 text-sm">{e.assessed_risk ?? "-"}</TableCell>
                                          <TableCell className="text-white/80 text-sm">{e.reliance_on_controls ?? "-"}</TableCell>
                                          <TableCell>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                                              onClick={() => {
                                                setSelectedSamplingEntry(row);
                                                const recon = row.entry?.reconciliation;
                                                setSelectedNoteLinesForFinancials(Array.isArray(recon?.selected_note_lines) ? recon.selected_note_lines : []);
                                                setSamplingLogReconciliationRows(
                                                  Array.isArray(recon?.reconciliation_rows)
                                                    ? recon.reconciliation_rows.map((r: any) => ({
                                                        particulars: r.particulars ?? "",
                                                        amount: r.amount != null ? String(r.amount) : "",
                                                      }))
                                                    : []
                                                );
                                                loadTrialBalanceMapping();
                                                setIsSamplingEntryDialogOpen(true);
                                              }}
                                              title="View details"
                                              data-allow-archival-edit={true}
                                            >
                                              <Eye className="h-4 w-4" />
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })
                                  )}
                                </TableBody>
                              </Table>
                            </div>
                          );
                        })()}

                        {!samplingLogLoading && Object.keys(samplingLogData).length === 0 && (
                  <div className="text-center text-white/60 py-8 text-sm">
                    No sampling log data found. Add {samplingLogSourceFile} to the juggernaut folder to get started.
                  </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        );

      case "sampling-library":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedLibrary(null)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Libraries
              </Button>
              <div>
                <h2 className="text-xl font-semibold text-white">Sampling Library</h2>
                <p className="text-sm text-white/60">
                  Sampling Logs
                </p>
              </div>
            </div>

            {/* Reuse the Sampling Logs table and dialog UI, driven by samplingLogData */}
            <Card className="border-white/10 bg-white/5 text-white">
              <CardHeader>
                <CardTitle className="text-base font-semibold text-white">
                  Sampling Logs
                </CardTitle>
              </CardHeader>
              <CardContent>
                {samplingLogLoading ? (
                  <div className="flex items-center justify-center gap-2 py-12 text-white/60">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Loading sampling logs...</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs text-white/60 uppercase tracking-wide">Workspace</label>
                        <Select
                          value={selectedSamplingWorkspace}
                          onValueChange={(value) => {
                            setSelectedSamplingWorkspace(value);
                            setSelectedSamplingSet("__all__");
                          }}
                          data-allow-archival-edit={true}
                        >
                          <SelectTrigger className="w-[220px] h-9 placeholder:text-white/40">
                            <SelectValue placeholder="All workspaces" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__all__" className="text-white focus:bg-white/10 focus:text-white">
                              All workspaces
                            </SelectItem>
                            {Object.keys(samplingLogData).sort().map((workspace) => (
                              <SelectItem key={workspace} value={workspace} className="text-white focus:bg-white/10 focus:text-white">
                                {workspace}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs text-white/60 uppercase tracking-wide">Sample Set</label>
                        <Select
                          value={selectedSamplingSet}
                          onValueChange={setSelectedSamplingSet}
                          disabled={selectedSamplingWorkspace === "__all__"}
                          data-allow-archival-edit={true}
                        >
                          <SelectTrigger className="w-[220px] h-9 placeholder:text-white/40 disabled:opacity-50">
                            <SelectValue placeholder={selectedSamplingWorkspace !== "__all__" ? "All sample sets" : "Select workspace first"} />
                          </SelectTrigger>
                          <SelectContent>
                            {selectedSamplingWorkspace && selectedSamplingWorkspace !== "__all__" && (
                              <>
                                <SelectItem value="__all__" className="text-white focus:bg-white/10 focus:text-white">
                                  All sample sets
                                </SelectItem>
                                {selectedSamplingWorkspace !== "__all__" && Object.keys(samplingLogData[selectedSamplingWorkspace] || {}).sort().map((setName) => (
                                  <SelectItem key={setName} value={setName} className="text-white focus:bg-white/10 focus:text-white">
                                    {setName}
                                  </SelectItem>
                                ))}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {Object.keys(samplingLogData).length > 0 && (() => {
                      // Flatten: all entries from all workspaces and sample sets, with workspace, sampleSet, entryIndex on each row
                      const flattened: { workspace: string; sampleSet: string; entry: any; entryIndex: number }[] = [];
                      Object.entries(samplingLogData).forEach(([workspace, sets]) => {
                        Object.entries(sets || {}).forEach(([sampleSet, entries]) => {
                          (entries || []).forEach((entry: any, entryIndex: number) => {
                            flattened.push({ workspace, sampleSet, entry, entryIndex });
                          });
                        });
                      });
                      // Apply filters
                      const filtered = flattened.filter((row) => {
                        if (selectedSamplingWorkspace !== "__all__" && row.workspace !== selectedSamplingWorkspace) return false;
                        if (selectedSamplingSet !== "__all__" && row.sampleSet !== selectedSamplingSet) return false;
                        return true;
                      });
                      return (
                        <div className="border border-white/10 rounded-lg overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="border-white/10 hover:bg-transparent">
                                <TableHead className="text-white/80 font-medium whitespace-nowrap">Workspace</TableHead>
                                <TableHead className="text-white/80 font-medium whitespace-nowrap">Sample Set</TableHead>
                                <TableHead className="text-white/80 font-medium whitespace-nowrap">Sampling Type</TableHead>
                                <TableHead className="text-white/80 font-medium whitespace-nowrap">Date / Time</TableHead>
                                <TableHead className="text-white/80 font-medium whitespace-nowrap">Assessed Risk</TableHead>
                                <TableHead className="text-white/80 font-medium whitespace-nowrap">Reliance on Controls</TableHead>
                                <TableHead className="text-white/80 font-medium w-[80px]">View</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {filtered.length === 0 ? (
                                <TableRow className="border-white/10 hover:bg-transparent">
                                  <TableCell colSpan={7} className="text-center text-white/50 py-8 text-sm">
                                    No entries match the selected filters.
                                  </TableCell>
                                </TableRow>
                              ) : (
                                filtered.map((row, idx) => {
                                  const e = row.entry;
                                  return (
                                    <TableRow key={`${row.workspace}-${row.sampleSet}-${idx}`} className="border-white/10 hover:bg-white/5">
                                      <TableCell className="text-white/90 text-sm whitespace-nowrap">{row.workspace}</TableCell>
                                      <TableCell className="text-white/90 text-sm whitespace-nowrap">{row.sampleSet}</TableCell>
                                      <TableCell className="text-white/90 text-sm">{e["sampling type"] ?? "-"}</TableCell>
                                      <TableCell className="text-white/80 text-sm">{e.date_time ?? "-"}</TableCell>
                                      <TableCell className="text-white/80 text-sm">{e.assessed_risk ?? "-"}</TableCell>
                                      <TableCell className="text-white/80 text-sm">{e.reliance_on_controls ?? "-"}</TableCell>
                                      <TableCell>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                                          onClick={() => {
                                            setSelectedSamplingEntry(row);
                                            const recon = row.entry?.reconciliation;
                                            setSelectedNoteLinesForFinancials(Array.isArray(recon?.selected_note_lines) ? recon.selected_note_lines : []);
                                            setSamplingLogReconciliationRows(
                                              Array.isArray(recon?.reconciliation_rows)
                                                ? recon.reconciliation_rows.map((r: any) => ({
                                                    particulars: r.particulars ?? "",
                                                    amount: r.amount != null ? String(r.amount) : "",
                                                  }))
                                                : []
                                            );
                                            loadTrialBalanceMapping();
                                            setIsSamplingEntryDialogOpen(true);
                                          }}
                                          title="View details"
                                          data-allow-archival-edit={true}
                                        >
                                          <Eye className="h-4 w-4" />
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      );
                    })()}

                    {!samplingLogLoading && Object.keys(samplingLogData).length === 0 && (
                      <div className="text-center text-white/60 py-8 text-sm">
                        No sampling log data found. Add {samplingLogSourceFile} to the juggernaut folder to get started.
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );

      case "misstatement-library":
      case "deficiency-library":
      default:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedLibrary(null)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Libraries
              </Button>
              <div>
                <h2 className="text-xl font-semibold text-white">{library.title}</h2>
                <p className="text-sm text-white/60">{library.description}</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">
                  {selectedLibrary === "deficiency-library" 
                    ? "Deficiencies" 
                    : selectedLibrary === "misstatement-library"
                    ? "Misstatements"
                    : "Files"}
                </h3>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {selectedLibrary === "deficiency-library"
                      ? `${deficiencies.length} item(s)`
                      : selectedLibrary === "misstatement-library"
                      ? `${misstatements.length} item(s)`
                      : `${libraryFiles.length} file(s)`}
                  </Badge>
                  {selectedLibrary === "deficiency-library" && !isArchivalMode && (
                    <>
                      {selectedDeficienciesForSignoff.size > 0 && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={deficiencySignoffLoading}
                              className="border-blue-500/50 text-blue-300 hover:bg-blue-500/10 hover:text-blue-200"
                            >
                              {deficiencySignoffLoading ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <FileCheck className="h-4 w-4 mr-2" />
                              )}
                              Sign off on selected ({selectedDeficienciesForSignoff.size})
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-gray-900 border-white/10 text-white min-w-[200px]" align="end">
                            {(() => {
                              const selected = Array.from(selectedDeficienciesForSignoff);
                              const allHavePrep = selected.length > 0 && selected.every((id) => {
                                const d = deficiencies.find((x) => x.id === id);
                                return d && isDeficiencyPrepCompleted(d.signoff || {});
                              });
                              const canPrep = canControlSignoffSection("prep_so");
                              const canRev = canControlSignoffSection("rev_so") && allHavePrep;
                              const canConc = canControlSignoffSection("conc_so") && allHavePrep;
                              const canEqr = canControlSignoffSection("eqr_so") && allHavePrep;
                              const canPartner = canControlSignoffSection("partner_so") && allHavePrep;
                              return (
                                <>
                                  <DropdownMenuItem disabled={!canPrep} onClick={() => canPrep && handleBulkDeficiencySignoff("prep_so")}>Preparer</DropdownMenuItem>
                                  <DropdownMenuItem disabled={!canRev} onClick={() => canRev && handleBulkDeficiencySignoff("rev_so")}>Reviewer</DropdownMenuItem>
                                  <DropdownMenuItem disabled={!canConc} onClick={() => canConc && handleBulkDeficiencySignoff("conc_so")}>Concurrent</DropdownMenuItem>
                                  <DropdownMenuItem disabled={!canEqr} onClick={() => canEqr && handleBulkDeficiencySignoff("eqr_so")}>EQR</DropdownMenuItem>
                                  <DropdownMenuItem disabled={!canPartner} onClick={() => canPartner && handleBulkDeficiencySignoff("partner_so")}>Partner</DropdownMenuItem>
                                </>
                              );
                            })()}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      <Button
                        size="sm"
                        type="button"
                        onClick={() => setIsDeficiencyFormOpen(true)}
                        className="bg-[#4da3ff] text-white hover:bg-[#4da3ff]/80"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Deficiency
                      </Button>
                      <Button
                        size="sm"
                        type="button"
                        onClick={() => {
                          setSelectedDeficienciesToDelete(new Set());
                          setIsDeleteDeficiencyDialogOpen(true);
                        }}
                        variant="destructive"
                        className="bg-red-600 text-white hover:bg-red-700"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Deficiency
                      </Button>
                    </>
                  )}
                  {selectedLibrary === "misstatement-library" && !isArchivalMode && (
                    <>
                      {selectedMisstatementsForSignoff.size > 0 && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={misstatementSignoffLoading}
                              className="border-blue-500/50 text-blue-300 hover:bg-blue-500/10 hover:text-blue-200"
                            >
                              {misstatementSignoffLoading ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <FileCheck className="h-4 w-4 mr-2" />
                              )}
                              Sign off on selected ({selectedMisstatementsForSignoff.size})
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-gray-900 border-white/10 text-white min-w-[200px]" align="end">
                            {(() => {
                              const selected = Array.from(selectedMisstatementsForSignoff);
                              const allHavePrep = selected.length > 0 && selected.every((id) => {
                                const m = misstatements.find((x) => x.id === id);
                                return m && isMisstatementPrepCompleted(m.signoff || {});
                              });
                              const canPrep = canControlSignoffSection("prep_so");
                              const canRev = canControlSignoffSection("rev_so") && allHavePrep;
                              const canConc = canControlSignoffSection("conc_so") && allHavePrep;
                              const canEqr = canControlSignoffSection("eqr_so") && allHavePrep;
                              const canPartner = canControlSignoffSection("partner_so") && allHavePrep;
                              return (
                                <>
                                  <DropdownMenuItem disabled={!canPrep} onClick={() => canPrep && handleBulkMisstatementSignoff("prep_so")}>Preparer</DropdownMenuItem>
                                  <DropdownMenuItem disabled={!canRev} onClick={() => canRev && handleBulkMisstatementSignoff("rev_so")}>Reviewer</DropdownMenuItem>
                                  <DropdownMenuItem disabled={!canConc} onClick={() => canConc && handleBulkMisstatementSignoff("conc_so")}>Concurrent</DropdownMenuItem>
                                  <DropdownMenuItem disabled={!canEqr} onClick={() => canEqr && handleBulkMisstatementSignoff("eqr_so")}>EQR</DropdownMenuItem>
                                  <DropdownMenuItem disabled={!canPartner} onClick={() => canPartner && handleBulkMisstatementSignoff("partner_so")}>Partner</DropdownMenuItem>
                                </>
                              );
                            })()}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      <Button
                        size="sm"
                        type="button"
                        onClick={() => setIsMisstatementFormOpen(true)}
                        className="bg-[#4da3ff] text-white hover:bg-[#4da3ff]/80"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Misstatement
                      </Button>
                      <Button
                        size="sm"
                        type="button"
                        onClick={() => {
                          setSelectedMisstatementsToDelete(new Set());
                          setIsDeleteMisstatementDialogOpen(true);
                        }}
                        variant="destructive"
                        className="bg-red-600 text-white hover:bg-red-700"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Misstatement
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {selectedLibrary === "misstatement-library" && activeMisstatement ? (
                <MisstatementDetailView
                  misstatementId={activeMisstatement.id}
                  misstatementData={activeMisstatement.data}
                  workspaceName={activeMisstatement.data.workspace || ""}
                  onBack={() => setActiveMisstatement(null)}
                  onSave={async (updatedData: any) => {
                    try {
                      if (!window.sharePointAPI?.updateMisstatement) {
                        throw new Error("SharePoint API for updating misstatements is not available");
                      }
                      const result = await window.sharePointAPI.updateMisstatement({
                        misstatementId: activeMisstatement.id,
                        data: updatedData,
                      });
                      if (!result?.success) {
                        throw new Error(result?.error || "Failed to update misstatement");
                      }
                      // Reload misstatements
                      await loadMisstatements();
                      setActiveMisstatement(null);
                    } catch (error) {
                      console.error("Failed to save misstatement:", error);
                      throw error;
                    }
                  }}
                  mode="edit"
                />
              ) : selectedLibrary === "misstatement-library" ? (
                misstatementsLoading ? (
                  <div className="text-center text-white/60 py-8">
                    Loading misstatements...
                  </div>
                ) : misstatements.length === 0 ? (
                  <Card className="border-white/10 bg-white/5 text-white">
                    <CardContent className="p-6">
                      <div className="text-center text-white/60">
                        <library.icon className="mx-auto h-12 w-12 mb-4 text-white/40" />
                        <p>No misstatements created yet.</p>
                        <p className="text-sm mt-2">
                          Use the &quot;Add Misstatement&quot; button to create a new misstatement.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {!misstatementSignoffLoading && (
                      <div className="flex items-center gap-4 rounded-md border border-white/10 bg-white/5 px-3 py-2">
                        {!isArchivalMode && (
                          <div className="w-10 shrink-0 flex items-center justify-center">
                            <Checkbox
                              checked={misstatements.length > 0 && misstatements.every((m) => selectedMisstatementsForSignoff.has(m.id))}
                              onCheckedChange={() => {
                                if (misstatements.length > 0 && misstatements.every((m) => selectedMisstatementsForSignoff.has(m.id))) {
                                  setSelectedMisstatementsForSignoff(new Set());
                                } else {
                                  setSelectedMisstatementsForSignoff(new Set(misstatements.map((m) => m.id)));
                                }
                              }}
                              aria-label="Select all misstatements"
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0 text-xs font-semibold text-white/70">
                          Misstatement
                        </div>
                        <div className="flex items-center gap-4 flex-shrink-0">
                          <div className="w-24 text-center text-xs font-semibold text-white/70" title="Preparer">Preparer</div>
                          <div className="w-24 text-center text-xs font-semibold text-white/70" title="Reviewer">Reviewer</div>
                          <div className="w-24 text-center text-xs font-semibold text-white/70" title="Concurrent">Concurrent</div>
                          <div className="w-24 text-center text-xs font-semibold text-white/70" title="EQR">EQR</div>
                          <div className="w-24 text-center text-xs font-semibold text-white/70" title="Partner">Partner</div>
                        </div>
                        <div className="w-8 flex-shrink-0" />
                      </div>
                    )}
                    {misstatements.map((mis) => {
                      const signoff = mis.signoff || {
                        prep_so: [],
                        rev_so: [],
                        conc_so: [],
                        eqr_so: [],
                        partner_so: [],
                      };
                      const prepCompleted = isMisstatementPrepCompleted(signoff);
                      const canPrep = canControlSignoffSection("prep_so");
                      const canRev = canControlSignoffSection("rev_so") && prepCompleted;
                      const canConc = canControlSignoffSection("conc_so") && prepCompleted;
                      const canEqr = canControlSignoffSection("eqr_so") && prepCompleted;
                      const canPartner = canControlSignoffSection("partner_so") && prepCompleted;

                      return (
                        <div
                          key={mis.id}
                          className="flex items-center gap-4 rounded-md border border-white/10 bg-white/5 p-3 hover:border-white/20 hover:bg-white/10"
                        >
                          {!isArchivalMode && (
                            <div className="w-10 shrink-0 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedMisstatementsForSignoff.has(mis.id)}
                                onCheckedChange={() => {
                                  setSelectedMisstatementsForSignoff((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(mis.id)) next.delete(mis.id);
                                    else next.add(mis.id);
                                    return next;
                                  });
                                }}
                                aria-label={`Select ${mis.id}`}
                              />
                            </div>
                          )}
                          <button
                            type="button"
                            className="flex-1 min-w-0 text-left"
                            onClick={() => setActiveMisstatement({ id: mis.id, data: mis.data })}
                          >
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-sm font-medium text-white truncate">
                                {mis.id}
                              </span>
                              <Badge variant="outline" className="text-[10px] shrink-0">
                                {mis.type} Misstatement
                              </Badge>
                              <Badge variant="outline" className="text-[10px] shrink-0">
                                Workspace: {mis.workspace || "N/A"}
                              </Badge>
                            </div>
                            <p className="text-xs text-white/50 mt-0.5">
                              Click to view details
                            </p>
                          </button>
                          {!misstatementSignoffLoading && (
                            <div className="flex items-center gap-4 flex-shrink-0">
                              {isArchivalMode ? (
                                <>
                                  <div className="w-24 flex justify-center">
                                    {signoff.prep_so && signoff.prep_so.length > 0 && (
                                      <div className="flex items-center gap-0.5">
                                        {signoff.prep_so.slice(0, 3).map((entry: any, i: number) => (
                                          <div
                                            key={i}
                                            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                            title={entry.metadata}
                                          >
                                            {getInitialsForSignoff(entry.name)}
                                          </div>
                                        ))}
                                        {signoff.prep_so.length > 3 && (
                                          <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                            +{signoff.prep_so.length - 3}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="w-24 flex justify-center">
                                    {signoff.rev_so && signoff.rev_so.length > 0 && (
                                      <div className="flex items-center gap-0.5">
                                        {signoff.rev_so.slice(0, 3).map((entry: any, i: number) => (
                                          <div
                                            key={i}
                                            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                            title={entry.metadata}
                                          >
                                            {getInitialsForSignoff(entry.name)}
                                          </div>
                                        ))}
                                        {signoff.rev_so.length > 3 && (
                                          <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                            +{signoff.rev_so.length - 3}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="w-24 flex justify-center">
                                    {signoff.conc_so && signoff.conc_so.length > 0 && (
                                      <div className="flex items-center gap-0.5">
                                        {signoff.conc_so.slice(0, 3).map((entry: any, i: number) => (
                                          <div
                                            key={i}
                                            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                            title={entry.metadata}
                                          >
                                            {getInitialsForSignoff(entry.name)}
                                          </div>
                                        ))}
                                        {signoff.conc_so.length > 3 && (
                                          <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                            +{signoff.conc_so.length - 3}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="w-24 flex justify-center">
                                    {signoff.eqr_so && signoff.eqr_so.length > 0 && (
                                      <div className="flex items-center gap-0.5">
                                        {signoff.eqr_so.slice(0, 3).map((entry: any, i: number) => (
                                          <div
                                            key={i}
                                            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                            title={entry.metadata}
                                          >
                                            {getInitialsForSignoff(entry.name)}
                                          </div>
                                        ))}
                                        {signoff.eqr_so.length > 3 && (
                                          <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                            +{signoff.eqr_so.length - 3}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="w-24 flex justify-center">
                                    {signoff.partner_so && signoff.partner_so.length > 0 && (
                                      <div className="flex items-center gap-0.5">
                                        {signoff.partner_so.slice(0, 3).map((entry: any, i: number) => (
                                          <div
                                            key={i}
                                            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                            title={entry.metadata}
                                          >
                                            {getInitialsForSignoff(entry.name)}
                                          </div>
                                        ))}
                                        {signoff.partner_so.length > 3 && (
                                          <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                            +{signoff.partner_so.length - 3}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="w-24 flex justify-center">
                                    <ControlSignoffBadge
                                      label="Preparer"
                                      signoffArray={signoff.prep_so || []}
                                      onSignoffClick={() => handleMisstatementSignoff(mis.id, "prep_so")}
                                      disabled={!canPrep}
                                    />
                                  </div>
                                  <div className="w-24 flex justify-center">
                                    <ControlSignoffBadge
                                      label="Reviewer"
                                      signoffArray={signoff.rev_so || []}
                                      onSignoffClick={() => handleMisstatementSignoff(mis.id, "rev_so")}
                                      disabled={!canRev}
                                    />
                                  </div>
                                  <div className="w-24 flex justify-center">
                                    <ControlSignoffBadge
                                      label="Concurrent"
                                      signoffArray={signoff.conc_so || []}
                                      onSignoffClick={() => handleMisstatementSignoff(mis.id, "conc_so")}
                                      disabled={!canConc}
                                    />
                                  </div>
                                  <div className="w-24 flex justify-center">
                                    <ControlSignoffBadge
                                      label="EQR"
                                      signoffArray={signoff.eqr_so || []}
                                      onSignoffClick={() => handleMisstatementSignoff(mis.id, "eqr_so")}
                                      disabled={!canEqr}
                                    />
                                  </div>
                                  <div className="w-24 flex justify-center">
                                    <ControlSignoffBadge
                                      label="Partner"
                                      signoffArray={signoff.partner_so || []}
                                      onSignoffClick={() => handleMisstatementSignoff(mis.id, "partner_so")}
                                      disabled={!canPartner}
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                          <div className="w-8 flex-shrink-0" />
                        </div>
                      );
                    })}
                  </div>
                )
              ) : selectedLibrary === "deficiency-library" && activeDeficiencyId ? (
                <DeficiencyDetailView
                  deficiencyId={activeDeficiencyId}
                  onBack={() => setActiveDeficiencyId(null)}
                />
              ) : selectedLibrary === "deficiency-library" ? (
                deficienciesLoading ? (
                  <div className="text-center text-white/60 py-8">
                    Loading deficiencies...
                  </div>
                ) : deficiencies.length === 0 ? (
                  <Card className="border-white/10 bg-white/5 text-white">
                    <CardContent className="p-6">
                      <div className="text-center text-white/60">
                        <library.icon className="mx-auto h-12 w-12 mb-4 text-white/40" />
                        <p>No deficiencies created yet.</p>
                        <p className="text-sm mt-2">
                          Use the &quot;Add Deficiency&quot; button to create a new deficiency.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {!deficiencySignoffLoading && (
                      <div className="flex items-center gap-4 rounded-md border border-white/10 bg-white/5 px-3 py-2">
                        {!isArchivalMode && (
                          <div className="w-10 shrink-0 flex items-center justify-center">
                            <Checkbox
                              checked={deficiencies.length > 0 && deficiencies.every((d) => selectedDeficienciesForSignoff.has(d.id))}
                              onCheckedChange={() => {
                                if (deficiencies.length > 0 && deficiencies.every((d) => selectedDeficienciesForSignoff.has(d.id))) {
                                  setSelectedDeficienciesForSignoff(new Set());
                                } else {
                                  setSelectedDeficienciesForSignoff(new Set(deficiencies.map((d) => d.id)));
                                }
                              }}
                              aria-label="Select all deficiencies"
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0 text-xs font-semibold text-white/70">
                          Deficiency
                        </div>
                        <div className="flex items-center gap-4 flex-shrink-0">
                          <div className="w-24 text-center text-xs font-semibold text-white/70" title="Preparer">Preparer</div>
                          <div className="w-24 text-center text-xs font-semibold text-white/70" title="Reviewer">Reviewer</div>
                          <div className="w-24 text-center text-xs font-semibold text-white/70" title="Concurrent">Concurrent</div>
                          <div className="w-24 text-center text-xs font-semibold text-white/70" title="EQR">EQR</div>
                          <div className="w-24 text-center text-xs font-semibold text-white/70" title="Partner">Partner</div>
                        </div>
                        <div className="w-8 flex-shrink-0" />
                      </div>
                    )}
                    {deficiencies.map((def) => {
                      const signoff = def.signoff || {
                        prep_so: [],
                        rev_so: [],
                        conc_so: [],
                        eqr_so: [],
                        partner_so: [],
                      };
                      const prepCompleted = isDeficiencyPrepCompleted(signoff);
                      const canPrep = canControlSignoffSection("prep_so");
                      const canRev = canControlSignoffSection("rev_so") && prepCompleted;
                      const canConc = canControlSignoffSection("conc_so") && prepCompleted;
                      const canEqr = canControlSignoffSection("eqr_so") && prepCompleted;
                      const canPartner = canControlSignoffSection("partner_so") && prepCompleted;

                      return (
                        <div
                          key={def.id}
                          className="flex items-center gap-4 rounded-md border border-white/10 bg-white/5 p-3 hover:border-white/20 hover:bg-white/10"
                        >
                          {!isArchivalMode && (
                            <div className="w-10 shrink-0 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedDeficienciesForSignoff.has(def.id)}
                                onCheckedChange={() => {
                                  setSelectedDeficienciesForSignoff((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(def.id)) next.delete(def.id);
                                    else next.add(def.id);
                                    return next;
                                  });
                                }}
                                aria-label={`Select ${def.id}`}
                              />
                            </div>
                          )}
                          <button
                            type="button"
                            className="flex-1 min-w-0 text-left"
                            onClick={() => setActiveDeficiencyId(def.id)}
                          >
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-sm font-medium text-white truncate">
                                {def.id}
                              </span>
                              <Badge variant="outline" className="text-[10px] shrink-0">
                                {def.deficiency_type || "Deficiency"}
                              </Badge>
                              <Badge variant="outline" className="text-[10px] shrink-0">
                                Relevant: {def.control_relevance || "N/A"}
                              </Badge>
                            </div>
                            {def.control_id && def.control_id.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {def.control_id.slice(0, 3).map((cid) => (
                                  <Badge key={cid} variant="outline" className="text-[9px]">
                                    {cid}
                                  </Badge>
                                ))}
                                {def.control_id.length > 3 && (
                                  <span className="text-[9px] text-white/50">+{def.control_id.length - 3}</span>
                                )}
                              </div>
                            )}
                            {def.description && (
                              <p className="text-xs text-white/80 truncate mt-0.5 line-clamp-2">
                                <span dangerouslySetInnerHTML={{ __html: def.description }} />
                              </p>
                            )}
                          </button>
                          {!deficiencySignoffLoading && (
                            <div className="flex items-center gap-4 flex-shrink-0">
                              {isArchivalMode ? (
                                <>
                                  <div className="w-24 flex justify-center">
                                    {signoff.prep_so && signoff.prep_so.length > 0 && (
                                      <div className="flex items-center gap-0.5">
                                        {signoff.prep_so.slice(0, 3).map((entry: any, i: number) => (
                                          <div
                                            key={i}
                                            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                            title={entry.metadata}
                                          >
                                            {getInitialsForSignoff(entry.name)}
                                          </div>
                                        ))}
                                        {signoff.prep_so.length > 3 && (
                                          <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                            +{signoff.prep_so.length - 3}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="w-24 flex justify-center">
                                    {signoff.rev_so && signoff.rev_so.length > 0 && (
                                      <div className="flex items-center gap-0.5">
                                        {signoff.rev_so.slice(0, 3).map((entry: any, i: number) => (
                                          <div
                                            key={i}
                                            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                            title={entry.metadata}
                                          >
                                            {getInitialsForSignoff(entry.name)}
                                          </div>
                                        ))}
                                        {signoff.rev_so.length > 3 && (
                                          <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                            +{signoff.rev_so.length - 3}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="w-24 flex justify-center">
                                    {signoff.conc_so && signoff.conc_so.length > 0 && (
                                      <div className="flex items-center gap-0.5">
                                        {signoff.conc_so.slice(0, 3).map((entry: any, i: number) => (
                                          <div
                                            key={i}
                                            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                            title={entry.metadata}
                                          >
                                            {getInitialsForSignoff(entry.name)}
                                          </div>
                                        ))}
                                        {signoff.conc_so.length > 3 && (
                                          <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                            +{signoff.conc_so.length - 3}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="w-24 flex justify-center">
                                    {signoff.eqr_so && signoff.eqr_so.length > 0 && (
                                      <div className="flex items-center gap-0.5">
                                        {signoff.eqr_so.slice(0, 3).map((entry: any, i: number) => (
                                          <div
                                            key={i}
                                            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                            title={entry.metadata}
                                          >
                                            {getInitialsForSignoff(entry.name)}
                                          </div>
                                        ))}
                                        {signoff.eqr_so.length > 3 && (
                                          <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                            +{signoff.eqr_so.length - 3}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="w-24 flex justify-center">
                                    {signoff.partner_so && signoff.partner_so.length > 0 && (
                                      <div className="flex items-center gap-0.5">
                                        {signoff.partner_so.slice(0, 3).map((entry: any, i: number) => (
                                          <div
                                            key={i}
                                            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                            title={entry.metadata}
                                          >
                                            {getInitialsForSignoff(entry.name)}
                                          </div>
                                        ))}
                                        {signoff.partner_so.length > 3 && (
                                          <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                            +{signoff.partner_so.length - 3}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="w-24 flex justify-center">
                                    <ControlSignoffBadge
                                      label="Preparer"
                                      signoffArray={signoff.prep_so || []}
                                      onSignoffClick={() => handleDeficiencySignoff(def.id, "prep_so")}
                                      disabled={!canPrep}
                                    />
                                  </div>
                                  <div className="w-24 flex justify-center">
                                    <ControlSignoffBadge
                                      label="Reviewer"
                                      signoffArray={signoff.rev_so || []}
                                      onSignoffClick={() => handleDeficiencySignoff(def.id, "rev_so")}
                                      disabled={!canRev}
                                    />
                                  </div>
                                  <div className="w-24 flex justify-center">
                                    <ControlSignoffBadge
                                      label="Concurrent"
                                      signoffArray={signoff.conc_so || []}
                                      onSignoffClick={() => handleDeficiencySignoff(def.id, "conc_so")}
                                      disabled={!canConc}
                                    />
                                  </div>
                                  <div className="w-24 flex justify-center">
                                    <ControlSignoffBadge
                                      label="EQR"
                                      signoffArray={signoff.eqr_so || []}
                                      onSignoffClick={() => handleDeficiencySignoff(def.id, "eqr_so")}
                                      disabled={!canEqr}
                                    />
                                  </div>
                                  <div className="w-24 flex justify-center">
                                    <ControlSignoffBadge
                                      label="Partner"
                                      signoffArray={signoff.partner_so || []}
                                      onSignoffClick={() => handleDeficiencySignoff(def.id, "partner_so")}
                                      disabled={!canPartner}
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                          <div className="w-8 flex-shrink-0" />
                        </div>
                      );
                    })}
                  </div>
                )
              ) : filesLoading ? (
                <div className="text-center text-white/60 py-8">
                  Loading files...
                </div>
              ) : libraryFiles.length === 0 ? (
                <Card className="border-white/10 bg-white/5 text-white">
                  <CardContent className="p-6">
                    <div className="text-center text-white/60">
                      <library.icon className="mx-auto h-12 w-12 mb-4 text-white/40" />
                      <p>No files in this library yet.</p>
                      <p className="text-sm mt-2">Use the "Add" button to upload files to this library.</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {libraryFiles.map((file) => (
                    <Card
                      key={file.id}
                      className="cursor-pointer border-white/10 bg-white/5 text-white transition-all hover:border-white/20 hover:bg-white/10"
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="text-2xl">
                            {fileManager.getFileIcon(file.extension)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-white truncate" title={file.fileName}>
                              {file.fileName}
                            </h4>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                {fileManager.formatFileSize(file.size)}
                              </Badge>
                              <span className="text-xs text-white/50">
                                {file.uploadDate.toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case "information-library":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedLibrary(null)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Libraries
              </Button>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-white">Information Library</h2>
                <p className="text-sm text-white/60">
                  Manage information resources and documentation
                </p>
              </div>
              {!isArchivalMode && (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => setIsInformationFormOpen(true)}
                    className="bg-blue-600 text-white hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Information
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedInformationToDelete(new Set());
                      setIsDeleteInformationDialogOpen(true);
                    }}
                    variant="destructive"
                    className="bg-red-600 text-white hover:bg-red-700"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">Information Entries</h3>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {informationEntries.length} entry(ies)
                  </Badge>
                  {!isArchivalMode && selectedInformationForSignoff.size > 0 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={informationSignoffLoading}
                          className="border-blue-500/50 text-blue-300 hover:bg-blue-500/10 hover:text-blue-200"
                        >
                          {informationSignoffLoading ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <FileCheck className="h-4 w-4 mr-2" />
                          )}
                          Sign off on selected ({selectedInformationForSignoff.size})
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-gray-900 border-white/10 text-white min-w-[200px]" align="end">
                        {(() => {
                          const selected = Array.from(selectedInformationForSignoff);
                          const allHavePrep = selected.length > 0 && selected.every((name) => {
                            const e = informationEntries.find((x) => (x.information_name || "") === name);
                            return e && isInformationPrepCompleted(e.signoff || {});
                          });
                          const canPrep = canControlSignoffSection("prep_so");
                          const canRev = canControlSignoffSection("rev_so") && allHavePrep;
                          const canConc = canControlSignoffSection("conc_so") && allHavePrep;
                          const canEqr = canControlSignoffSection("eqr_so") && allHavePrep;
                          const canPartner = canControlSignoffSection("partner_so") && allHavePrep;
                          return (
                            <>
                              <DropdownMenuItem disabled={!canPrep} onClick={() => canPrep && handleBulkInformationSignoff("prep_so")}>Preparer</DropdownMenuItem>
                              <DropdownMenuItem disabled={!canRev} onClick={() => canRev && handleBulkInformationSignoff("rev_so")}>Reviewer</DropdownMenuItem>
                              <DropdownMenuItem disabled={!canConc} onClick={() => canConc && handleBulkInformationSignoff("conc_so")}>Concurrent</DropdownMenuItem>
                              <DropdownMenuItem disabled={!canEqr} onClick={() => canEqr && handleBulkInformationSignoff("eqr_so")}>EQR</DropdownMenuItem>
                              <DropdownMenuItem disabled={!canPartner} onClick={() => canPartner && handleBulkInformationSignoff("partner_so")}>Partner</DropdownMenuItem>
                            </>
                          );
                        })()}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>

              {informationLoading ? (
                <div className="text-center text-white/60 py-8">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                  Loading information...
                </div>
              ) : informationEntries.length === 0 ? (
                <Card className="border-white/10 bg-white/5 text-white">
                  <CardContent className="p-6">
                    <div className="text-center text-white/60">
                      <BookOpen className="mx-auto h-12 w-12 mb-4 text-white/40" />
                      <p>No information entries found.</p>
                      {!isArchivalMode && (
                        <p className="text-sm mt-2">
                          Use the &quot;Add Information&quot; button to create a new entry.
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {!informationSignoffLoading && (
                    <div className="flex items-center gap-4 rounded-md border border-white/10 bg-white/5 px-3 py-2">
                      {!isArchivalMode && (
                        <div className="w-10 shrink-0 flex items-center justify-center">
                          <Checkbox
                            checked={informationEntries.length > 0 && informationEntries.every((e) => selectedInformationForSignoff.has(e.information_name || ""))}
                            onCheckedChange={() => {
                              if (informationEntries.length > 0 && informationEntries.every((e) => selectedInformationForSignoff.has(e.information_name || ""))) {
                                setSelectedInformationForSignoff(new Set());
                              } else {
                                setSelectedInformationForSignoff(new Set(informationEntries.map((e) => e.information_name || "")));
                              }
                            }}
                            aria-label="Select all information entries"
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0 text-xs font-semibold text-white/70">
                        Information
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <div className="w-24 text-center text-xs font-semibold text-white/70" title="Preparer">Preparer</div>
                        <div className="w-24 text-center text-xs font-semibold text-white/70" title="Reviewer">Reviewer</div>
                        <div className="w-24 text-center text-xs font-semibold text-white/70" title="Concurrent">Concurrent</div>
                        <div className="w-24 text-center text-xs font-semibold text-white/70" title="EQR">EQR</div>
                        <div className="w-24 text-center text-xs font-semibold text-white/70" title="Partner">Partner</div>
                      </div>
                      <div className="w-8 flex-shrink-0" />
                    </div>
                  )}
                  {informationEntries.map((entry, index) => {
                    const signoff = entry.signoff || {
                      prep_so: [],
                      rev_so: [],
                      conc_so: [],
                      eqr_so: [],
                      partner_so: [],
                    };
                    const prepCompleted = isInformationPrepCompleted(signoff);
                    const canPrep = canControlSignoffSection("prep_so");
                    const canRev = canControlSignoffSection("rev_so") && prepCompleted;
                    const canConc = canControlSignoffSection("conc_so") && prepCompleted;
                    const canEqr = canControlSignoffSection("eqr_so") && prepCompleted;
                    const canPartner = canControlSignoffSection("partner_so") && prepCompleted;

                    return (
                      <div
                        key={index}
                        className="flex items-center gap-4 rounded-md border border-white/10 bg-white/5 p-3 hover:border-white/20 hover:bg-white/10"
                      >
                        {!isArchivalMode && (
                          <div className="w-10 shrink-0 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedInformationForSignoff.has(entry.information_name || "")}
                              onCheckedChange={() => {
                                setSelectedInformationForSignoff((prev) => {
                                  const next = new Set(prev);
                                  const name = entry.information_name || "";
                                  if (next.has(name)) next.delete(name);
                                  else next.add(name);
                                  return next;
                                });
                              }}
                              aria-label={`Select ${entry.information_name || "Unnamed"}`}
                            />
                          </div>
                        )}
                        <button
                          type="button"
                          className="flex-1 min-w-0 text-left"
                          onClick={() => setSelectedInformation(entry.information_name)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-white mb-1 flex items-center gap-2">
                                {entry.information_name || "Unnamed"}
                                <ChevronRight className="h-4 w-4 text-white/60" />
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {entry.workspace_prefix && entry.workspace_prefix.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {entry.workspace_prefix.map((prefix: string, idx: number) => (
                                      <Badge key={idx} variant="outline" className="text-xs bg-blue-500/20 border-blue-500/30">
                                        {prefix}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            {entry.file_name && (
                              <Badge variant="outline" className="text-xs text-green-400 border-green-500/30">
                                <FileText className="h-3 w-3 mr-1" />
                                {entry.file_name}
                              </Badge>
                            )}
                          </div>
                        </button>
                        {!informationSignoffLoading && (
                          <div className="flex items-center gap-4 flex-shrink-0">
                            {isArchivalMode ? (
                              <>
                                <div className="w-24 flex justify-center">
                                  {signoff.prep_so && signoff.prep_so.length > 0 && (
                                    <div className="flex items-center gap-0.5">
                                      {signoff.prep_so.slice(0, 3).map((entrySo: any, i: number) => (
                                        <div
                                          key={i}
                                          className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                          title={entrySo.metadata}
                                        >
                                          {getInitialsForSignoff(entrySo.name)}
                                        </div>
                                      ))}
                                      {signoff.prep_so.length > 3 && (
                                        <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                          +{signoff.prep_so.length - 3}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="w-24 flex justify-center">
                                  {signoff.rev_so && signoff.rev_so.length > 0 && (
                                    <div className="flex items-center gap-0.5">
                                      {signoff.rev_so.slice(0, 3).map((entrySo: any, i: number) => (
                                        <div
                                          key={i}
                                          className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                          title={entrySo.metadata}
                                        >
                                          {getInitialsForSignoff(entrySo.name)}
                                        </div>
                                      ))}
                                      {signoff.rev_so.length > 3 && (
                                        <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                          +{signoff.rev_so.length - 3}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="w-24 flex justify-center">
                                  {signoff.conc_so && signoff.conc_so.length > 0 && (
                                    <div className="flex items-center gap-0.5">
                                      {signoff.conc_so.slice(0, 3).map((entrySo: any, i: number) => (
                                        <div
                                          key={i}
                                          className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                          title={entrySo.metadata}
                                        >
                                          {getInitialsForSignoff(entrySo.name)}
                                        </div>
                                      ))}
                                      {signoff.conc_so.length > 3 && (
                                        <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                          +{signoff.conc_so.length - 3}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="w-24 flex justify-center">
                                  {signoff.eqr_so && signoff.eqr_so.length > 0 && (
                                    <div className="flex items-center gap-0.5">
                                      {signoff.eqr_so.slice(0, 3).map((entrySo: any, i: number) => (
                                        <div
                                          key={i}
                                          className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                          title={entrySo.metadata}
                                        >
                                          {getInitialsForSignoff(entrySo.name)}
                                        </div>
                                      ))}
                                      {signoff.eqr_so.length > 3 && (
                                        <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                          +{signoff.eqr_so.length - 3}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="w-24 flex justify-center">
                                  {signoff.partner_so && signoff.partner_so.length > 0 && (
                                    <div className="flex items-center gap-0.5">
                                      {signoff.partner_so.slice(0, 3).map((entrySo: any, i: number) => (
                                        <div
                                          key={i}
                                          className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium border border-blue-500/30"
                                          title={entrySo.metadata}
                                        >
                                          {getInitialsForSignoff(entrySo.name)}
                                        </div>
                                      ))}
                                      {signoff.partner_so.length > 3 && (
                                        <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                                          +{signoff.partner_so.length - 3}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="w-24 flex justify-center">
                                  <ControlSignoffBadge
                                    label="Preparer"
                                    signoffArray={signoff.prep_so || []}
                                    onSignoffClick={() => handleInformationSignoff(entry.information_name, "prep_so")}
                                    disabled={!canPrep}
                                  />
                                </div>
                                <div className="w-24 flex justify-center">
                                  <ControlSignoffBadge
                                    label="Reviewer"
                                    signoffArray={signoff.rev_so || []}
                                    onSignoffClick={() => handleInformationSignoff(entry.information_name, "rev_so")}
                                    disabled={!canRev}
                                  />
                                </div>
                                <div className="w-24 flex justify-center">
                                  <ControlSignoffBadge
                                    label="Concurrent"
                                    signoffArray={signoff.conc_so || []}
                                    onSignoffClick={() => handleInformationSignoff(entry.information_name, "conc_so")}
                                    disabled={!canConc}
                                  />
                                </div>
                                <div className="w-24 flex justify-center">
                                  <ControlSignoffBadge
                                    label="EQR"
                                    signoffArray={signoff.eqr_so || []}
                                    onSignoffClick={() => handleInformationSignoff(entry.information_name, "eqr_so")}
                                    disabled={!canEqr}
                                  />
                                </div>
                                <div className="w-24 flex justify-center">
                                  <ControlSignoffBadge
                                    label="Partner"
                                    signoffArray={signoff.partner_so || []}
                                    onSignoffClick={() => handleInformationSignoff(entry.information_name, "partner_so")}
                                    disabled={!canPartner}
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        <div className="w-8 flex-shrink-0" />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
    }
  };

  return (
    <>
      {selectedInformation ? (
        <InformationDetailView
          informationName={selectedInformation}
          onBack={() => setSelectedInformation(null)}
        />
      ) : viewingControl ? (
        <InternalControlView
          controlId={viewingControl.controlId}
          controlName={viewingControl.controlName}
          activeSection={viewingControl.activeSection}
          onSectionChange={(section) => {
            setViewingControl({ ...viewingControl, activeSection: section });
            // Update activeSection to sync with LeftPanel
            if (setActiveSection) {
              setActiveSection(`internal-control:${viewingControl.controlId}:${section}`);
            } else if ((window as any).setActiveSection) {
              (window as any).setActiveSection(`internal-control:${viewingControl.controlId}:${section}`);
            }
          }}
          onClose={() => {
            setViewingControl(null);
            // Detect where we came from - if from execution/planning workspace controls, go back there
            // Otherwise go to control-library
            const referrer = sessionStorage.getItem('control-view-referrer');
            const targetSection = referrer || "control-library";
            sessionStorage.removeItem('control-view-referrer');
            
            if (setActiveSection) {
              setActiveSection(targetSection);
            } else if ((window as any).setActiveSection) {
              (window as any).setActiveSection(targetSection);
            }
          }}
        />
      ) : selectedLibrary ? (
        renderLibraryContent()
      ) : (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">Libraries</h2>
              <p className="text-sm text-white/60">
                Access various libraries and resources
              </p>
            </div>
            {onBack && (
              <Button variant="outline" onClick={onBack}>
                Back
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {visibleLibraryItems.map((library) => {
              const Icon = library.icon;
              const isDisabled = library.disabled;
              return (
                <Card
                  key={library.id}
                  className={`group border-white/10 bg-white/5 text-white transition-all ${
                    isDisabled
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer hover:border-white/20 hover:bg-white/10"
                  }`}
                  onClick={() => !isDisabled && handleLibrarySelect(library.id)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className={`rounded-lg p-2 bg-gradient-to-br ${library.accent}`}>
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      {isDisabled ? (
                        <Badge variant="outline" className="text-xs text-white/40 border-white/20">
                          Coming Soon
                        </Badge>
                      ) : (
                        <ChevronRight className="h-4 w-4 text-white/40 transition-transform group-hover:translate-x-1" />
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 text-left">
                    <CardTitle className="text-sm font-medium mb-1 text-left">
                      {library.title}
                    </CardTitle>
                    <p className="text-xs text-white/60 text-left">
                      {library.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Old modals removed - using new InternalControlView instead */}

      {/* Control Owner Form Modal - Always rendered so it appears on top */}
      <ControlOwnerFormModal
        isOpen={isControlOwnerFormOpen}
        onClose={() => {
          setIsControlOwnerFormOpen(false);
          setEditingOwner(null); // Clear editing state when closing
        }}
        onSave={() => {
          loadControlOwners();
        }}
        existingOwner={editingOwner ? {
          id: editingOwner.id,
          name: editingOwner.data.name,
          designation: editingOwner.data.designation,
          emailid: editingOwner.data.emailid,
          authorityCompetence: editingOwner.data.authorityCompetence,
        } : undefined}
      />

      {/* Deficiency Form Modal */}
      <DeficiencyFormModal
        isOpen={isDeficiencyFormOpen}
        onClose={() => setIsDeficiencyFormOpen(false)}
        onCreated={(deficiencyId) => {
          console.log("Deficiency created:", deficiencyId);
          // Reload deficiencies list after creating a new one
          loadDeficiencies();
        }}
      />

      {/* Delete Deficiency Dialog */}
      <Dialog open={isDeleteDeficiencyDialogOpen} onOpenChange={setIsDeleteDeficiencyDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] bg-black/90 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Delete Deficiency</DialogTitle>
            <DialogDescription className="text-white/60">
              Select deficiencies to delete.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2 max-h-[50vh] overflow-y-auto border border-white/10 rounded-md p-4">
              {deficienciesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-white/60" />
                </div>
              ) : deficiencies.length === 0 ? (
                <div className="text-center py-8 text-white/60 text-sm">
                  No deficiencies found.
                </div>
              ) : (
                deficiencies.map((def) => {
                  const isSelected = selectedDeficienciesToDelete.has(def.id);
                  return (
                    <div
                      key={def.id}
                      className={`flex items-center justify-between p-3 rounded-md border transition-colors cursor-pointer ${
                        isSelected
                          ? "border-red-500/50 bg-red-500/10"
                          : "border-white/10 bg-black/20 hover:bg-white/5"
                      }`}
                      onClick={() => {
                        const newSet = new Set(selectedDeficienciesToDelete);
                        if (isSelected) {
                          newSet.delete(def.id);
                        } else {
                          newSet.add(def.id);
                        }
                        setSelectedDeficienciesToDelete(newSet);
                      }}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            const newSet = new Set(selectedDeficienciesToDelete);
                            if (isSelected) {
                              newSet.delete(def.id);
                            } else {
                              newSet.add(def.id);
                            }
                            setSelectedDeficienciesToDelete(newSet);
                          }}
                          className="w-4 h-4 rounded border-white/20 bg-black/40 text-red-500 focus:ring-red-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {def.id}
                            </Badge>
                            <span className="text-sm font-medium text-white">
                              {def.deficiency_type || "Deficiency"}
                            </span>
                          </div>
                          {def.control_id && def.control_id.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {def.control_id.map((cid) => (
                                <Badge key={cid} variant="outline" className="text-[10px]">
                                  {cid}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDeficiencyDialogOpen(false);
                setSelectedDeficienciesToDelete(new Set());
              }}
              disabled={deletingDeficiencies}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (selectedDeficienciesToDelete.size === 0) {
                  alert("Please select at least one deficiency to delete");
                  return;
                }

                try {
                  setDeletingDeficiencies(true);
                  const deficiencyIds = Array.from(selectedDeficienciesToDelete);
                  
                  const res = await (window as any).sharePointAPI?.deleteDeficiencies?.({
                    deficiencyIds: deficiencyIds,
                  });

                  if (res?.success) {
                    await loadDeficiencies();
                    setIsDeleteDeficiencyDialogOpen(false);
                    setSelectedDeficienciesToDelete(new Set());
                  } else {
                    alert(`Failed to delete deficiencies: ${res?.error || "Unknown error"}`);
                  }
                } catch (e: any) {
                  console.error("Failed to delete deficiencies", e);
                  alert(`Error deleting deficiencies: ${e?.message || "Unknown error"}`);
                } finally {
                  setDeletingDeficiencies(false);
                }
              }}
              disabled={deletingDeficiencies || selectedDeficienciesToDelete.size === 0}
              variant="destructive"
            >
              {deletingDeficiencies ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Selected"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete RoMM Dialog */}
      <Dialog open={isDeleteRommDialogOpen} onOpenChange={setIsDeleteRommDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] bg-black/90 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Delete Custom RoMM</DialogTitle>
            <DialogDescription className="text-white/60">
              Select custom RoMMs to delete.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2 max-h-[50vh] overflow-y-auto border border-white/10 rounded-md p-4">
              {risksLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-white/60" />
                </div>
              ) : risks.filter((r: any) => r.id && r.id.includes('.CR.')).length === 0 ? (
                <div className="text-center py-8 text-white/60 text-sm">
                  No custom RoMMs found.
                </div>
              ) : (
                risks
                  .filter((r: any) => r.id && r.id.includes('.CR.'))
                  .map((risk: any) => {
                    const isSelected = selectedRommsToDelete.has(risk.id);
                    return (
                      <div
                        key={risk.id}
                        className={`flex items-center justify-between p-3 rounded-md border transition-colors cursor-pointer ${
                          isSelected
                            ? "border-red-500/50 bg-red-500/10"
                            : "border-white/10 bg-black/20 hover:bg-white/5"
                        }`}
                        onClick={() => {
                          const newSet = new Set(selectedRommsToDelete);
                          if (isSelected) {
                            newSet.delete(risk.id);
                          } else {
                            newSet.add(risk.id);
                          }
                          setSelectedRommsToDelete(newSet);
                        }}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              const newSet = new Set(selectedRommsToDelete);
                              if (isSelected) {
                                newSet.delete(risk.id);
                              } else {
                                newSet.add(risk.id);
                              }
                              setSelectedRommsToDelete(newSet);
                            }}
                            className="w-4 h-4 rounded border-white/20 bg-black/40 text-red-500 focus:ring-red-500"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {risk.id}
                              </Badge>
                              {risk.workspace && (
                                <Badge variant="outline" className="text-xs">
                                  {risk.workspace}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-white/80 mt-1">
                              {risk.risk}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteRommDialogOpen(false);
                setSelectedRommsToDelete(new Set());
              }}
              disabled={deletingRomms}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (selectedRommsToDelete.size === 0) {
                  alert("Please select at least one custom RoMM to delete");
                  return;
                }

                try {
                  setDeletingRomms(true);
                  const rommIds = Array.from(selectedRommsToDelete);
                  
                  const res = await (window as any).sharePointAPI?.deleteRomms?.({
                    rommIds: rommIds,
                  });

                  if (res?.success) {
                    await loadRisks();
                    setIsDeleteRommDialogOpen(false);
                    setSelectedRommsToDelete(new Set());
                  } else {
                    alert(`Failed to delete RoMMs: ${res?.error || "Unknown error"}`);
                  }
                } catch (e: any) {
                  console.error("Failed to delete RoMMs", e);
                  alert(`Error deleting RoMMs: ${e?.message || "Unknown error"}`);
                } finally {
                  setDeletingRomms(false);
                }
              }}
              disabled={deletingRomms || selectedRommsToDelete.size === 0}
              variant="destructive"
            >
              {deletingRomms ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Selected"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Control Dialog */}
      <Dialog open={isDeleteControlDialogOpen} onOpenChange={setIsDeleteControlDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] bg-black/90 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Delete Control</DialogTitle>
            <DialogDescription className="text-white/60">
              Select controls to delete.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2 max-h-[50vh] overflow-y-auto border border-white/10 rounded-md p-4">
              {controlsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-white/60" />
                </div>
              ) : controls.length === 0 ? (
                <div className="text-center py-8 text-white/60 text-sm">
                  No controls found.
                </div>
              ) : (
                controls.map((control) => {
                  const isSelected = selectedControlsToDelete.has(control.controlId);
                  return (
                    <div
                      key={control.controlId}
                      className={`flex items-center justify-between p-3 rounded-md border transition-colors cursor-pointer ${
                        isSelected
                          ? "border-red-500/50 bg-red-500/10"
                          : "border-white/10 bg-black/20 hover:bg-white/5"
                      }`}
                      onClick={() => {
                        const newSet = new Set(selectedControlsToDelete);
                        if (isSelected) {
                          newSet.delete(control.controlId);
                        } else {
                          newSet.add(control.controlId);
                        }
                        setSelectedControlsToDelete(newSet);
                      }}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            const newSet = new Set(selectedControlsToDelete);
                            if (isSelected) {
                              newSet.delete(control.controlId);
                            } else {
                              newSet.add(control.controlId);
                            }
                            setSelectedControlsToDelete(newSet);
                          }}
                          className="w-4 h-4 rounded border-white/20 bg-black/40 text-red-500 focus:ring-red-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {control.controlId}
                            </Badge>
                            <span className="text-sm font-medium text-white">
                              {control.controlName || control.controlId}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-[10px]">
                              {control.typeOfControl}
                            </Badge>
                            <Badge variant="outline" className="text-[10px]">
                              {control.nature}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteControlDialogOpen(false);
                setSelectedControlsToDelete(new Set());
              }}
              disabled={deletingControls}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (selectedControlsToDelete.size === 0) {
                  alert("Please select at least one control to delete");
                  return;
                }

                try {
                  setDeletingControls(true);
                  const controlIds = Array.from(selectedControlsToDelete);
                  
                  const res = await (window as any).sharePointAPI?.deleteControls?.({
                    controlIds: controlIds,
                  });

                  if (res?.success) {
                    await loadControls();
                    setIsDeleteControlDialogOpen(false);
                    setSelectedControlsToDelete(new Set());
                  } else {
                    alert(`Failed to delete controls: ${res?.error || "Unknown error"}`);
                  }
                } catch (e: any) {
                  console.error("Failed to delete controls", e);
                  alert(`Error deleting controls: ${e?.message || "Unknown error"}`);
                } finally {
                  setDeletingControls(false);
                }
              }}
              disabled={deletingControls || selectedControlsToDelete.size === 0}
              variant="destructive"
            >
              {deletingControls ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Selected"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Control Owner Dialog */}
      <Dialog open={isDeleteControlOwnerDialogOpen} onOpenChange={setIsDeleteControlOwnerDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] bg-black/90 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Delete Control Owner</DialogTitle>
            <DialogDescription className="text-white/60">
              Select control owners to delete.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2 max-h-[50vh] overflow-y-auto border border-white/10 rounded-md p-4">
              {controlOwnersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-white/60" />
                </div>
              ) : Object.keys(controlOwners).length === 0 ? (
                <div className="text-center py-8 text-white/60 text-sm">
                  No control owners found.
                </div>
              ) : (
                Object.entries(controlOwners).map(([id, owner]: [string, any]) => {
                  const isSelected = selectedControlOwnersToDelete.has(id);
                  return (
                    <div
                      key={id}
                      className={`flex items-center justify-between p-3 rounded-md border transition-colors cursor-pointer ${
                        isSelected
                          ? "border-red-500/50 bg-red-500/10"
                          : "border-white/10 bg-black/20 hover:bg-white/5"
                      }`}
                      onClick={() => {
                        const newSet = new Set(selectedControlOwnersToDelete);
                        if (isSelected) {
                          newSet.delete(id);
                        } else {
                          newSet.add(id);
                        }
                        setSelectedControlOwnersToDelete(newSet);
                      }}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            const newSet = new Set(selectedControlOwnersToDelete);
                            if (isSelected) {
                              newSet.delete(id);
                            } else {
                              newSet.add(id);
                            }
                            setSelectedControlOwnersToDelete(newSet);
                          }}
                          className="w-4 h-4 rounded border-white/20 bg-black/40 text-red-500 focus:ring-red-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {id}
                            </Badge>
                            <span className="text-sm font-medium text-white">
                              {owner.name || "Unnamed Owner"}
                            </span>
                          </div>
                          {owner.designation && (
                            <div className="mt-1">
                              <Badge variant="outline" className="text-[10px]">
                                {owner.designation}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteControlOwnerDialogOpen(false);
                setSelectedControlOwnersToDelete(new Set());
              }}
              disabled={deletingControlOwners}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (selectedControlOwnersToDelete.size === 0) {
                  alert("Please select at least one control owner to delete");
                  return;
                }

                try {
                  setDeletingControlOwners(true);
                  const ownerIds = Array.from(selectedControlOwnersToDelete);
                  
                  const res = await (window as any).sharePointAPI?.deleteControlOwners?.({
                    ownerIds: ownerIds,
                  });

                  if (res?.success) {
                    await loadControlOwners();
                    setIsDeleteControlOwnerDialogOpen(false);
                    setSelectedControlOwnersToDelete(new Set());
                  } else {
                    alert(`Failed to delete control owners: ${res?.error || "Unknown error"}`);
                  }
                } catch (e: any) {
                  console.error("Failed to delete control owners", e);
                  alert(`Error deleting control owners: ${e?.message || "Unknown error"}`);
                } finally {
                  setDeletingControlOwners(false);
                }
              }}
              disabled={deletingControlOwners || selectedControlOwnersToDelete.size === 0}
              variant="destructive"
            >
              {deletingControlOwners ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Selected"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Information Dialog */}
      <Dialog open={isDeleteInformationDialogOpen} onOpenChange={setIsDeleteInformationDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] bg-black/90 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Delete Information</DialogTitle>
            <DialogDescription className="text-white/60">
              Select information entries to delete. They will be moved to the recycle bin (rbin folder).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2 max-h-[50vh] overflow-y-auto border border-white/10 rounded-md p-4">
              {informationLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-white/60" />
                </div>
              ) : informationEntries.length === 0 ? (
                <div className="text-center py-8 text-white/60 text-sm">
                  No information entries found.
                </div>
              ) : (
                informationEntries.map((entry) => {
                  const name = entry.information_name || "";
                  const isSelected = selectedInformationToDelete.has(name);
                  return (
                    <div
                      key={name}
                      className={`flex items-center justify-between p-3 rounded-md border transition-colors cursor-pointer ${
                        isSelected
                          ? "border-red-500/50 bg-red-500/10"
                          : "border-white/10 bg-black/20 hover:bg-white/5"
                      }`}
                      onClick={() => {
                        const newSet = new Set(selectedInformationToDelete);
                        if (isSelected) {
                          newSet.delete(name);
                        } else {
                          newSet.add(name);
                        }
                        setSelectedInformationToDelete(newSet);
                      }}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            const newSet = new Set(selectedInformationToDelete);
                            if (isSelected) {
                              newSet.delete(name);
                            } else {
                              newSet.add(name);
                            }
                            setSelectedInformationToDelete(newSet);
                          }}
                          className="w-4 h-4 rounded border-white/20 bg-black/40 text-red-500 focus:ring-red-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-white">
                              {name || "Unnamed"}
                            </span>
                          </div>
                          {entry.workspace_prefix && entry.workspace_prefix.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {entry.workspace_prefix.slice(0, 3).map((prefix: string, idx: number) => (
                                <Badge key={idx} variant="outline" className="text-[10px]">
                                  {prefix}
                                </Badge>
                              ))}
                              {entry.workspace_prefix.length > 3 && (
                                <Badge variant="outline" className="text-[10px]">+{entry.workspace_prefix.length - 3}</Badge>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteInformationDialogOpen(false);
                setSelectedInformationToDelete(new Set());
              }}
              disabled={deletingInformation}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (selectedInformationToDelete.size === 0) {
                  alert("Please select at least one information entry to delete");
                  return;
                }
                try {
                  setDeletingInformation(true);
                  const entryNames = Array.from(selectedInformationToDelete);
                  const res = await (window as any).sharePointAPI?.deleteInformationEntries?.({
                    entryNames,
                  });
                  if (res?.success) {
                    await loadInformationEntries();
                    setIsDeleteInformationDialogOpen(false);
                    setSelectedInformationToDelete(new Set());
                  } else {
                    alert(`Failed to delete information entries: ${res?.error || "Unknown error"}`);
                  }
                } catch (e: any) {
                  console.error("Failed to delete information entries", e);
                  alert(`Error deleting information entries: ${e?.message || "Unknown error"}`);
                } finally {
                  setDeletingInformation(false);
                }
              }}
              disabled={deletingInformation || selectedInformationToDelete.size === 0}
              variant="destructive"
            >
              {deletingInformation ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Selected"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Misstatement Form Modal */}
      <MisstatementFormModal
        isOpen={isMisstatementFormOpen}
        onClose={() => setIsMisstatementFormOpen(false)}
        onCreated={(misstatementId) => {
          console.log("Misstatement created:", misstatementId);
          // Reload misstatements list after creating a new one
          loadMisstatements();
        }}
      />

      {/* Information Library Form Modal */}
      <InformationLibraryFormModal
        isOpen={isInformationFormOpen}
        onClose={() => setIsInformationFormOpen(false)}
        onCreated={() => {
          console.log("Information created");
          // Reload information entries after creating a new one
          loadInformationEntries();
        }}
      />

      {/* Delete Misstatement Dialog */}
      <Dialog open={isDeleteMisstatementDialogOpen} onOpenChange={setIsDeleteMisstatementDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] bg-black/90 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Delete Misstatement</DialogTitle>
            <DialogDescription className="text-white/60">
              Select misstatements to delete.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2 max-h-[50vh] overflow-y-auto border border-white/10 rounded-md p-4">
              {misstatementsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-white/60" />
                </div>
              ) : misstatements.length === 0 ? (
                <div className="text-center py-8 text-white/60 text-sm">
                  No misstatements found.
                </div>
              ) : (
                misstatements.map((mis) => {
                  const isSelected = selectedMisstatementsToDelete.has(mis.id);
                  return (
                    <div
                      key={mis.id}
                      className={`flex items-center justify-between p-3 rounded-md border transition-colors cursor-pointer ${
                        isSelected
                          ? "border-red-500/50 bg-red-500/10"
                          : "border-white/10 bg-black/20 hover:bg-white/5"
                      }`}
                      onClick={() => {
                        const newSet = new Set(selectedMisstatementsToDelete);
                        if (isSelected) {
                          newSet.delete(mis.id);
                        } else {
                          newSet.add(mis.id);
                        }
                        setSelectedMisstatementsToDelete(newSet);
                      }}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            const newSet = new Set(selectedMisstatementsToDelete);
                            if (isSelected) {
                              newSet.delete(mis.id);
                            } else {
                              newSet.add(mis.id);
                            }
                            setSelectedMisstatementsToDelete(newSet);
                          }}
                          className="w-4 h-4 rounded border-white/20 bg-black/40 text-red-500 focus:ring-red-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {mis.id}
                            </Badge>
                            <span className="text-sm font-medium text-white">
                              {mis.type} Misstatement
                            </span>
                          </div>
                          {mis.workspace && (
                            <div className="mt-1">
                              <Badge variant="outline" className="text-[10px]">
                                Workspace: {mis.workspace}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteMisstatementDialogOpen(false);
                setSelectedMisstatementsToDelete(new Set());
              }}
              disabled={deletingMisstatements}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (selectedMisstatementsToDelete.size === 0) {
                  alert("Please select at least one misstatement to delete");
                  return;
                }

                try {
                  setDeletingMisstatements(true);
                  const misstatementIds = Array.from(selectedMisstatementsToDelete);
                  
                  const res = await (window as any).sharePointAPI?.deleteMisstatements?.({
                    misstatementIds: misstatementIds,
                  });

                  if (res?.success) {
                    await loadMisstatements();
                    setIsDeleteMisstatementDialogOpen(false);
                    setSelectedMisstatementsToDelete(new Set());
                  } else {
                    alert(`Failed to delete misstatements: ${res?.error || "Unknown error"}`);
                  }
                } catch (e: any) {
                  console.error("Failed to delete misstatements", e);
                  alert(`Error deleting misstatements: ${e?.message || "Unknown error"}`);
                } finally {
                  setDeletingMisstatements(false);
                }
              }}
              disabled={deletingMisstatements || selectedMisstatementsToDelete.size === 0}
              variant="destructive"
            >
              {deletingMisstatements ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Selected"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Domain Test Results Dialog */}
      {selectedDomainRecord && (
        <Dialog open={isDomainDetailsDialogOpen} onOpenChange={(open) => {
          setIsDomainDetailsDialogOpen(open);
          if (!open) {
            setSelectedDomainRecord(null);
          }
        }}>
          <DialogContent className="sm:max-w-2xl bg-black/90 border-white/10 text-white max-h-[90vh] overflow-y-auto">
            <DialogHeader className="pb-4">
              <DialogTitle className="text-xl font-semibold text-white">
                Domain Test Results
              </DialogTitle>
              <DialogDescription className="text-sm text-white/60 mt-1">
                Domain verification details for {selectedDomainRecord?.confirming_party_info?.email_address || "N/A"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Status Banner - Only show for Active status */}
              {(() => {
                const status = selectedDomainRecord.domain_info?.status || "";
                const isActive = status.toLowerCase() === "active";
                
                if (isActive) {
                  return (
                    <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 flex items-start gap-3">
                      <div className="bg-green-500 rounded-full p-1.5 flex-shrink-0 mt-0.5">
                        <CheckCircle className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-green-400 mb-1">Domain Test Passed</div>
                        <div className="text-sm text-green-300/80">
                          The domain has been successfully verified and is valid for confirmation requests.
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Domain Information Section */}
              <div className="border border-white/10 rounded-lg p-4 bg-white/5">
                <h3 className="text-base font-semibold text-white mb-4">Domain Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-white/60 block mb-1.5 uppercase tracking-wide">Domain</label>
                    <div className="text-sm font-medium text-white">
                      {selectedDomainRecord.domain_info?.domain || "-"}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-white/60 block mb-1.5 uppercase tracking-wide">Status</label>
                    <div className="text-sm font-medium">
                      {(() => {
                        const status = selectedDomainRecord.domain_info?.status || "";
                        const isActive = status.toLowerCase() === "active";
                        return (
                          <span className={isActive ? "text-green-400" : "text-white"}>
                            {status || "-"}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-white/60 block mb-1.5 uppercase tracking-wide">Creation Date</label>
                    <div className="text-sm font-medium text-white">
                      {selectedDomainRecord.domain_info?.creation_date || "-"}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-white/60 block mb-1.5 uppercase tracking-wide">Expiry Date</label>
                    <div className="text-sm font-medium text-white">
                      {selectedDomainRecord.domain_info?.expiry_date || "-"}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-white/60 block mb-1.5 uppercase tracking-wide">Registrar</label>
                    <div className="text-sm font-medium text-white">
                      {selectedDomainRecord.domain_info?.registrar || "-"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button
                onClick={() => {
                  setIsDomainDetailsDialogOpen(false);
                  setSelectedDomainRecord(null);
                }}
                className="bg-[#4da3ff] text-white hover:bg-[#4da3ff]/80"
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Sampling Log Entry Details Dialog */}
      {selectedSamplingEntry && (
        <Dialog open={isSamplingEntryDialogOpen} onOpenChange={(open) => {
          setIsSamplingEntryDialogOpen(open);
          if (!open) {
            setSelectedSamplingEntry(null);
            setSelectedNoteLinesForFinancials([]);
            setSamplingLogReconciliationRows([]);
          }
        }}>
          <DialogContent className="sm:max-w-2xl bg-black/90 border-white/10 text-white max-h-[90vh] overflow-y-auto">
            <DialogHeader className="pb-4">
              <DialogTitle className="text-xl font-semibold text-white">
                Sampling Log Details
              </DialogTitle>
              <DialogDescription className="text-sm text-white/60 mt-1">
                {selectedSamplingEntry.workspace} / {selectedSamplingEntry.sampleSet}
              </DialogDescription>
            </DialogHeader>
            <div className="border border-white/10 rounded-lg p-4 bg-white/5">
              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                {[
                  { label: "Workspace", value: selectedSamplingEntry.workspace },
                  { label: "Sample Set", value: selectedSamplingEntry.sampleSet },
                  { label: "Sampling Type", value: selectedSamplingEntry.entry["sampling type"] },
                  { label: "Date / Time", value: selectedSamplingEntry.entry.date_time },
                  { label: "Performance Materiality", value: selectedSamplingEntry.entry.performance_materiality != null ? Number(selectedSamplingEntry.entry.performance_materiality).toLocaleString() : "" },
                  { label: "Assessed Risk", value: selectedSamplingEntry.entry.assessed_risk },
                  { label: "Reliance on Controls", value: selectedSamplingEntry.entry.reliance_on_controls },
                  { label: "Amount Column", value: selectedSamplingEntry.entry.amount_column },
                  { label: "Type of Items", value: selectedSamplingEntry.entry.type_of_items },
                  { label: "Total Amount", value: selectedSamplingEntry.entry.total_amount != null ? Number(selectedSamplingEntry.entry.total_amount).toLocaleString(undefined, { maximumFractionDigits: 2 }) : "" },
                  { label: "Net Population Subject to Sampling", value: selectedSamplingEntry.entry.net_population_subject_to_sampling },
                  { label: "Number of Samples", value: selectedSamplingEntry.entry.number_of_samples },
                  { label: "Seed", value: selectedSamplingEntry.entry.seed },
                  { label: "High Value Samples", value: selectedSamplingEntry.entry.high_value_samples },
                ].map(({ label, value }) => (
                  <div key={label} className="flex flex-col gap-1 text-sm">
                    <span className="text-white/60 text-xs uppercase tracking-wide">{label}</span>
                    <span className="text-white">{value ?? "-"}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Linked file (if any) */}
            <div className="mt-4">
              <span className="text-white/70 text-sm font-medium">
                File Linked:&nbsp;
              </span>
              {selectedSamplingEntry.entry?.file_name ? (
                <Button
                  variant="link"
                  className="text-blue-400 hover:text-blue-300 p-0 h-auto align-baseline"
                  onClick={() => handleOpenSamplingFile(selectedSamplingEntry.entry.file_name)}
                >
                  <ExternalLink className="h-4 w-4 mr-1 inline-block align-text-bottom" />
                  {selectedSamplingEntry.entry.file_name}
                </Button>
              ) : (
                <span className="text-white/50 text-sm">No file linked</span>
              )}
            </div>

            {/* Population Reconciliation table */}
            <div className="mt-6">
              <h3 className="text-base font-semibold text-white mb-3">Population Reconciliation</h3>
              {(() => {
                const populationAsPerLog = Number(selectedSamplingEntry.entry?.net_population_subject_to_sampling) || 0;
                const populationAsPerFinancials = computePopulationAsPerFinancials();
                const difference = populationAsPerLog - populationAsPerFinancials;
                const reconciliationSum = samplingLogReconciliationRows.reduce(
                  (sum, row) => sum + (parseFloat(row.amount) || 0),
                  0
                );
                const netDifference = difference - reconciliationSum;
                return (
                  <div className="border border-white/10 rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10 hover:bg-transparent">
                          <TableHead className="text-white/80 font-medium">Particulars</TableHead>
                          <TableHead className="text-white/80 font-medium text-right w-[140px]">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow className="border-white/10 hover:bg-white/5">
                          <TableCell className="text-white/90 text-sm">Population as per Log</TableCell>
                          <TableCell className="text-white/90 text-sm text-right">
                            {populationAsPerLog.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                          </TableCell>
                        </TableRow>
                        <TableRow className="border-white/10 hover:bg-white/5">
                          <TableCell className="text-white/90 text-sm align-top">
                            <div className="space-y-2">
                              <span className="block">Population as per Financials</span>
                              <MultiSelectDropdown
                                options={uniqueNoteLines}
                                value={selectedNoteLinesForFinancials}
                                onChange={setSelectedNoteLinesForFinancials}
                                placeholder="Select note line(s)"
                                className="max-w-full"
                                loading={trialBalanceMappingLoading}
                              />
                            </div>
                          </TableCell>
                          <TableCell className="text-white/90 text-sm text-right align-top pt-2">
                            {populationAsPerFinancials.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                          </TableCell>
                        </TableRow>
                        <TableRow className="border-white/10 hover:bg-white/5 bg-white/5">
                          <TableCell className="text-white font-semibold text-sm">Difference</TableCell>
                          <TableCell className="text-white font-semibold text-sm text-right">
                            {difference.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                          </TableCell>
                        </TableRow>
                        <TableRow className="border-white/10 hover:bg-transparent">
                          <TableCell colSpan={2} className="text-white/60 text-xs uppercase tracking-wide pt-2 pb-1">
                            Reconciliation
                          </TableCell>
                        </TableRow>
                        {samplingLogReconciliationRows.map((row, idx) => (
                          <TableRow key={idx} className="border-white/10 hover:bg-white/5">
                            <TableCell className="text-white/90 text-sm p-1">
                              <div className="flex items-center gap-1">
                                <Input
                                  type="text"
                                  placeholder="Explanation"
                                  value={row.particulars}
                                  onChange={(e) => {
                                    const next = [...samplingLogReconciliationRows];
                                    next[idx] = { ...next[idx], particulars: e.target.value };
                                    setSamplingLogReconciliationRows(next);
                                  }}
                                  className="h-8 flex-1 bg-black/20 border-white/10 text-white placeholder:text-white/40 text-sm"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-white/50 hover:text-white hover:bg-white/10 shrink-0"
                                  onClick={() => setSamplingLogReconciliationRows(samplingLogReconciliationRows.filter((_, i) => i !== idx))}
                                  title="Remove row"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                            <TableCell className="text-white/90 text-sm text-right p-1 w-[140px]">
                              <Input
                                type="number"
                                placeholder="Amount"
                                value={row.amount}
                                onChange={(e) => {
                                  const next = [...samplingLogReconciliationRows];
                                  next[idx] = { ...next[idx], amount: e.target.value };
                                  setSamplingLogReconciliationRows(next);
                                }}
                                className="h-8 text-right bg-black/20 border-white/10 text-white placeholder:text-white/40 text-sm"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="border-white/10 hover:bg-transparent">
                          <TableCell className="py-1" colSpan={2}>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-white/70 hover:text-white hover:bg-white/10 text-xs h-7"
                              onClick={() => setSamplingLogReconciliationRows([...samplingLogReconciliationRows, { particulars: "", amount: "" }])}
                            >
                              <Plus className="h-3.5 w-3 mr-1" />
                              Add reconciliation row
                            </Button>
                          </TableCell>
                        </TableRow>
                        <TableRow className="border-white/10 hover:bg-white/5 bg-white/5">
                          <TableCell className="text-white font-semibold text-sm">Net Difference</TableCell>
                          <TableCell className="text-white font-semibold text-sm text-right">
                            {netDifference.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                );
              })()}
            </div>

            <DialogFooter className="mt-6 flex gap-2 justify-end">
              <Button
                onClick={() => {
                  setIsSamplingEntryDialogOpen(false);
                  setSelectedSamplingEntry(null);
                  setSelectedNoteLinesForFinancials([]);
                  setSamplingLogReconciliationRows([]);
                }}
                className="bg-[#4da3ff] text-white hover:bg-[#4da3ff]/80"
                disabled={savingReconciliation}
              >
                Close
              </Button>
              <Button
                onClick={async () => {
                  if (!selectedSamplingEntry) return;
                  const { workspace, sampleSet, entry, entryIndex } = selectedSamplingEntry;
                  const populationAsPerLog = Number(entry?.net_population_subject_to_sampling) || 0;
                  const populationAsPerFinancials = computePopulationAsPerFinancials();
                  const difference = populationAsPerLog - populationAsPerFinancials;
                  const reconciliationSum = samplingLogReconciliationRows.reduce(
                    (sum, row) => sum + (parseFloat(row.amount) || 0),
                    0
                  );
                  const netDifference = difference - reconciliationSum;
                  const reconciliation = {
                    selected_note_lines: selectedNoteLinesForFinancials,
                    population_as_per_log: populationAsPerLog,
                    population_as_per_financials: populationAsPerFinancials,
                    difference,
                    reconciliation_rows: samplingLogReconciliationRows.map((row) => ({
                      particulars: row.particulars,
                      amount: parseFloat(row.amount) || 0,
                    })),
                    net_difference: netDifference,
                  };
                  setSavingReconciliation(true);
                  try {
                    const updatedData = JSON.parse(JSON.stringify(samplingLogData));
                    if (
                      updatedData[workspace] &&
                      Array.isArray(updatedData[workspace][sampleSet]) &&
                      updatedData[workspace][sampleSet][entryIndex] != null
                    ) {
                      updatedData[workspace][sampleSet][entryIndex].reconciliation = reconciliation;
                    }
                    const result = await (window as any).sharePointAPI?.uploadClientJson?.(samplingLogSourceFile, updatedData);
                    if (result?.success) {
                      setSamplingLogData(updatedData);
                      alert("Population reconciliation saved successfully.");
                    } else {
                      alert(`Failed to save: ${result?.error || "Unknown error"}`);
                    }
                  } catch (e: any) {
                    console.error("Failed to save reconciliation", e);
                    alert(`Error saving: ${e?.message || "Unknown error"}`);
                  } finally {
                    setSavingReconciliation(false);
                  }
                }}
                disabled={savingReconciliation}
                className="bg-green-600 text-white hover:bg-green-700"
              >
                {savingReconciliation ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Confirmation Details View Dialog */}
      {viewingConfirmationId && (
        <Dialog open={!!viewingConfirmationId} onOpenChange={(open) => !open && setViewingConfirmationId(null)}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto overflow-x-hidden border-white/10 bg-slate-900 text-white">
            <DialogHeader>
              <DialogTitle className="text-white">Confirmation Details - {viewingConfirmationId}</DialogTitle>
              <DialogDescription className="text-white/60">
                View complete confirmation information, response, and activity log
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4 overflow-x-hidden">
              {(() => {
                const confirmationData = confirmationsData[viewingConfirmationId];
                if (!confirmationData) {
                  return <div className="text-center py-8 text-white/60">Loading confirmation data...</div>;
                }

                const partydetails = confirmationData.partydetails || {};
                const status = confirmationData.status || "submitted";
                const confirmedAt = confirmationData.confirmedAt || confirmationData.confirmed_at || "";
                const confirmedBy = confirmationData.confirmedBy || confirmationData.confirmed_by || "";
                const confirmedIP = confirmationData.confirmedIP || confirmationData.confirmedIP || confirmationData.confirmed_ip || "";
                // Try to get sent date from activity log or confirmation data
                let sentDate = confirmationData.sentDate || confirmationData.sent_date || confirmationData.sentAt || confirmationData.sent_at || "";
                const remarks = confirmationData.templateDetails?.remarks || "";
                const attachments = confirmationData.templateDetails?.attachments || [];
                
                // If sentDate not found, try to get it from activity log
                if (!sentDate) {
                  const sentLogEntry = (confirmationsActivityLog.timeline || [])
                    .find((entry: any) => 
                      entry.letter_id === viewingConfirmationId && 
                      (entry.stage === "Send to Confirming Party" || entry.action?.includes("Sent to"))
                    );
                  if (sentLogEntry) {
                    sentDate = sentLogEntry.timestamp || "";
                  }
                }

                // Get activity log for this confirmation ID
                const activityLogEntries = (confirmationsActivityLog.timeline || [])
                  .filter((entry: any) => entry.letter_id === viewingConfirmationId)
                  .map((entry: any) => ({
                    timestamp: entry.timestamp || "",
                    stage: entry.stage || "",
                    action: entry.action || "",
                    performedBy: entry.performed_by || "",
                    details: entry.details || "",
                    status: (entry.status || "completed") as "completed" | "in-progress" | "pending",
                  }));

                return (
                  <>
                    {/* Status Badge */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-white">Status:</span>
                      {status === "confirmed" || status === "submitted" ? (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Confirmed
                        </Badge>
                      ) : (
                        <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                          <XCircle className="h-3 w-3 mr-1" />
                          Not Confirmed
                        </Badge>
                      )}
                    </div>

                    {/* Confirmation Metadata */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div className="break-words">
                        <p className="text-white/60">Area</p>
                        <p className="font-medium text-white break-words">{partydetails.area || "-"}</p>
                      </div>
                      <div className="break-words">
                        <p className="text-white/60">Confirming Party</p>
                        <p className="font-medium text-white break-words">{partydetails.ConfirmingParty || "-"}</p>
                      </div>
                      <div className="break-words">
                        <p className="text-white/60">Recipient Name</p>
                        <p className="font-medium text-white break-words">{confirmationData.recipientName || "-"}</p>
                      </div>
                      <div className="break-words">
                        <p className="text-white/60">Recipient Email</p>
                        <p className="font-medium text-white break-words break-all">{confirmationData.recipientEmail || "-"}</p>
                      </div>
                      <div className="break-words">
                        <p className="text-white/60">Organization</p>
                        <p className="font-medium text-white break-words">{partydetails.ConfirmingParty || "-"}</p>
                      </div>
                      {sentDate && (
                        <div className="break-words">
                          <p className="text-white/60">Sent Date</p>
                          <p className="font-medium text-white break-words">{formatDateTime(sentDate)}</p>
                        </div>
                      )}
                      {confirmedAt && (
                        <div className="break-words">
                          <p className="text-white/60">Confirmed Date</p>
                          <p className="font-medium text-white break-words">{formatDateTime(confirmedAt)}</p>
                        </div>
                      )}
                      {confirmedBy && (
                        <div className="break-words">
                          <p className="text-white/60">Confirmed By</p>
                          <p className="font-medium text-white break-words">{confirmedBy}</p>
                        </div>
                      )}
                      {confirmedIP && (
                        <div className="break-words">
                          <p className="text-white/60">IP Address</p>
                          <p className="font-medium text-white break-words">{confirmedIP}</p>
                        </div>
                      )}
                    </div>

                    {/* Confirmation Form Template Section */}
                    <div className="pt-4 border-t border-white/10">
                      <h3 className="text-lg font-semibold mb-4 text-white">Confirmation Form Template & Response</h3>
                      <Card className="border-white/10 bg-white/5">
                        <CardContent className="pt-6">
                          <ConfirmationFormView confirmationData={confirmationData} />
                        </CardContent>
                      </Card>
                    </div>

                    {/* Remarks from Confirming Party */}
                    {remarks && (
                      <div className="pt-4 border-t border-white/10">
                        <p className="text-sm font-medium mb-2 text-white">Remarks from Confirming Party</p>
                        <div className="bg-white/5 p-3 rounded-md border border-white/10">
                          <p className="text-sm text-white">{remarks}</p>
                        </div>
                      </div>
                    )}

                    {/* Attachments from Confirming Party */}
                    {attachments && attachments.length > 0 ? (
                      <div className="pt-4 border-t border-white/10">
                        <p className="text-sm font-medium mb-2 text-white">Attachments from Confirming Party</p>
                        <div className="space-y-2">
                          {attachments.map((file: any, index: number) => {
                            const fileName = typeof file === "string" ? file : (file.name || file.originalFileName || "");
                            return (
                              <div 
                                key={index} 
                                className="flex items-center gap-2 p-2 bg-white/5 rounded-md border border-white/10 hover:bg-white/10 cursor-pointer transition-colors"
                                onClick={() => handleOpenConfirmationAttachment(fileName)}
                                title={`Click to open ${fileName} in SharePoint`}
                              >
                                <FileText className="h-4 w-4 text-white/70 flex-shrink-0" />
                                <span className="text-sm text-white flex-1 hover:text-blue-300 hover:underline break-words">{fileName}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="pt-4 border-t border-white/10">
                        <p className="text-sm font-medium mb-2 text-white">Attachments from Confirming Party</p>
                        <p className="text-sm text-white/60">No attachments provided</p>
                      </div>
                    )}

                    {/* Activity Log Section */}
                    <div className="pt-4 border-t border-white/10">
                      <h3 className="text-lg font-semibold mb-4 text-white">Activity Log</h3>
                      <div className="rounded-md border border-white/10 overflow-hidden">
                        <Table className="table-fixed">
                          <TableHeader>
                            <TableRow className="border-white/10">
                              <TableHead className="text-white/80 text-xs whitespace-normal w-[100px]">Timestamp</TableHead>
                              <TableHead className="text-white/80 text-xs whitespace-normal w-[100px]">Stage</TableHead>
                              <TableHead className="text-white/80 text-xs whitespace-normal w-[100px]">Action</TableHead>
                              <TableHead className="text-white/80 text-xs whitespace-normal w-[90px]">Performed By</TableHead>
                              <TableHead className="text-white/80 text-xs whitespace-normal w-[180px]">Details</TableHead>
                              <TableHead className="text-white/80 text-xs whitespace-normal w-[90px]">Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {activityLogEntries.length > 0 ? (
                              activityLogEntries.map((log: any, index: number) => (
                                <TableRow key={index} className="border-white/10">
                                  <TableCell className="font-mono text-xs text-white/90 whitespace-normal break-words align-top">
                                    {formatDateTime(log.timestamp)}
                                  </TableCell>
                                  <TableCell className="font-medium text-white/90 text-xs whitespace-normal break-words align-top">{log.stage}</TableCell>
                                  <TableCell className="text-white/90 text-xs whitespace-normal break-words align-top">{log.action}</TableCell>
                                  <TableCell className="text-white/90 text-xs whitespace-normal break-words align-top">{log.performedBy}</TableCell>
                                  <TableCell className="text-xs text-white/70 whitespace-normal break-words align-top min-w-0">{log.details}</TableCell>
                                  <TableCell className="text-xs whitespace-normal align-top">
                                    {log.status === "completed" ? (
                                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs px-1.5 py-0.5">
                                        <CheckCircle className="h-2.5 w-2.5 mr-1" />
                                        Completed
                                      </Badge>
                                    ) : (
                                      <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs px-1.5 py-0.5">
                                        <Clock className="h-2.5 w-2.5 mr-1" />
                                        {log.status === "in-progress" ? "In Progress" : "Pending"}
                                      </Badge>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow className="border-white/10">
                                <TableCell colSpan={6} className="text-center text-white/50 py-8 text-sm">
                                  No activity log entries found
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
